import React, { useState, useRef, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { pdfjs } from 'react-pdf';
import { OPS } from 'pdfjs-dist';
import {
  Square, Circle, Type, Pen, MousePointer, Hand, Save, Download,
  Trash2, Undo, Redo, Minus, ChevronLeft, ChevronRight,
  ZoomIn, ZoomOut, Crosshair, RotateCw, Camera, Send,
  ArrowRight, Cloud, MessageSquare, StickyNote,
  MessageCircle, Stamp as StampIcon, Ruler, FileText, Zap, ArrowLeft, Calculator, Sparkles, Key, HardHat, FolderOpen, Hash, X, Cable, Loader2, Check, Lightbulb, Grid3X3, Plus, Scan, Upload,
  Sun, Moon, HelpCircle, ChevronUp
} from 'lucide-react';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { supabase } from '../lib/supabase';
import { useBlueprintContext } from '../contexts/BlueprintContext';
import { useAnnotationSaveContext } from '../contexts/AnnotationSaveContext';
import { HandDrawnAnnotations } from './HandDrawnAnnotations';
import { CostEstimationTable } from './CostEstimationTable';
import { SimplifiedEstimateTable } from './SimplifiedEstimateTable';
import PageIndexSidebar from './PageIndexSidebar';
import RFIManager from './RFIManager';
import { WireTypeModal, WireType } from './WireTypeModal';
import { supabase as supabaseClient } from '../lib/supabase';
import { loadGroupedAnnotationsFromV2 } from '../lib/annotationV2Persistence';
import { componentLibraryService } from '../services/componentLibraryService';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import JSZip from 'jszip';
import {
  getUniqueComponentName,
  deduplicateComponentNames,
  getComponentColor,
  POWER_COLOR,
  TELE_DATA_COLOR,
} from '../utils/componentNameUtils';
import { STANDARD_RECEPTACLE_CLASSES, LEGACY_STANDARD_RECEPTACLE_NAMES } from '../utils/receptacleUtils';
import { upload } from '@vercel/blob/client';
import { useAutocountFlow } from './PDFAnnotator/hooks/useAutocountFlow';
import { useCountAllFlow } from './PDFAnnotator/hooks/useCountAllFlow';
import { useCountSingleComponent, type CountSingleComponentOptions } from './PDFAnnotator/hooks/useCountSingleComponent';
import { useDrawAnnotations } from './PDFAnnotator/hooks/useDrawAnnotations';
import { useEstimationAndBilling } from './PDFAnnotator/hooks/useEstimationAndBilling';
import { useCanvasInteractionHandlers } from './PDFAnnotator/hooks/useCanvasInteractionHandlers';
import { useAnnotationCanvasOperations } from './PDFAnnotator/hooks/useAnnotationCanvasOperations';
import { useKeyboardAndWheelShortcuts } from './PDFAnnotator/hooks/useKeyboardAndWheelShortcuts';
import type {
  Annotation,
  AnnotationTool,
  PDFAnnotatorProps,
  PDFVectorElement,
  WireRunEditContextMenuState,
} from './PDFAnnotator/types';
import { ELECTRICAL_COLORS } from './PDFAnnotator/constants';
import { MultiComponentReviewModal } from './PDFAnnotator/components/MultiComponentReviewModal';
import { CountAllProgressModal } from './PDFAnnotator/components/CountAllProgressModal';
import { PDFCanvasStage } from './PDFAnnotator/components/PDFCanvasStage';
import { NewComponentCountModal } from './PDFAnnotator/components/NewComponentCountModal';
import { AutocountConfirmModal } from './PDFAnnotator/components/AutocountConfirmModal';
import { AutocountReviewModal } from './PDFAnnotator/components/AutocountReviewModal';
import { LightingScheduleConfirmModal } from './PDFAnnotator/components/LightingScheduleConfirmModal';
import { PageSelectionModal } from './PDFAnnotator/components/PageSelectionModal';
import { KeyExtractionPanel } from './PDFAnnotator/components/KeyExtractionPanel';
import { KeyExtractionProcessingOverlay } from './PDFAnnotator/components/KeyExtractionProcessingOverlay';
import { KeyExtractionConfirmModal } from './PDFAnnotator/components/KeyExtractionConfirmModal';
import { KeyExtractionLoadingOverlay } from './PDFAnnotator/components/KeyExtractionLoadingOverlay';
import { KeyExtractionPreviewModal } from './PDFAnnotator/components/KeyExtractionPreviewModal';
import { LightingSchedulePreviewModal } from './PDFAnnotator/components/LightingSchedulePreviewModal';
import { AutocountProgressOverlay } from './PDFAnnotator/components/AutocountProgressOverlay';
import { LaborEstimationIndicator } from './PDFAnnotator/components/LaborEstimationIndicator';
import { TooltipButton } from './PDFAnnotator/components/TooltipButton';
import { LightingExtractionIndicator } from './PDFAnnotator/components/LightingExtractionIndicator';
import { BottomModeToolbar } from './PDFAnnotator/components/BottomModeToolbar';
import { SpecsModeViewer } from './PDFAnnotator/components/SpecsModeViewer';
import { NecaModeViewer } from './PDFAnnotator/components/NecaModeViewer';
import { DrawModeLayout } from './PDFAnnotator/components/DrawModeLayout';
import { AutocountAreaSelectionOverlay } from './PDFAnnotator/components/AutocountAreaSelectionOverlay';
import { TakeoffOnboardingStartModal, TakeoffOnboardingMode } from './PDFAnnotator/components/TakeoffOnboardingStartModal';
import { TakeoffPageSelectionModal } from './PDFAnnotator/components/TakeoffPageSelectionModal';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import '../styles/PDFAnnotator.css';
import {
  commitClearScopeAnnotations,
  commitCreateAnnotations,
  commitDeleteAnnotations,
  commitUpdateAnnotations,
  getAnnotationsHash,
  getAnnotationSignature,
} from './PDFAnnotator/savepaths';

// Set up the PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type CalibrationUnit = 'ft' | 'in' | 'm' | 'cm' | 'mm';
type CalibrationMode = 'manual' | 'preset';
type VectorizationStatus = 'unknown' | 'checking' | 'vector' | 'raster';

interface CalibrationPreset {
  id: string;
  label: string;
  unit: CalibrationUnit;
  realUnitsPerDrawingInch: number;
}

interface PageCalibration {
  canvasPercentage?: number;
  realWorldValue?: number;
  unit: CalibrationUnit;
  referencePageNumber?: number;
  createdAt?: string;
  referenceLine?: {
    startX: number;
    startY: number;
    endX: number;
    endY: number;
  };
  showReferenceLine?: boolean;
  mode?: CalibrationMode;
  presetKey?: string;
  presetLabel?: string;
  realUnitsPerPdfPoint?: number;
  realUnitsPerDrawingInch?: number;
  pageWidthPt?: number;
  pageHeightPt?: number;
  rotationAtCalibration?: number;
}

type GuidedCountComponent = {
  color: string;
  name: string;
  symbol: string;
  referenceImage: string;
  symbolType?: string;
  componentType?: string;
  subscript?: string;
  description?: string;
};

type RectangleClipboardItem = {
  offsetX: number;
  offsetY: number;
  width: number;
  height: number;
  color: string;
  thickness: number;
  componentType: string;
  text?: string;
  autoDetected?: boolean;
  confidence?: number;
};

type RectangleClipboardPayload = {
  items: RectangleClipboardItem[];
  width: number;
  height: number;
};

const CALIBRATION_PRESETS: CalibrationPreset[] = [
  { id: '1-8in-eq-1ft', label: '1/8" = 1\'-0"', unit: 'ft', realUnitsPerDrawingInch: 8 },
  { id: '1-4in-eq-1ft', label: '1/4" = 1\'-0"', unit: 'ft', realUnitsPerDrawingInch: 4 },
  { id: '3-8in-eq-1ft', label: '3/8" = 1\'-0"', unit: 'ft', realUnitsPerDrawingInch: 8 / 3 },
  { id: '1-2in-eq-1ft', label: '1/2" = 1\'-0"', unit: 'ft', realUnitsPerDrawingInch: 2 },
  { id: '1in-eq-1ft', label: '1" = 1\'-0"', unit: 'ft', realUnitsPerDrawingInch: 1 },
  { id: 'scale-1-20', label: '1:20', unit: 'm', realUnitsPerDrawingInch: 20 * 0.0254 },
  { id: 'scale-1-50', label: '1:50', unit: 'm', realUnitsPerDrawingInch: 50 * 0.0254 },
  { id: 'scale-1-100', label: '1:100', unit: 'm', realUnitsPerDrawingInch: 100 * 0.0254 },
];

const CALIBRATION_PRESET_MAP: Record<string, CalibrationPreset> = Object.fromEntries(
  CALIBRATION_PRESETS.map((preset) => [preset.id, preset])
);

const formatCalibrationSummary = (calibration?: PageCalibration | null): string => {
  if (!calibration) return 'No calibration set';
  if (calibration.mode === 'preset') {
    if (calibration.presetLabel) {
      return `Preset: ${calibration.presetLabel}`;
    }
    if (calibration.presetKey && CALIBRATION_PRESET_MAP[calibration.presetKey]) {
      return `Preset: ${CALIBRATION_PRESET_MAP[calibration.presetKey].label}`;
    }
    return `Preset calibration (${calibration.unit})`;
  }
  if (calibration.canvasPercentage && calibration.realWorldValue) {
    return `${calibration.canvasPercentage}% : ${calibration.realWorldValue}${calibration.unit}`;
  }
  return `Manual calibration (${calibration.unit})`;
};

// Manual calibration (draw-a-line) value modal.
const CalibrationMeasurementModal: React.FC<{
  isOpen: boolean;
  percentage: number;
  onClose: () => void;
  onSave: (realValue: number, unit: CalibrationUnit) => void;
  onRedo: () => void;
}> = ({ isOpen, percentage, onClose, onSave, onRedo }) => {
  const [realValue, setRealValue] = useState('');
  const [unit, setUnit] = useState<CalibrationUnit>('ft');
  
  if (!isOpen) return null;
  
  return (
    <div className="calibration-modal-overlay" onClick={onClose}>
      <div className="calibration-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Set Real-World Measurement</h3>
        
        <div className="calibration-measurement-info">
          <p>Line measures <strong>{percentage}%</strong> of canvas</p>
        </div>
        
        <div className="calibration-measurement-input">
          <label>What is the real-world measurement?</label>
          <div className="calibration-input-group">
            <input
              type="number"
              step="0.1"
              value={realValue}
              onChange={(e) => setRealValue(e.target.value)}
              placeholder="Enter value"
              autoFocus
            />
            <select value={unit} onChange={(e) => setUnit(e.target.value as CalibrationUnit)}>
              <option value="ft">Feet (ft)</option>
              <option value="in">Inches (in)</option>
              <option value="m">Meters (m)</option>
              <option value="cm">Centimeters (cm)</option>
              <option value="mm">Millimeters (mm)</option>
            </select>
          </div>
        </div>
        
        <div className="calibration-modal-actions">
          <button onClick={onRedo} className="btn-secondary">
            ↻ Redo
          </button>
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button 
            onClick={() => {
              const val = parseFloat(realValue);
              if (!isNaN(val) && val > 0) {
                onSave(val, unit as CalibrationUnit);
              }
            }}
            disabled={!realValue || parseFloat(realValue) <= 0}
            className="btn-primary"
          >
            ✓ Save
          </button>
        </div>
      </div>
    </div>
  );
};

// Bluebeam-style calibration setup modal.
const CalibrationSetupModal: React.FC<{
  isOpen: boolean;
  currentPageNumber: number;
  currentPageCalibration?: PageCalibration;
  vectorizationStatus: VectorizationStatus;
  vectorizationMessage: string;
  onStartManualCalibration: () => void;
  onApplyPresetCalibration: (presetId: string) => void;
  onClose: () => void;
}> = ({
  isOpen,
  currentPageNumber,
  currentPageCalibration,
  vectorizationStatus,
  vectorizationMessage,
  onStartManualCalibration,
  onApplyPresetCalibration,
  onClose,
}) => {
  const [selectedPresetId, setSelectedPresetId] = useState(CALIBRATION_PRESETS[0].id);

  useEffect(() => {
    if (!isOpen) return;
    const existingPreset = currentPageCalibration?.presetKey;
    if (existingPreset && CALIBRATION_PRESET_MAP[existingPreset]) {
      setSelectedPresetId(existingPreset);
    } else {
      setSelectedPresetId(CALIBRATION_PRESETS[0].id);
    }
  }, [isOpen, currentPageCalibration?.presetKey]);

  if (!isOpen) return null;

  const canApplyPreset = vectorizationStatus === 'vector';

  return (
    <div className="calibration-modal-overlay" onClick={onClose}>
      <div className="calibration-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Calibrate Scale (Page {currentPageNumber})</h3>
        <div className="calibration-measurement-info">
          <p><strong>Current:</strong> {formatCalibrationSummary(currentPageCalibration)}</p>
        </div>

        <div className="calibration-measurement-input">
          <label>Preset scale (Bluebeam-style)</label>
          <div className="calibration-input-group">
            <select
              value={selectedPresetId}
              onChange={(e) => setSelectedPresetId(e.target.value)}
              disabled={!canApplyPreset}
            >
              {CALIBRATION_PRESETS.map((preset) => (
                <option key={preset.id} value={preset.id}>
                  {preset.label}
                </option>
              ))}
            </select>
            <button
              className="btn-primary"
              disabled={!canApplyPreset}
              onClick={() => onApplyPresetCalibration(selectedPresetId)}
            >
              Apply Preset
            </button>
          </div>
          <p style={{ marginTop: 10, fontSize: 13 }}>
            {vectorizationStatus === 'checking'
              ? 'Checking if this sheet is vectorized...'
              : vectorizationStatus === 'raster'
              ? `Preset calibration unavailable: ${vectorizationMessage}`
              : vectorizationStatus === 'vector'
              ? `Vector check passed: ${vectorizationMessage}`
              : vectorizationMessage}
          </p>
        </div>

        <div className="calibration-measurement-input">
          <label>Self calibrate from known line</label>
          <button className="btn-secondary" onClick={onStartManualCalibration}>
            Start Self Calibration
          </button>
        </div>

        <div className="calibration-modal-actions">
          <button onClick={onClose} className="btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper to parse PDF and extract elements using simpler approach
const parsePDFAsSVG = async (page: any): Promise<PDFVectorElement[]> => {
  const elements: PDFVectorElement[] = [];
  
  try {
    console.log('📊 Extracting PDF elements...');
    
    // Get canvas for scaling
    const canvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
    if (!canvas) {
      console.warn('⚠️ Canvas not available');
      return elements;
    }
    
    const viewport = page.getViewport({ scale: 1 });
    const scaleX = canvas.width / viewport.width;
    const scaleY = canvas.height / viewport.height;
    
    // Get text content (always works)
    const textContent = await page.getTextContent();
    
    // Add text elements
    textContent.items.forEach((item: any) => {
      if (item.str && item.transform) {
        const x = item.transform[4] * scaleX;
        const y = item.transform[5] * scaleY;
        const fontSize = Math.sqrt(item.transform[0] * item.transform[0] + item.transform[1] * item.transform[1]);
        const textWidth = (item.width || item.str.length * fontSize * 0.6) * scaleX;
        const textHeight = fontSize * scaleY;
        
        elements.push({
          type: 'text',
          bounds: { x, y: y - textHeight, width: textWidth, height: textHeight },
          text: item.str,
          color: 'rgba(0, 255, 255, 0.7)'
        });
      }
    });
    
    // Get operator list for shape and symbol detection
    const operatorList = await page.getOperatorList();
    const ops = operatorList.fnArray;
    const args = operatorList.argsArray;
    
    // Parse operators to detect shapes and symbols
    let currentTransform = [1, 0, 0, 1, 0, 0];
    const transformStack: number[][] = [];
    
    // Helper to transform coords
    const transformCoord = (x: number, y: number) => ({
      x: x * scaleX,
      y: y * scaleY
    });
    
    // Track paths and detect filled shapes (electrical symbols!)
    let currentPath: {x: number, y: number}[] = [];
    const filledPaths: {x: number, y: number}[][] = [];
    
    for (let i = 0; i < ops.length; i++) {
      const op = ops[i];
      const arg = args[i];
      
      // Track transforms
      if (op === OPS.transform && arg && arg.length >= 6) {
        currentTransform = arg;
      } else if (op === OPS.save) {
        transformStack.push([...currentTransform]);
      } else if (op === OPS.restore && transformStack.length > 0) {
        currentTransform = transformStack.pop()!;
      }
      
      // Build paths from constructPath
      if (op === OPS.constructPath && arg && arg.length >= 3 && arg[1] && arg[1][0]) {
        const data = arg[1][0];
        let idx = 0;
        currentPath = [];
        
        while (idx < data.length) {
          const pathOp = data[idx++];
          if (pathOp === 1 && idx + 1 < data.length) { // moveTo
            currentPath = [transformCoord(data[idx++], data[idx++])];
          } else if (pathOp === 2 && idx + 1 < data.length) { // lineTo
            currentPath.push(transformCoord(data[idx++], data[idx++]));
          } else if (pathOp === 3 && idx + 5 < data.length) { // curveTo
            // Just add the endpoint for simplicity
            idx += 4; // Skip control points
            currentPath.push(transformCoord(data[idx++], data[idx++]));
          }
        }
      }
      
      // When a path is filled, it's likely a symbol!
      if ((op === OPS.fill || op === OPS.eoFill || op === OPS.fillStroke) && currentPath.length > 2) {
        filledPaths.push([...currentPath]);
        
        // Calculate bounds
        const xs = currentPath.map(p => p.x);
        const ys = currentPath.map(p => p.y);
        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);
        const width = maxX - minX;
        const height = maxY - minY;
        
        // Skip very large fills (backgrounds) and tiny artifacts
        if (width > 5 && width < 200 && height > 5 && height < 200) {
          elements.push({
            type: 'xobject', // Treat filled shapes as symbols
            bounds: { x: minX, y: minY, width, height },
            objectName: 'filled-shape',
            color: 'rgba(255, 0, 255, 0.7)'
          });
        }
        
        currentPath = [];
      }
      
      // Also detect XObject rendering operations
      if (op === OPS.paintFormXObjectBegin || op === OPS.paintImageXObject) {
        const tx = currentTransform[4];
        const ty = currentTransform[5];
        const sx = Math.abs(currentTransform[0]);
        const sy = Math.abs(currentTransform[3]);
        
        const coord = transformCoord(tx, ty);
        const size = Math.max(sx * scaleX, sy * scaleY, 40);
        
        elements.push({
          type: 'xobject',
          bounds: {
            x: coord.x - size / 2,
            y: coord.y - size / 2,
            width: size,
            height: size
          },
          objectName: 'xobject',
          color: 'rgba(255, 0, 255, 0.7)'
        });
      }
    }
    
    console.log(`📊 Detected ${filledPaths.length} filled paths (likely symbols)`);
    
    const typeCounts = {
      xobject: elements.filter(e => e.type === 'xobject').length,
      text: elements.filter(e => e.type === 'text').length,
      curve: elements.filter(e => e.type === 'curve').length,
      path: elements.filter(e => e.type === 'path').length,
      line: elements.filter(e => e.type === 'line').length,
      rect: elements.filter(e => e.type === 'rect').length
    };
    console.log(`✅ Parsed ${elements.length} SVG elements:`, typeCounts);
    
  } catch (error) {
    console.error('Error parsing PDF as SVG:', error);
  }
  
  return elements;
};

/**
 * PDFAnnotator Component
 * 
 * Coordinate Systems:
 * - Canvas Coordinates: The internal resolution (e.g., 16000x11428)
 *   This is determined by react-pdf based on device pixel ratio and target resolution
 * - Visual Coordinates: How it appears on screen (varies with DPR)
 *   Calculated as canvas coordinates * (display size / canvas size)
 * - Zoom-adjusted: Visual coordinates * zoom scale
 *   Final position accounting for user zoom level
 * 
 * All annotations are stored in canvas coordinates for consistency.
 * All UI positioning uses zoom-adjusted visual coordinates.
 */

const PDFAnnotator = forwardRef<any, PDFAnnotatorProps>(({ file, estimationId, specsFiles, currentSpecsFile, onSpecsFileChange, drawingFiles, currentDrawingFile, onDrawingFileChange, currentDrawingFileId, onScreenshotCaptured, electricalKey, onAutoAnnotationTrigger, onKeyExtracted, onKeyExtractionProgress, autoExtractKeyOnSnapshot = false, activeMode = 'draw', estimation, isFilesPanelCollapsed, onFilesToggle, onHomeClick, onModeChange, isAgentChatCollapsed, onChatToggle, hasUnreadChat, annotationCounts, countingModeActive, activePlacementComponent, onPlacementComplete, onStopCounting, onAnnotationSaved, onWireRunsChanged, onSwitchToCountingTab, onSwitchToWiresTab, onAddComponentToKey, agentChatRef, pageIndex, onReindex, onLightingScheduleExtractionActiveChange }, ref) => {
  // Get current user for library sync
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [specsNumPages, setSpecsNumPages] = useState<number | null>(null);
  const [specsPageNumber, setSpecsPageNumber] = useState(1);
  const [specsScale, setSpecsScale] = useState(1.2);
  const [specsPageRotations, setSpecsPageRotations] = useState<Record<number, number>>({});
  const [necaNumPages, setNecaNumPages] = useState<number | null>(null);
  const [necaPageNumber, setNecaPageNumber] = useState(1);
  const [necaScale, setNecaScale] = useState(1.2); // 120% default zoom
  
  // Page number input state for editable input
  const [pageInputValue, setPageInputValue] = useState<string>('1');
  
  // Sync input value with actual page number
  useEffect(() => {
    setPageInputValue(pageNumber.toString());
  }, [pageNumber]);
  
  // Debug: Log NECA page changes
  useEffect(() => {
    if (activeMode === 'neca') {
      console.log('📄 NECA page changed to:', necaPageNumber);
    }
  }, [necaPageNumber, activeMode]);
  
  // Debug: Log NECA scale changes
  useEffect(() => {
    if (activeMode === 'neca') {
      console.log('🔍 NECA scale changed to:', necaScale);
    }
  }, [necaScale, activeMode]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [highlightBoundingBox, setHighlightBoundingBox] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const MIN_VIEW_SCALE = 0.0;
  const DEFAULT_VIEW_SCALE = 0.15;
  const [scale, setScale] = useState(DEFAULT_VIEW_SCALE);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [pdfRenderScale, setPdfRenderScale] = useState(1);
  const [isHighResReady, setIsHighResReady] = useState(false);
  const [isUpgradingRes, setIsUpgradingRes] = useState(false);
  const highResRenderRef = useRef<{ cancel: boolean }>({ cancel: false });
  const highResUpgradeTimeoutRef = useRef<number | null>(null);
  const pdfPageRef = useRef<any>(null);

  const FAST_RES = 6000;
  const FULL_RES = 4000;
  const HIGH_RES_UPGRADE_DELAY_MS = 350;
  const ENABLE_PHASE_2_UPGRADE = false;
  // Keep render density consistent across displays (Retina and non-Retina).
  // A hard cap avoids giant backing canvases on HiDPI displays.
  const DPR_CAP = 1;
  const effectiveDevicePixelRatio =
    typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, DPR_CAP) : 1;
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [annotationsLoaded, setAnnotationsLoaded] = useState(false);
  const [annotationsDataLoaded, setAnnotationsDataLoaded] = useState(false);
  const [hasCheckedProjectV2Annotations, setHasCheckedProjectV2Annotations] = useState(false);
  const [projectHasNoV2Annotations, setProjectHasNoV2Annotations] = useState(false);
  const [hasDismissedTakeoffOnboardingPrompt, setHasDismissedTakeoffOnboardingPrompt] = useState(false);
  const [annotationCountsByPage, setAnnotationCountsByPage] = useState<Record<number, number>>({});
  const annotationLoadRequestIdRef = useRef(0);
  const annotationCountLoadRequestIdRef = useRef(0);
  const isAnnotationLoadInFlightRef = useRef(false);
  const queuedAnnotationReloadRef = useRef(false);
  const annotationRetryTimerRef = useRef<NodeJS.Timeout | null>(null);
  const annotationRetryInFlightRef = useRef(false);
  const loadAnnotationsRef = useRef<() => Promise<void>>(async () => {});
  const [isCanvasSynced, setIsCanvasSynced] = useState(false);
  const [isPDFRendered, setIsPDFRendered] = useState(false);
  const [isPageFullyLoaded, setIsPageFullyLoaded] = useState(false);
  const [currentTool, setCurrentTool] = useState<AnnotationTool>('select');
  const [currentColor, setCurrentColor] = useState<string>(ELECTRICAL_COLORS.power);
  const [thickness, setThickness] = useState(4);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentAnnotation, setCurrentAnnotation] = useState<Partial<Annotation> | null>(null);
  const [selectedAnnotation, setSelectedAnnotation] = useState<string | null>(null);
  const [history, setHistory] = useState<Annotation[][]>([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [pdfDocument, setPdfDocument] = useState<any>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [wireRunVertexDrag, setWireRunVertexDrag] = useState<{ annotationId: string; pointIndex: number } | null>(null);
  const [wireRunExtendMode, setWireRunExtendMode] = useState<{ annotationId: string; endpoint: 'start' | 'end' } | null>(null);
  // Resize state for rectangle annotations
  const [resizeHandle, setResizeHandle] = useState<{ annotationId: string; handle: 'nw' | 'ne' | 'sw' | 'se'; startX: number; startY: number; origRect: { startX: number; startY: number; width: number; height: number } } | null>(null);
  const [hoverCursor, setHoverCursor] = useState<string>('default');
  const lastMousePosRef = useRef<{ x: number; y: number } | null>(null);
  const copiedRectanglesRef = useRef<RectangleClipboardPayload | null>(null);
  const lastWheelTimeRef = useRef<number>(0);
  const shouldCenterAfterRenderRef = useRef<boolean>(true);
  const activeRenderTokenRef = useRef<number>(0);
  const [highlightedComponentType, setHighlightedComponentType] = useState<string | null>(null);
  const HIGHLIGHT_ALL = '__ALL__';
  // Edit scope is intentionally locked to "all components".
  const [editAllMode, setEditAllMode] = useState(true);
  const interactionHighlightedComponentType =
    editAllMode ? HIGHLIGHT_ALL : highlightedComponentType;
  const interactionIsHighlightAll = interactionHighlightedComponentType === HIGHLIGHT_ALL;
  const interactionHasActiveFilter =
    interactionHighlightedComponentType !== null && !interactionIsHighlightAll;
  useEffect(() => {
    if (!editAllMode) setEditAllMode(true);
  }, [editAllMode]);
  const [animatingAnnotations, setAnimatingAnnotations] = useState<Set<string>>(new Set());
  const [annotationTimestamps, setAnnotationTimestamps] = useState<Map<string, number>>(new Map());
  const [isPanning, setIsPanning] = useState(false);
  const [isRightClickPanning, setIsRightClickPanning] = useState(false); // Temporary pan via right-click hold
  
  // UNIFIED SELECT: Track selected hand-drawn strokes alongside counting annotations
  const [selectedHandDrawnStrokes, setSelectedHandDrawnStrokes] = useState<Set<string>>(new Set());
  const [panStart, setPanStart] = useState<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const [hoveredAnnotation, setHoveredAnnotation] = useState<string | null>(null);
  const [hoveredAnnotationPoint, setHoveredAnnotationPoint] = useState<{ x: number; y: number } | null>(null);
  const [wireRunExtendPreviewPoint, setWireRunExtendPreviewPoint] = useState<{ x: number; y: number } | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionRect, setSelectionRect] = useState<{ startX: number; startY: number; endX: number; endY: number } | null>(null);
  const [selectedAnnotations, setSelectedAnnotations] = useState<Set<string>>(new Set());
  const [pageRotations, setPageRotations] = useState<{ [pageNum: number]: number }>({});
  const [snapshotSelection, setSnapshotSelection] = useState<{ startX: number; startY: number; endX: number; endY: number } | null>(null);
  const [handDrawnEnabled, setHandDrawnEnabled] = useState(false);
  const [handDrawnColor, setHandDrawnColor] = useState('#FF0000');
  const [handDrawnLineWidth, setHandDrawnLineWidth] = useState(0.5); // Percentage of canvas width (0.1% to 10%)
  const [handDrawnFontSize, setHandDrawnFontSize] = useState(1.0); // Percentage of canvas width (0.3% to 3%)
  const fontDropdownRef = useRef<HTMLDivElement>(null);
  const [canUndoHandDrawn, setCanUndoHandDrawn] = useState(false);
  const [showNewComponentModal, setShowNewComponentModal] = useState(false);
  const [countingModalMode, setCountingModalMode] = useState<'manual' | 'auto'>('manual');
  const [newComponentName, setNewComponentName] = useState('');
  const [newComponentColor, setNewComponentColor] = useState('#3b82f6');
  const [isAddingComponent, setIsAddingComponent] = useState(false);
  const [isAddingNewOnly, setIsAddingNewOnly] = useState(false);
  const [isAddingNewOnlyScanMode, setIsAddingNewOnlyScanMode] = useState(false);
  const [selectedComponentsForBatch, setSelectedComponentsForBatch] = useState<Set<string>>(new Set());
  
  // Autocount area selection states
  const [autocountMode, setAutocountMode] = useState<'area-selection' | null>(null);
  const [autocountSelection, setAutocountSelection] = useState<{startX: number, startY: number, endX: number, endY: number} | null>(null);
  const [showAutocountConfirmModal, setShowAutocountConfirmModal] = useState(false);
  const [pendingAutocountComponent, setPendingAutocountComponent] = useState<{
    name: string, color: string; 
    isLighting?: boolean
    symbol?: string;       // Add this
    symbolType?: string;   // Add this
  } | null>(null);
  const [referenceAreaImage, setReferenceAreaImage] = useState<string | null>(null);
  const [queuedComponents, setQueuedComponents] = useState<Array<{ name: string; color: string; referenceImage: string }>>([]);
  const [editingQueuedName, setEditingQueuedName] = useState('');
  const [isAutocounting, setIsAutocounting] = useState(false);
  const [autocountProgress, setAutocountProgress] = useState('');
  const [showAutocountReviewModal, setShowAutocountReviewModal] = useState(false);
  const [showReceptacleRefPopup, setShowReceptacleRefPopup] = useState(false);
  const [quickCountSearch, setQuickCountSearch] = useState('');
  const [autocountDetections, setAutocountDetections] = useState<Array<{
    prediction: any;
    thumbnail: string;
    id: string;
  }>>([]);
  const [selectedDetectionsToKeep, setSelectedDetectionsToKeep] = useState<Set<string>>(new Set());
  const [canRedoHandDrawn, setCanRedoHandDrawn] = useState(false);
  
  // Streaming autocount progress tracking
  const [autocountTotalBatches, setAutocountTotalBatches] = useState(0);
  const [autocountCompletedBatches, setAutocountCompletedBatches] = useState(0);
  
  // Multi-page autocount progress tracking
  const [pageProgressMap, setPageProgressMap] = useState<Map<number, {
    current: number;
    total: number;
    detections: number;
    status: 'processing' | 'complete' | 'error';
  }>>(new Map());
  
  // Multi-component "Count All" state
  const [isCountingAll, setIsCountingAll] = useState(false);
  const [countAllProgress, setCountAllProgress] = useState('');

  // Lighting progress updates from streaming count flow (currently no dedicated modal)
  const [, setLightingProgress] = useState<{
    totalPages: number;
    pageProgress: Map<number, { phase: string; pct: number; completedTiles: number; totalTiles: number; verifiedSoFar?: number; totalToVerify?: number }>;
    overallPct: number;
    detectionsFound: number;
    verifiedCount: number;
    totalCandidates: number;
    message: string;
  }>({ totalPages: 0, pageProgress: new Map(), overallPct: 0, detectionsFound: 0, verifiedCount: 0, totalCandidates: 0, message: '' });
  
  // Key extraction from legend states
  const [keyExtractionMode, setKeyExtractionMode] = useState(false);
  const [keyExtractionSelection, setKeyExtractionSelection] = useState<{startX: number, startY: number, endX: number, endY: number} | null>(null);
  const [isExtractingKeyFromLegend, setIsExtractingKeyFromLegend] = useState(false);
  const [keyExtractionSnapshot, setKeyExtractionSnapshot] = useState<string | null>(null);
  const [showKeyExtractionConfirmModal, setShowKeyExtractionConfirmModal] = useState(false);
  const [showKeyExtractionPreview, setShowKeyExtractionPreview] = useState(false);
  const [extractedKeyPreview, setExtractedKeyPreview] = useState<Array<{
    name: string;
    abbreviation: string;
    description: string;
    componentType: string;
    subscript: string;
    box_2d: number[] | null;
    croppedImage: string | null;
    rowIndex: number;
  }>>([]);

  // Multi-area key extraction jobs (concurrent)
  type KeyExtractionJob = {
    id: string;
    snapshot: string;
    status: 'queued' | 'processing' | 'done' | 'error';
    results?: Array<{ name: string; abbreviation: string; description: string; componentType: string; subscript: string; box_2d: number[] | null; croppedImage: string | null; rowIndex: number }>;
    error?: string;
    abortController?: AbortController;
  };
  const [keyExtractionJobs, setKeyExtractionJobs] = useState<KeyExtractionJob[]>([]);

  // Lighting schedule extraction states
  type ExtractedLightingFixture = {
    code: string;
    name: string;
    manufacturer: string;
    description: string;
    rowIndex: number;
  };
  type LightingScheduleJob = {
    id: string;
    snapshot: string;
  };
  type LightingScheduleUploadItem = {
    id: string;
    fileName: string;
    fileSize: number;
    snapshot: string;
  };
  const [lightingScheduleMode, setLightingScheduleMode] = useState(false);
  const [lightingScheduleSelection, setLightingScheduleSelection] = useState<{startX: number, startY: number, endX: number, endY: number} | null>(null);
  const [isExtractingLightingSchedule, setIsExtractingLightingSchedule] = useState(false);
  const [lightingScheduleSnapshot, setLightingScheduleSnapshot] = useState<string | null>(null);
  const [lightingExtractionAnimationSnapshot, setLightingExtractionAnimationSnapshot] = useState<string | null>(null);
  const [showLightingScheduleConfirmModal, setShowLightingScheduleConfirmModal] = useState(false);
  const [lightingScheduleJobs, setLightingScheduleJobs] = useState<LightingScheduleJob[]>([]);
  const [showLightingSchedulePreview, setShowLightingSchedulePreview] = useState(false);
  const [lightingSchedulePreviewSnapshots, setLightingSchedulePreviewSnapshots] = useState<string[]>([]);
  const [extractedLightingFixtures, setExtractedLightingFixtures] = useState<ExtractedLightingFixture[]>([]);
  const [selectedLightingFixtureIndices, setSelectedLightingFixtureIndices] = useState<Set<number>>(new Set());
  const [selectedKeyPreviewIndices, setSelectedKeyPreviewIndices] = useState<Set<number>>(new Set());
  const [autocountModalTab, setAutocountModalTab] = useState<'components' | 'lighting'>('components');
  const [showTakeoffOnboardingStartModal, setShowTakeoffOnboardingStartModal] = useState(false);
  const [guidedTakeoffMode, setGuidedTakeoffMode] = useState<TakeoffOnboardingMode | null>(null);
  const [guidedTakeoffComponents, setGuidedTakeoffComponents] = useState<GuidedCountComponent[]>([]);
  const [showLightingExtractionGuideModal, setShowLightingExtractionGuideModal] = useState(false);
  const [showLightingScheduleUploadModal, setShowLightingScheduleUploadModal] = useState(false);
  const [isUploadingLightingScheduleScreenshot, setIsUploadingLightingScheduleScreenshot] = useState(false);
  const [lightingScheduleUploadError, setLightingScheduleUploadError] = useState<string | null>(null);
  const [lightingScheduleUploadItems, setLightingScheduleUploadItems] = useState<LightingScheduleUploadItem[]>([]);
  const [isDraggingLightingScheduleUpload, setIsDraggingLightingScheduleUpload] = useState(false);
  const [isLightingScheduleUploadHovered, setIsLightingScheduleUploadHovered] = useState(false);
  const [isGuidedLightingExtractionFlow, setIsGuidedLightingExtractionFlow] = useState(false);
  const [showTakeoffLightingFixtureKeyModal, setShowTakeoffLightingFixtureKeyModal] = useState(false);
  const [takeoffLightingFixtureKeyItems, setTakeoffLightingFixtureKeyItems] = useState<Array<{
    code: string;
    name: string;
    manufacturer: string;
    description: string;
  }>>([]);
  const [pendingTakeoffModeAfterLightingFixtureKeyModal, setPendingTakeoffModeAfterLightingFixtureKeyModal] = useState<TakeoffOnboardingMode | null>(null);
  const [takeoffPageSelectionStage, setTakeoffPageSelectionStage] = useState<'lighting' | 'power' | null>(null);
  const [showTakeoffPageSelectionModal, setShowTakeoffPageSelectionModal] = useState(false);
  const [takeoffLightingPages, setTakeoffLightingPages] = useState<Set<number>>(new Set());
  const [takeoffPowerPages, setTakeoffPowerPages] = useState<Set<number>>(new Set());
  const [pendingLightingScheduleStartPage, setPendingLightingScheduleStartPage] = useState<number | null>(null);
  const lightingScheduleScreenshotInputRef = useRef<HTMLInputElement | null>(null);

  const [countAllComponentProgress, setCountAllComponentProgress] = useState<Array<{
    name: string;
    color: string;
    referenceImage: string;
    status: 'pending' | 'processing' | 'complete' | 'failed';
    count?: number;
    completedBatches?: number;
    totalBatches?: number;
    currentPage?: number;
    currentBatch?: number;
    failMessage?: string;
  }>>([]);
  const [componentPagesByName, setComponentPagesByName] = useState<Map<string, number[]>>(new Map());
  const [countAllPageThumbnails, setCountAllPageThumbnails] = useState<Map<number, string>>(new Map());
  const countAllPageThumbnailsRef = useRef<Map<number, string>>(new Map());
  const countAllThumbnailRenderInFlightRef = useRef<Set<number>>(new Set());
  const streamRunTokensRef = useRef<Map<number, string>>(new Map());
  const retryPayloadsRef = useRef<Map<number, {
    component: any;
    componentIndex: number;
    pagesToCount: number[];
    options?: CountSingleComponentOptions;
  }>>(new Map());
  const [multiComponentDetections, setMultiComponentDetections] = useState<Array<{
    componentName: string;
    componentColor: string;
    detections: Array<{ prediction: any; thumbnail: string; id: string }>;
    referenceImage: string;
    isLighting?: boolean;
  }>>([]);
  const [showMultiComponentReviewModal, setShowMultiComponentReviewModal] = useState(false);
  const [currentReviewComponentIndex, setCurrentReviewComponentIndex] = useState(0);
  const [allComponentSelections, setAllComponentSelections] = useState<Map<string, Set<string>>>(new Map());
  
  // Original receptacle components when grouped into a single RECEPTACLES pass
  const [receptacleGroupComponents, setReceptacleGroupComponents] = useState<Array<{
    color: string; name: string; symbol: string; referenceImage: string;
    symbolType?: string; componentType?: string; subscript?: string; description?: string;
  }>>([]);
  // Original tele/data components when grouped into a single TELE/DATA pass
  const [teleDataGroupComponents, setTeleDataGroupComponents] = useState<Array<{
    color: string; name: string; symbol: string; referenceImage: string;
    symbolType?: string; componentType?: string; subscript?: string; description?: string;
  }>>([]);
  // Original lighting components when grouped into a single LIGHTING pass
  const [lightingGroupComponents, setLightingGroupComponents] = useState<Array<{
    color: string; name: string; symbol: string; referenceImage: string;
    symbolType?: string; componentType?: string; subscript?: string; description?: string;
  }>>([]);
  // Per-detection classification overrides (detection id -> component name)
  const [detectionClassifications, setDetectionClassifications] = useState<Map<string, string>>(new Map());
  // Target component names for selective counting — when non-empty, detections classified
  // as a different component are removed during streaming instead of kept
  const countTargetNamesRef = useRef<Set<string>>(new Set());
  const [receptacleClassFilter, setReceptacleClassFilter] = useState<string>('all');
  const [teleDataClassFilter, setTeleDataClassFilter] = useState<string>('all');
  const [lightingClassFilter, setLightingClassFilter] = useState<string>('all');
  const [bulkSelectedDetections, setBulkSelectedDetections] = useState<Set<string>>(new Set());
  const [bulkReclassTarget, setBulkReclassTarget] = useState<string>('');
  const [isParallelCountSession, setIsParallelCountSession] = useState(false);
  const [isFinalizingReview, setIsFinalizingReview] = useState(false);
  const [reviewFinalizeError, setReviewFinalizeError] = useState<string | null>(null);
  const [isExportingDataset, setIsExportingDataset] = useState(false);
  const [datasetExportError, setDatasetExportError] = useState<string | null>(null);
  
  // State for sequential key extraction counting
  const [pendingKeyComponents, setPendingKeyComponents] = useState<Array<{
    color: string;
    name: string;
    symbol: string;
    referenceImage: string;
    symbolType?: string;
    componentType?: string;
    subscript?: string;
    description?: string;
  }>>([]);

  // Page selection modal (before counting a component)
  const [showPageSelectionModal, setShowPageSelectionModal] = useState(false);
  const [pageSelectionContext, setPageSelectionContext] = useState<{
    component: { color: string; name: string; symbol: string; referenceImage: string; symbolType?: string; componentType?: string; subscript?: string; description?: string };
    componentIndex: number;
    newComponents?: Array<{ color: string; name: string; symbol: string; referenceImage: string; symbolType?: string; componentType?: string; subscript?: string; description?: string }>;
    parallelMode?: boolean;
  } | null>(null);
  const [selectedPagesForCount, setSelectedPagesForCount] = useState<Set<number>>(new Set());
  
  const [showLaborEstimationModal, setShowLaborEstimationModal] = useState(false);
  const [laborEstimationProgress, setLaborEstimationProgress] = useState<Array<{
    name: string;
    count: number;
    status: 'pending' | 'processing' | 'complete' | 'error';
  }>>([]);
  const [showEstimatePopover, setShowEstimatePopover] = useState(false);
  const [showDrawingDropdown, setShowDrawingDropdown] = useState(false);
  const drawingDropdownRef = useRef<HTMLDivElement | null>(null);
  const [showFilesDropdown, setShowFilesDropdown] = useState(false);
  const filesDropdownRef = useRef<HTMLDivElement | null>(null);
  const [showModeDropdown, setShowModeDropdown] = useState(false);
  const modeDropdownRef = useRef<HTMLDivElement | null>(null);
  
  // Wire run states
  const [isWireRunMode, setIsWireRunMode] = useState(false);
  const [showWireTypeModal, setShowWireTypeModal] = useState(false);
  const [pendingWireRunPoints, setPendingWireRunPoints] = useState<{x: number; y: number}[]>([]);
  const [pendingWireRunLength, setPendingWireRunLength] = useState(0);
  const [wireRunContextMenu, setWireRunContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [wireRunEditContextMenu, setWireRunEditContextMenu] = useState<WireRunEditContextMenuState | null>(null);
  const [pendingWireRunPage, setPendingWireRunPage] = useState(1);
  const [wireRunRefreshTrigger, setWireRunRefreshTrigger] = useState(0);
  const [selectedWireType, setSelectedWireType] = useState<WireType | null>(null); // Store selected type for drawing
  const bumpWireRunRefresh = useCallback(() => {
    setWireRunRefreshTrigger(prev => prev + 1);
    onWireRunsChanged?.();
  }, [onWireRunsChanged]);
  // Context menu for active wire run drawing - REMOVED: Right-click now auto-finishes
  
  // Square footage states
  const [isSquareFootageMode, setIsSquareFootageMode] = useState(false);
  
  // Exit wire run mode when switching to a different tool
  useEffect(() => {
    if (currentTool !== 'hand-wire-run' && isWireRunMode) {
      console.log('🔌 Exiting wire run mode - tool changed to:', currentTool);
      setIsWireRunMode(false);
      setSelectedWireType(null);
    }
  }, [currentTool, isWireRunMode]);
  
  // Exit square footage mode when switching to a different tool
  useEffect(() => {
    if (currentTool !== 'hand-square-footage' && isSquareFootageMode) {
      setIsSquareFootageMode(false);
    }
  }, [currentTool, isSquareFootageMode]);
  
  // Per-tool settings
  const [toolSettings, setToolSettings] = useState<{[key: string]: { color: string; thickness: number; opacity?: number }}>({
    'hand-pen': { color: 'var(--text-primary)', thickness: 0.05 },
    'hand-highlight': { color: '#FFFF00', thickness: 0.05, opacity: 0.3 },
    'hand-line': { color: '#1E40AF', thickness: 0.05 },
    'hand-arrow': { color: '#DC2626', thickness: 0.05 },
    'hand-dimension': { color: '#0891B2', thickness: 0.05 },
    'hand-eraser': { color: '#ffffff', thickness: 0.05 },
  });
  const [showToolDropdown, setShowToolDropdown] = useState<{[key: string]: boolean}>({});
  const toolDropdownRefs = useRef<{[key: string]: HTMLDivElement | null}>({});
  const [dropdownPositions, setDropdownPositions] = useState<{[key: string]: {top: number; left: number}}>({});
  const [isCapturingSnapshot, setIsCapturingSnapshot] = useState(false);
  const [showSnapshotActions, setShowSnapshotActions] = useState(false);
  const dirtyWireRunAnnotationIdsRef = useRef<Set<string>>(new Set());
  // Initialize with 0 - will be synced with actual PDF dimensions once rendered
  const [currentCanvasWidth, setCurrentCanvasWidth] = useState(0);
  const [currentCanvasHeight, setCurrentCanvasHeight] = useState(0);
  
  // Calibration states
  const [showCalibrationDropdown, setShowCalibrationDropdown] = useState(false);
  const [isCalibrating, setIsCalibrating] = useState(false);
  const [vectorizationStatus, setVectorizationStatus] = useState<VectorizationStatus>('unknown');
  const [vectorizationMessage, setVectorizationMessage] = useState('Run vector check to unlock presets.');
  const [calibrationLine, setCalibrationLine] = useState<{
    startX: number;
    startY: number;
    endX: number;
    endY: number;
    percentage: number;
  } | null>(null);
  const [showCalibrationModal, setShowCalibrationModal] = useState(false);
  const vectorizationCacheRef = useRef<Record<number, { status: VectorizationStatus; message: string }>>({});
  const [calibrationData, setCalibrationData] = useState<Record<number, PageCalibration>>({});
  const [isGridSnapActive, setIsGridSnapActive] = useState(false);
  const [showDebugTileGrid, setShowDebugTileGrid] = useState(false);
  const [pipingStart, setPipingStart] = useState<{ annotationId: string; x: number; y: number } | null>(null);
  const [pipingPreview, setPipingPreview] = useState<{ startX: number; startY: number; endX: number; endY: number } | null>(null);
  const [pipingSnapTarget, setPipingSnapTarget] = useState<{ annotationId: string; x: number; y: number } | null>(null);
  const [isElementDetectionMode, setIsElementDetectionMode] = useState(false);
  const [pdfVectorElements, setPdfVectorElements] = useState<PDFVectorElement[]>([]);
  const [hoveredPDFElement, setHoveredPDFElement] = useState<PDFVectorElement | null>(null);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debugTileGridRef = useRef<{ gridSize: number; tileWidth: number; tileHeight: number; overlap: number; hiResWidth: number; hiResHeight: number } | null>(null);
  const lastPlacedAnnotationsRef = useRef<string[]>([]); // Track last 20 placed annotations for undo
  
  // Memoize PDF options to prevent unnecessary reloads
  const pdfOptions = useMemo(() => ({
    cMapUrl: 'https://unpkg.com/pdfjs-dist@3.11.174/cmaps/',
    cMapPacked: true,
  }), []);
  
  // Close tool dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      Object.entries(toolDropdownRefs.current).forEach(([tool, ref]) => {
        if (ref && !ref.contains(event.target as Node)) {
          setShowToolDropdown(prev => ({ ...prev, [tool]: false }));
        }
      });
      
      // Handle drawing dropdown
      if (drawingDropdownRef.current && !drawingDropdownRef.current.contains(event.target as Node)) {
        setShowDrawingDropdown(false);
      }
      if (filesDropdownRef.current && !filesDropdownRef.current.contains(event.target as Node)) {
        setShowFilesDropdown(false);
      }
      if (modeDropdownRef.current && !modeDropdownRef.current.contains(event.target as Node)) {
        setShowModeDropdown(false);
      }
    };

    const hasOpenDropdown = Object.values(showToolDropdown).some(isOpen => isOpen) || showDrawingDropdown || showFilesDropdown || showModeDropdown;
    if (hasOpenDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showToolDropdown, showDrawingDropdown, showFilesDropdown, showModeDropdown]);

  // UNIFIED SELECT: Counting mode now only affects placement behavior, not tool restrictions
  // Users can freely use select tool and other drawing tools while in counting mode
  useEffect(() => {
    console.log('📊 Counting mode changed:', countingModeActive, 'activePlacementComponent:', activePlacementComponent?.name);
    if (countingModeActive) {
      console.log('✅ Counting mode active - select tool can interact with both annotation layers');
      // Switch to select tool for component placement, but don't disable other tools
      setCurrentTool('select');
      // Keep hand-drawn tools enabled - users can switch to them freely
      setHandDrawnEnabled(true);
    } else {
      console.log('✅ Counting mode off');
      setHandDrawnEnabled(true);
      // Clear last placed annotations when exiting counting mode
      lastPlacedAnnotationsRef.current = [];
    }
    
    // Clear array when switching between different components to prevent undoing wrong component
    lastPlacedAnnotationsRef.current = [];
  }, [countingModeActive, activePlacementComponent]);
  // Sync tool settings with global color/thickness when tool changes
  useEffect(() => {
    if (currentTool && toolSettings[currentTool]) {
      setHandDrawnColor(toolSettings[currentTool].color);
      setHandDrawnLineWidth(toolSettings[currentTool].thickness);
    }
  }, [currentTool, toolSettings]);
  
  // NEW: Coordinate conversion for the transform-based stage
  const getScreenToCanvasCoords = useCallback((clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    
    // Calculate actual coordinates accounting for internal canvas resolution vs screen size
    // getBoundingClientRect() already accounts for CSS transforms (scale/pan)
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    return { x, y };
  }, []);

  const canvasToVisualCoords = useCallback((canvasX: number, canvasY: number) => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return { x: canvasX, y: canvasY };
    
    const rect = canvas.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Ratio of screen pixels to internal pixels
    const visualScaleX = rect.width / canvas.width;
    const visualScaleY = rect.height / canvas.height;
    
    // Return coordinates relative to the viewport container
    return {
      x: (canvasX * visualScaleX) + (rect.left - containerRect.left),
      y: (canvasY * visualScaleY) + (rect.top - containerRect.top)
    };
  }, []);

  const rescaleAnnotation = useCallback((ann: Annotation, newWidth: number, newHeight: number): Annotation => {
    // If canvas dimensions aren't valid, return annotation as-is to prevent collapsing to 0,0
    if (!newWidth || !newHeight || (newWidth === 300 && newHeight === 150)) {
      return ann;
    }

    const updated = { ...ann } as any;
    if (ann.relativeStartX !== undefined && ann.relativeStartY !== undefined) {
      updated.startX = ann.relativeStartX * newWidth;
      updated.startY = ann.relativeStartY * newHeight;
    }
    if (ann.relativeX !== undefined && ann.relativeY !== undefined) {
      updated.x = ann.relativeX * newWidth;
      updated.y = ann.relativeY * newHeight;
    }
    
    // For endX/endY, prefer relative coordinates if they exist
    if (ann.relativeWidth !== undefined && ann.relativeHeight !== undefined) {
      updated.width = ann.relativeWidth * newWidth;
      updated.height = ann.relativeHeight * newHeight;
      
      if (ann.relativeStartX !== undefined && ann.relativeStartY !== undefined) {
        updated.endX = updated.startX + updated.width;
        updated.endY = updated.startY + updated.height;
      }
    } else {
      // Legacy fallback using savedCanvasWidth
      if (ann.endX !== undefined && ann.savedCanvasWidth) {
        updated.endX = (ann.endX / ann.savedCanvasWidth) * newWidth;
      }
      if (ann.endY !== undefined && ann.savedCanvasHeight) {
        updated.endY = (ann.endY / ann.savedCanvasHeight) * newHeight;
      }
      if (ann.width !== undefined && ann.savedCanvasWidth) {
        updated.width = (ann.width / ann.savedCanvasWidth) * newWidth;
      }
      if (ann.height !== undefined && ann.savedCanvasHeight) {
        updated.height = (ann.height / ann.savedCanvasHeight) * newHeight;
      }
    }

    if ((ann as any).points) {
      updated.points = (ann as any).points.map((p: any) => ({
        ...p,
        x: p.relativeX !== undefined ? p.relativeX * newWidth : p.x,
        y: p.relativeY !== undefined ? p.relativeY * newHeight : p.y,
      }));
    }
    return updated as Annotation;
  }, []);

  // Get context if available (optional - component can work without it)
  const blueprintContext = useBlueprintContext();
  const hasContext = blueprintContext !== null;
  const { acquireSaveLock, releaseSaveLock } = useAnnotationSaveContext();
  
  // Resolve annotation color from canonical component color rules.
  const getColorForAnnotation = useCallback((annotation: Annotation): string => {
    if (electricalKey && annotation.component_type) {
      const component = electricalKey.components.find(
        c => c.name === annotation.component_type || c.symbol === annotation.component_type
      );
      if (component) {
        return getComponentColor(component);
      }
    }

    if (
      annotation.component_type &&
      (annotation.type === 'rectangle' || annotation.type === 'circle')
    ) {
      const componentKey = annotation.component_type.toLowerCase();
      const canInferColor =
        componentKey.includes('receptacle') ||
        componentKey.includes('duplex') ||
        componentKey.includes('gfci') ||
        componentKey.includes('quadruplex') ||
        componentKey.includes('switch') ||
        componentKey.includes('dimmer') ||
        componentKey.includes('toggle') ||
        componentKey.includes('tele') ||
        componentKey.includes('data') ||
        componentKey.includes('communication');
      if (canInferColor) {
        return getComponentColor({
          name: annotation.component_type,
          symbol: annotation.component_type,
        });
      }
    }

    // Fallback to the annotation's stored color for non-component annotations.
    return annotation.color;
  }, [electricalKey]);

  // Load saved page rotations from localStorage
  useEffect(() => {
    if (estimationId && file?.name) {
      const storageKey = `page-rotations-${estimationId}-${file.name}`;
      const savedRotations = localStorage.getItem(storageKey);
      if (savedRotations) {
        try {
          const rotations = JSON.parse(savedRotations);
          setPageRotations(rotations);
          console.log('📐 Loaded saved page rotations:', rotations);
        } catch (err) {
          console.error('Failed to parse saved rotations:', err);
        }
      }
      
      // Also load specs rotations
      const specsStorageKey = `specs-rotations-${estimationId}-${file.name}`;
      const savedSpecsRotations = localStorage.getItem(specsStorageKey);
      if (savedSpecsRotations) {
        try {
          const rotations = JSON.parse(savedSpecsRotations);
          setSpecsPageRotations(rotations);
          console.log('📐 Loaded saved specs rotations:', rotations);
        } catch (err) {
          console.error('Failed to parse saved specs rotations:', err);
        }
      }
    }
  }, [estimationId, file?.name]);

  // Save current page numbers to localStorage for persistence across mode switches
  useEffect(() => {
    if (estimationId && file?.name && activeMode === 'draw') {
      try {
        const storageKey = `page-number-${estimationId}-${file.name}`;
        localStorage.setItem(storageKey, pageNumber.toString());
      } catch { /* quota exceeded — ignore */ }
    }
  }, [pageNumber, estimationId, file?.name, activeMode]);

  // Save specs page number
  useEffect(() => {
    if (estimationId && file?.name && activeMode === 'specs') {
      try {
        const storageKey = `specs-page-number-${estimationId}-${file.name}`;
        localStorage.setItem(storageKey, specsPageNumber.toString());
      } catch { /* quota exceeded — ignore */ }
    }
  }, [specsPageNumber, estimationId, file?.name, activeMode]);

  // Save NECA page number
  useEffect(() => {
    if (estimationId && activeMode === 'neca') {
      try {
        const storageKey = `neca-page-number-${estimationId}`;
        localStorage.setItem(storageKey, necaPageNumber.toString());
      } catch { /* quota exceeded — ignore */ }
    }
  }, [necaPageNumber, estimationId, activeMode]);
  
  // Handle mode transitions with cleanup delay to prevent worker conflicts
  useEffect(() => {
    console.log('🔄 Mode transition starting:', activeMode);
    setIsTransitioning(true);
    
    // AGGRESSIVE delay to ensure old Document cleanup completes
    const timer = setTimeout(() => {
      console.log('✅ Transition complete, rendering:', activeMode);
      setIsTransitioning(false);
      
      // In draw mode, enable hand-drawn tools and default to select
      if (activeMode === 'draw') {
        setHandDrawnEnabled(true);
        setCurrentTool('select');
      } else {
        setHandDrawnEnabled(false);
        setCurrentTool('select');
      }
    }, 600); // INCREASED to 600ms for complete cleanup
    
    return () => {
      console.log('🧹 Cleaning up mode transition');
      clearTimeout(timer);
    };
  }, [activeMode]);

  // Debug calibration state changes
  useEffect(() => {
    console.log('🔧 Calibration state changed:', {
      isCalibrating,
      handDrawnEnabled,
      handDrawnWillBeDisabled: isCalibrating
    });
  }, [isCalibrating, handDrawnEnabled]);

  // Clear highlight when page changes (but not when we just set a highlight)
  const lastHighlightPageRef = useRef<number | null>(null);
  
  useEffect(() => {
    // Only clear if page changed and it's NOT from a citation click
    if (lastHighlightPageRef.current !== null && lastHighlightPageRef.current !== specsPageNumber) {
      console.log('📄 Page changed from', lastHighlightPageRef.current, 'to', specsPageNumber, '- clearing highlight');
      setHighlightBoundingBox(null);
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = null;
      }
    }
    // Update the ref for next time
    lastHighlightPageRef.current = specsPageNumber;
  }, [specsPageNumber]);

  // Reset loading state when page changes
  useEffect(() => {
    annotationLoadRequestIdRef.current += 1; // Invalidate any in-flight load for the previous page.
    queuedAnnotationReloadRef.current = false;
    annotationRetryInFlightRef.current = false;
    if (annotationRetryTimerRef.current) {
      clearTimeout(annotationRetryTimerRef.current);
      annotationRetryTimerRef.current = null;
    }
    setIsPageFullyLoaded(false);
    setIsCanvasSynced(false);
    setAnnotationsLoaded(false);
    setAnnotationsDataLoaded(false);
    setIsPDFRendered(false);
  }, [pageNumber]);
  
  // Mark page as fully loaded when PDF is rendered and canvas is synced (annotations load in background)
  useEffect(() => {
    if (isPDFRendered && isCanvasSynced) {
      console.log('✅ Page fully loaded - PDF rendered and canvas synced');
      setIsPageFullyLoaded(true);
    }
  }, [isPDFRendered, isCanvasSynced]);

  // Load measurement calibrations from Supabase
  useEffect(() => {
    const loadCalibrations = async () => {
      if (!estimationId) return;
      
      const { data, error } = await supabase
        .from('estimations')
        .select('measurement_calibs')
        .eq('id', estimationId)
        .single();
      
      if (data && data.measurement_calibs) {
        setCalibrationData(data.measurement_calibs);
        console.log('📏 Loaded calibrations:', data.measurement_calibs);
      }
    };
    
    loadCalibrations();
  }, [estimationId]);

  useEffect(() => {
    vectorizationCacheRef.current = {};
    setVectorizationStatus('unknown');
    setVectorizationMessage('Run vector check to unlock presets.');
  }, [estimationId, file?.id]);

  // Track Command/Ctrl key for grid snapping during calibration
  useEffect(() => {
    if (!isCalibrating) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey) {
        setIsGridSnapActive(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (!e.metaKey && !e.ctrlKey) {
        setIsGridSnapActive(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      setIsGridSnapActive(false);
    };
  }, [isCalibrating]);

  // Calculate normalized percentage from line (coordinates normalized to 0-1 range)
  const calculateCanvasPercentage = useCallback((startX: number, startY: number, endX: number, endY: number): number => {
    const canvas = canvasRef.current;
    if (!canvas) return 0;
    
    const dx = (endX - startX) / canvas.width;
    const dy = (endY - startY) / canvas.height;
    const normalizedLength = Math.sqrt(dx * dx + dy * dy);
    const percentage = normalizedLength * 100;
    
    return parseFloat(percentage.toFixed(2));
  }, []);

  const hasUsableCalibration = useCallback((calibration?: PageCalibration | null): boolean => {
    if (!calibration) return false;
    if (
      calibration.mode === 'preset' &&
      calibration.realUnitsPerPdfPoint &&
      calibration.pageWidthPt &&
      calibration.pageHeightPt
    ) {
      return true;
    }
    return Boolean(calibration.canvasPercentage && calibration.realWorldValue);
  }, []);

  const calculateRealDistanceFromCanvasDelta = useCallback((
    deltaX: number,
    deltaY: number,
    page: number,
    canvasWidth: number,
    canvasHeight: number
  ): { value: number; unit: CalibrationUnit } | null => {
    if (canvasWidth <= 0 || canvasHeight <= 0) return null;

    const calibration = calibrationData[page];
    if (!calibration) return null;

    if (
      calibration.mode === 'preset' &&
      calibration.realUnitsPerPdfPoint &&
      calibration.pageWidthPt &&
      calibration.pageHeightPt
    ) {
      let widthPt = calibration.pageWidthPt;
      let heightPt = calibration.pageHeightPt;
      const savedRotation = calibration.rotationAtCalibration ?? 0;
      const currentRotation = pageRotations[page] ?? savedRotation;
      const normalizedRotationDelta = ((currentRotation - savedRotation) % 360 + 360) % 360;
      if (normalizedRotationDelta === 90 || normalizedRotationDelta === 270) {
        widthPt = calibration.pageHeightPt;
        heightPt = calibration.pageWidthPt;
      }

      const dxNorm = deltaX / canvasWidth;
      const dyNorm = deltaY / canvasHeight;
      const dxPt = dxNorm * widthPt;
      const dyPt = dyNorm * heightPt;
      const distPt = Math.sqrt(dxPt * dxPt + dyPt * dyPt);
      return {
        value: distPt * calibration.realUnitsPerPdfPoint,
        unit: calibration.unit,
      };
    }

    if (!calibration.canvasPercentage || !calibration.realWorldValue) return null;
    const normalizedPercent = Math.sqrt(
      Math.pow(deltaX / canvasWidth, 2) + Math.pow(deltaY / canvasHeight, 2)
    ) * 100;
    return {
      value: (normalizedPercent / calibration.canvasPercentage) * calibration.realWorldValue,
      unit: calibration.unit,
    };
  }, [calibrationData, pageRotations]);

  // Save calibration to Supabase
  const saveCalibrationToSupabase = useCallback(async (
    pageNum: number,
    calibration: PageCalibration
  ) => {
    if (!estimationId) return;
    
    const updatedCalibs = {
      ...calibrationData,
      [pageNum]: calibration
    };
    
    const { error } = await supabase
      .from('estimations')
      .update({ measurement_calibs: updatedCalibs })
      .eq('id', estimationId);
    
    if (error) {
      console.error('Error saving calibration:', error);
      return;
    }
    
    setCalibrationData(updatedCalibs);
    if (calibration.mode === 'preset') {
      console.log(`✅ Preset calibration saved for page ${pageNum}:`, calibration.presetLabel || calibration.presetKey);
    } else {
      console.log(
        `✅ Manual calibration saved for page ${pageNum}: ${calibration.canvasPercentage}% = ${calibration.realWorldValue}${calibration.unit}`
      );
    }
  }, [estimationId, calibrationData]);

  // Handle calibration modal save
  const handleCalibrationSave = useCallback((realValue: number, unit: CalibrationUnit) => {
    if (calibrationLine) {
      const referenceLine = {
        startX: calibrationLine.startX,
        startY: calibrationLine.startY,
        endX: calibrationLine.endX,
        endY: calibrationLine.endY
      };
      saveCalibrationToSupabase(pageNumber, {
        mode: 'manual',
        canvasPercentage: calibrationLine.percentage,
        realWorldValue: realValue,
        unit,
        createdAt: new Date().toISOString(),
        referenceLine,
        showReferenceLine: true,
      });
      setShowCalibrationModal(false);
      setCalibrationLine(null);
      setIsCalibrating(false);
      setShowCalibrationDropdown(false);
    }
  }, [calibrationLine, pageNumber, saveCalibrationToSupabase]);

  // Handle calibration redo
  const handleCalibrationRedo = useCallback(() => {
    setShowCalibrationModal(false);
    setCalibrationLine(null);
    // Keep isCalibrating true so user can draw again
  }, []);

  const WIRE_RUN_ANNOTATION_PREFIX = 'wire-run-';
  const WIRE_RUN_TEMP_ID_PREFIX = 'wire-run-temp-';

  const getWireRunDbIdFromAnnotationId = (annotationId: string): string | null => {
    if (!annotationId.startsWith(WIRE_RUN_ANNOTATION_PREFIX)) return null;
    const candidateId = annotationId.slice(WIRE_RUN_ANNOTATION_PREFIX.length);
    if (!candidateId || candidateId.startsWith('temp-')) return null;
    return candidateId;
  };

  const deletePersistedWireRunsForAnnotationIds = async (annotationIds: Iterable<string>) => {
    const wireRunDbIds = Array.from(
      new Set(
        Array.from(annotationIds)
          .map(annotationId => getWireRunDbIdFromAnnotationId(annotationId))
          .filter((id): id is string => !!id)
      )
    );

    if (wireRunDbIds.length === 0) return;

    for (const wireRunDbId of wireRunDbIds) {
      try {
        const { error } = await supabaseClient
          .from('wire_runs')
          .delete()
          .eq('id', wireRunDbId);

        if (error) {
          console.error('Error deleting wire run from database:', wireRunDbId, error);
        } else {
          console.log('✅ Wire run deleted from database:', wireRunDbId);
        }
      } catch (err) {
        console.error('Error deleting wire run from database:', wireRunDbId, err);
      }
    }

    bumpWireRunRefresh();
  };

  const calculateWireRunLengthFromCanvasPoints = useCallback((
    points: { x: number; y: number }[],
    page: number,
    canvasWidth: number,
    canvasHeight: number
  ): number => {
    if (points.length < 2 || canvasWidth <= 0 || canvasHeight <= 0) return 0;
    let totalLength = 0;
    for (let i = 1; i < points.length; i++) {
      const converted = calculateRealDistanceFromCanvasDelta(
        points[i].x - points[i - 1].x,
        points[i].y - points[i - 1].y,
        page,
        canvasWidth,
        canvasHeight
      );
      if (!converted) return 0;
      totalLength += converted.value;
    }
    return totalLength;
  }, [calculateRealDistanceFromCanvasDelta]);

  const buildWireRunAnnotationFromRow = useCallback((
    wireRunRow: any,
    canvasWidth: number,
    canvasHeight: number
  ): Annotation | null => {
    const wireRunId = String(wireRunRow?.id || '').trim();
    if (!wireRunId || canvasWidth <= 0 || canvasHeight <= 0) return null;

    const relativePoints = Array.isArray(wireRunRow?.points) ? wireRunRow.points : [];
    if (relativePoints.length < 2) return null;

    const canvasPoints = relativePoints
      .map((point: any) => {
        const relativeX = Number(point?.x);
        const relativeY = Number(point?.y);
        if (!Number.isFinite(relativeX) || !Number.isFinite(relativeY)) return null;
        return {
          x: relativeX * canvasWidth,
          y: relativeY * canvasHeight,
          relativeX,
          relativeY
        };
      })
      .filter((point: { x: number; y: number; relativeX: number; relativeY: number } | null): point is { x: number; y: number; relativeX: number; relativeY: number } => !!point);

    if (canvasPoints.length < 2) return null;

    const pageValue = Number(wireRunRow?.page_number);
    const page = Number.isFinite(pageValue) && pageValue > 0 ? pageValue : pageNumber;
    const wireType = (wireRunRow?.wire_type && typeof wireRunRow.wire_type === 'object')
      ? wireRunRow.wire_type
      : {};
    const wireTypeName = typeof wireType.name === 'string' && wireType.name.trim()
      ? wireType.name.trim()
      : 'Wire Run';
    const wireTypeColor = typeof wireType.color === 'string' && wireType.color.trim()
      ? wireType.color.trim()
      : '#3b82f6';

    const firstPoint = canvasPoints[0];
    const lastPoint = canvasPoints[canvasPoints.length - 1];
    const dbTotalLength = Number(wireRunRow?.total_length);
    const totalLength = Number.isFinite(dbTotalLength)
      ? dbTotalLength
      : calculateWireRunLengthFromCanvasPoints(canvasPoints, page, canvasWidth, canvasHeight);

    return {
      id: `${WIRE_RUN_ANNOTATION_PREFIX}${wireRunId}`,
      type: 'wire-run',
      page,
      startX: firstPoint.x,
      startY: firstPoint.y,
      relativeStartX: firstPoint.relativeX,
      relativeStartY: firstPoint.relativeY,
      endX: lastPoint.x,
      endY: lastPoint.y,
      points: canvasPoints as any,
      color: wireTypeColor,
      thickness: 3,
      timestamp: wireRunRow?.created_at || new Date().toISOString(),
      wireTypeName,
      wireTypeColor,
      totalLength,
      measurementUnit: calibrationData[page]?.unit || 'ft',
      component_type: `wire-${wireTypeName}`,
      savedCanvasWidth: canvasWidth,
      savedCanvasHeight: canvasHeight
    };
  }, [WIRE_RUN_ANNOTATION_PREFIX, pageNumber, calibrationData, calculateWireRunLengthFromCanvasPoints]);

  const normalizeWireRunGeometry = (
    annotation: Annotation,
    points: { x: number; y: number }[],
    canvasWidth: number,
    canvasHeight: number
  ): Annotation => {
    const nextPoints = points.map(point => ({
      x: point.x,
      y: point.y,
      relativeX: canvasWidth > 0 ? point.x / canvasWidth : 0,
      relativeY: canvasHeight > 0 ? point.y / canvasHeight : 0
    }));
    const firstPoint = nextPoints[0];
    const lastPoint = nextPoints[nextPoints.length - 1];
    const pageCalibration = calibrationData[annotation.page];
    const hasCalibration = hasUsableCalibration(pageCalibration);
    const nextTotalLength = hasCalibration
      ? calculateWireRunLengthFromCanvasPoints(nextPoints, annotation.page, canvasWidth, canvasHeight)
      : (annotation.totalLength ?? 0);

    return {
      ...annotation,
      points: nextPoints as any,
      startX: firstPoint.x,
      startY: firstPoint.y,
      endX: lastPoint.x,
      endY: lastPoint.y,
      relativeStartX: firstPoint.relativeX,
      relativeStartY: firstPoint.relativeY,
      totalLength: nextTotalLength,
      measurementUnit: pageCalibration?.unit || annotation.measurementUnit || 'ft',
      savedCanvasWidth: canvasWidth,
      savedCanvasHeight: canvasHeight
    };
  };

  const syncWireRunsToDatabase = async (annotationIds: string[], sourceAnnotations: Annotation[]) => {
    if (annotationIds.length === 0) return;

    const canvas = canvasRef.current;
    const canvasWidth = canvas?.width || 0;
    const canvasHeight = canvas?.height || 0;
    const uniqueAnnotationIds = Array.from(new Set(annotationIds));

    let updatedRows = 0;
    for (const annotationId of uniqueAnnotationIds) {
      const dbId = getWireRunDbIdFromAnnotationId(annotationId);
      if (!dbId) continue;

      const annotation = sourceAnnotations.find(a => a.id === annotationId);
      if (!annotation || annotation.type !== 'wire-run' || !annotation.points || annotation.points.length < 2) continue;

      const pointsForDb = annotation.points.map((point: any) => ({
        x: point.relativeX !== undefined && point.relativeX !== null
          ? point.relativeX
          : (canvasWidth > 0 ? point.x / canvasWidth : 0),
        y: point.relativeY !== undefined && point.relativeY !== null
          ? point.relativeY
          : (canvasHeight > 0 ? point.y / canvasHeight : 0)
      }));
      const totalLength = annotation.totalLength ?? calculateWireRunLengthFromCanvasPoints(annotation.points, annotation.page, canvasWidth, canvasHeight);

      try {
        const { error } = await supabaseClient
          .from('wire_runs')
          .update({
            points: pointsForDb,
            total_length: totalLength,
            segments: Math.max(pointsForDb.length - 1, 0)
          })
          .eq('id', dbId);

        if (error) {
          console.error('Error syncing wire run update:', dbId, error);
          continue;
        }
        updatedRows += 1;
      } catch (err) {
        console.error('Error syncing wire run update:', dbId, err);
      }
    }

    if (updatedRows > 0) {
      bumpWireRunRefresh();
    }
  };

  // Handle wire run completion (called from HandDrawnAnnotations)
  // When polyline is finished, save directly with the pre-selected wire type
  const handleWireRunComplete = useCallback(async (points: {x: number; y: number}[], totalLength: number, page: number) => {
    console.log('🔌 Wire run completed:', { points: points.length, totalLength, page, wireType: selectedWireType?.name });
    
    if (!selectedWireType || !estimationId || !file?.id) {
      console.error('Cannot save wire run: missing wire type or file info');
      setIsWireRunMode(false);
      setSelectedWireType(null);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      console.error('Cannot save wire run: no canvas');
      return;
    }

    // Convert relative points (0-1) to canvas coordinates, preserving relative coords
    const canvasPoints = points.map(p => ({
      x: p.x * canvas.width,
      y: p.y * canvas.height,
      relativeX: p.x,
      relativeY: p.y
    }));

    // Create annotation immediately for instant visual feedback (before DB round-trip)
    const tempId = `${WIRE_RUN_TEMP_ID_PREFIX}${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const wireRunAnnotation: Annotation = {
      id: tempId,
      type: 'wire-run',
      page: page,
      startX: canvasPoints[0]?.x || 0,
      startY: canvasPoints[0]?.y || 0,
      relativeStartX: points[0]?.x || 0,
      relativeStartY: points[0]?.y || 0,
      endX: canvasPoints[canvasPoints.length - 1]?.x,
      endY: canvasPoints[canvasPoints.length - 1]?.y,
      points: canvasPoints,
      color: selectedWireType.color,
      thickness: 3,
      timestamp: new Date().toISOString(),
      wireTypeName: selectedWireType.name,
      wireTypeColor: selectedWireType.color,
      totalLength: totalLength,
      measurementUnit: calibrationData[page]?.unit || 'ft',
      component_type: `wire-${selectedWireType.name}`
    };

    setAnnotations(prev => [...prev, wireRunAnnotation]);
    console.log(
      '✅ Wire run annotation added immediately:',
      selectedWireType.name,
      totalLength.toFixed(2),
      calibrationData[page]?.unit || 'ft'
    );

    // Save to DB in background, then update annotation ID to match the database ID
    try {
      const { data: insertedRun, error } = await supabaseClient
        .from('wire_runs')
        .insert({
          estimation_id: estimationId,
          file_id: file.id,
          wire_type: selectedWireType,
          points: points,
          page_number: page,
          total_length: totalLength,
          segments: points.length - 1
        })
        .select('id')
        .single();

      if (error || !insertedRun?.id) {
        console.error('Error saving wire run to database:', error);
        setAnnotations(prev => prev.filter(a => a.id !== tempId));
        return;
      }

      // Update annotation ID to match the database record.
      // If the temp annotation was deleted before save completed, remove the DB row we just created.
      let linkedToAnnotation = false;
      setAnnotations(prev => {
        const nextAnnotations = prev.map(a => {
          if (a.id === tempId) {
            linkedToAnnotation = true;
            return { ...a, id: `${WIRE_RUN_ANNOTATION_PREFIX}${insertedRun.id}` };
          }
          return a;
        });
        return nextAnnotations;
      });

      if (!linkedToAnnotation) {
        // The optimistic temp annotation may disappear during fast page/file switches
        // before this async insert resolves. Do NOT delete the just-created DB row here.
        // It is the source of truth and will be rehydrated on next load.
        console.warn('Wire run temp annotation missing before DB link; preserving DB row for rehydrate:', insertedRun.id);
        bumpWireRunRefresh();
        return;
      }

      console.log('✅ Wire run DB ID linked:', insertedRun.id);
      bumpWireRunRefresh();
    } catch (err) {
      console.error('Error saving wire run to database:', err);
      setAnnotations(prev => prev.filter(a => a.id !== tempId));
    }
  }, [selectedWireType, estimationId, file?.id, calibrationData, WIRE_RUN_TEMP_ID_PREFIX, WIRE_RUN_ANNOTATION_PREFIX, bumpWireRunRefresh]);

  // Handle square footage completion (called from HandDrawnAnnotations)
  // rect coords are relative (0-1), matching wire-run pattern
  const handleSquareFootageComplete = useCallback((
    rect: { x: number; y: number; width: number; height: number },
    area: number,
    page: number
  ) => {
    const canvas = canvasRef.current;
    if (!canvas || !estimationId || !file?.id) return;

    const unit = calibrationData[page]?.unit || 'ft';
    const absStartX = rect.x * canvas.width;
    const absStartY = rect.y * canvas.height;
    const absEndX = (rect.x + rect.width) * canvas.width;
    const absEndY = (rect.y + rect.height) * canvas.height;
    const absWidth = rect.width * canvas.width;
    const absHeight = rect.height * canvas.height;

    const annotation: Annotation = {
      id: `sqft-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'hand-square-footage',
      page,
      startX: absStartX,
      startY: absStartY,
      endX: absEndX,
      endY: absEndY,
      width: absWidth,
      height: absHeight,
      relativeStartX: rect.x,
      relativeStartY: rect.y,
      relativeWidth: rect.width,
      relativeHeight: rect.height,
      savedCanvasWidth: canvas.width,
      savedCanvasHeight: canvas.height,
      color: '#9333EA',
      thickness: 2,
      timestamp: new Date().toISOString(),
      totalArea: area,
      areaUnit: unit,
      component_type: 'square-footage',
    };

    setAnnotations(prev => [...prev, annotation]);

    // Return to select mode after drawing the box
    setIsSquareFootageMode(false);
    setCurrentTool('select');
  }, [estimationId, file?.id, calibrationData]);

  // Handle wire type selection from modal - starts drawing mode
  const handleWireTypeSelected = useCallback((wireType: WireType) => {
    console.log('🔌 Wire type selected:', wireType.name);
    setSelectedWireType(wireType);
    setShowWireTypeModal(false);
    
    // Start wire run drawing mode (tab switch already happened before modal opened)
    setIsWireRunMode(true);
    setHandDrawnEnabled(true);
    setCurrentTool('hand-wire-run' as AnnotationTool);
  }, []);

  // UNIFIED SELECT: Handle hand-drawn stroke selection changes
  const handleStrokeSelectionChange = useCallback((strokeIds: Set<string>, append: boolean) => {
    console.log('🎨 Hand-drawn stroke selection changed:', strokeIds.size, 'strokes, append:', append);
    
    if (currentTool === 'select') {
      // When append is true, keep existing counting annotation selections
      // When append is false, clear counting annotation selections
      if (!append) {
        setSelectedAnnotation(null);
        setSelectedAnnotations(new Set());
      }
      setSelectedHandDrawnStrokes(strokeIds);
    }
  }, [currentTool]);

  // Start wire run mode - switch to counting/wires tab first, then show modal
  const startWireRunMode = useCallback(() => {
    console.log('🔌 Switching to Wires tab, then opening modal');
    
    // First switch to Counting tab -> Wires subtab
    if (onSwitchToWiresTab) {
      onSwitchToWiresTab();
    }
    
    // Then open the modal (small delay to ensure tab switch completes)
    setTimeout(() => {
      setShowWireTypeModal(true);
    }, 50);
  }, [onSwitchToWiresTab]);

  const startWireRunWithType = useCallback((wireType: WireType) => {
    if (!wireType) return;
    console.log('🔌 Starting wire run with existing type:', wireType.name);
    setSelectedWireType(wireType);
    setShowWireTypeModal(false);
    setWireRunContextMenu(null);
    setIsWireRunMode(true);
    setHandDrawnEnabled(true);
    setCurrentTool('hand-wire-run' as AnnotationTool);
  }, []);

  // Start square footage mode
  const startSquareFootageMode = useCallback(() => {
    if (!calibrationData[pageNumber]) {
      alert('Please calibrate the page first before measuring square footage.');
      return;
    }
    setIsSquareFootageMode(true);
    setCurrentTool('hand-square-footage');
    setHandDrawnEnabled(true);
  }, [calibrationData, pageNumber]);

  // Handle starting key extraction from modal
  const handleStartKeyExtraction = useCallback(() => {
    console.log('🔑 Starting key extraction mode from modal');
    
    setShowNewComponentModal(false);
    setQuickCountSearch('');
    
    // Clear previous jobs and enter multi-area selection mode
    setKeyExtractionJobs([]);
    setKeyExtractionMode(true);
    setKeyExtractionSelection(null);
    setKeyExtractionSnapshot(null);
  }, []);

  // Handle key extraction area selection complete
  const handleKeyExtractionComplete = useCallback(async (snapshotBase64: string) => {
    console.log('🔑 Key area selected, extracting symbols...');
    setKeyExtractionMode(false);
    setIsExtractingKeyFromLegend(true);
    setKeyExtractionSnapshot(snapshotBase64);
    
    try {
      const response = await fetch('/api/extract-key-agentic', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageBase64: snapshotBase64 })
      });
      
      const data = await response.json();
      
      if (data.success && data.symbols) {
        console.log(`✅ Extracted ${data.symbols.length} symbols from legend`);
        
        // Crop symbol images using bounding boxes
        const symbolsWithImages = await cropSymbolImages(snapshotBase64, data.symbols);
        
        // Deduplicate names within the extracted batch (e.g. two "Switch" entries with different symbols)
        const dedupedSymbols = deduplicateComponentNames(symbolsWithImages);
        console.log(`📋 Deduped extraction: ${symbolsWithImages.length} → ${dedupedSymbols.length} unique names`);
        
        setExtractedKeyPreview(dedupedSymbols);
        setSelectedKeyPreviewIndices(new Set(dedupedSymbols.map((_: any, i: number) => i)));
        setShowKeyExtractionPreview(true);
      } else {
        console.error('❌ Extraction failed:', data.error);
        alert('Failed to extract key: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ API call failed:', error);
      alert('Failed to call extraction API: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsExtractingKeyFromLegend(false);
    }
  }, []);

  // "Add & Scan Next": queue this screenshot and go back to selection mode (no API call yet)
  const handleExtractAndScanNext = useCallback(() => {
    if (!keyExtractionSnapshot) return;

    setKeyExtractionJobs(prev => [...prev, {
      id: crypto.randomUUID(),
      snapshot: keyExtractionSnapshot,
      status: 'queued' as const,
    }]);
    setShowKeyExtractionConfirmModal(false);
    setKeyExtractionSnapshot(null);
    setKeyExtractionSelection(null);
  }, [keyExtractionSnapshot]);

  // Fire all queued jobs concurrently
  const fireAllKeyExtractionJobs = useCallback(() => {
    setKeyExtractionJobs(prev => prev.map(job => {
      if (job.status !== 'queued') return job;

      const abortController = new AbortController();
      const jobId = job.id;
      const snapshot = job.snapshot;

      (async () => {
        try {
          const response = await fetch('/api/extract-key-agentic', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ imageBase64: snapshot }),
            signal: abortController.signal,
          });
          const data = await response.json();

          if (data.success && data.symbols) {
            const symbolsWithImages = await cropSymbolImages(snapshot, data.symbols);
            setKeyExtractionJobs(p => p.map(j =>
              j.id === jobId ? { ...j, status: 'done' as const, results: symbolsWithImages } : j
            ));
          } else {
            setKeyExtractionJobs(p => p.map(j =>
              j.id === jobId ? { ...j, status: 'error' as const, error: data.error || 'Extraction failed' } : j
            ));
          }
        } catch (err: any) {
          if (err.name === 'AbortError') return;
          setKeyExtractionJobs(p => p.map(j =>
            j.id === jobId ? { ...j, status: 'error' as const, error: err.message || 'API call failed' } : j
          ));
        }
      })();

      return { ...job, status: 'processing' as const, abortController };
    }));
  }, []);

  // Flag to trigger firing all jobs after state settles
  const pendingFireAllRef = useRef(false);

  // "Extract" / "Extract & Done": queue this snapshot if present, fire ALL jobs, exit selection mode
  const handleExtractSingle = useCallback(() => {
    if (keyExtractionSnapshot) {
      setKeyExtractionJobs(prev => [...prev, {
        id: crypto.randomUUID(),
        snapshot: keyExtractionSnapshot,
        status: 'queued' as const,
      }]);
    }
    setShowKeyExtractionConfirmModal(false);
    setKeyExtractionSnapshot(null);
    setKeyExtractionSelection(null);
    setKeyExtractionMode(false);
    pendingFireAllRef.current = true;
  }, [keyExtractionSnapshot]);

  // Fire all queued jobs once state has settled after handleExtractSingle or handleFinishKeyExtractionSelection
  useEffect(() => {
    if (!pendingFireAllRef.current) return;
    if (keyExtractionJobs.length === 0) return;
    if (!keyExtractionJobs.some(j => j.status === 'queued')) return;
    pendingFireAllRef.current = false;
    fireAllKeyExtractionJobs();
  }, [keyExtractionJobs, fireAllKeyExtractionJobs]);

  // Delete a key extraction job (abort if still processing)
  const handleDeleteKeyExtractionJob = useCallback((jobId: string) => {
    setKeyExtractionJobs(prev => {
      const job = prev.find(j => j.id === jobId);
      if (job && job.status === 'processing' && job.abortController) {
        job.abortController.abort();
      }
      return prev.filter(j => j.id !== jobId);
    });
  }, []);

  // "Extract Key" from floating panel: fire all queued jobs concurrently, exit selection mode
  const handleFinishKeyExtractionSelection = useCallback(() => {
    setKeyExtractionMode(false);
    setKeyExtractionSelection(null);
    pendingFireAllRef.current = true;
    // Force a re-render so the useEffect picks up the flag
    setKeyExtractionJobs(prev => [...prev]);
  }, []);

  // Auto-open preview when all jobs finish after user clicked Extract
  useEffect(() => {
    if (keyExtractionMode) return;
    if (keyExtractionJobs.length === 0) return;
    if (showKeyExtractionPreview) return;
    if (keyExtractionJobs.some(j => j.status === 'processing' || j.status === 'queued')) return;

    const allResults = keyExtractionJobs
      .filter(j => j.status === 'done' && j.results)
      .flatMap(j => j.results!);

    if (allResults.length === 0) {
      alert('No symbols were extracted. Try selecting the legend areas again.');
      setKeyExtractionJobs([]);
      return;
    }

    const dedupedSymbols = deduplicateComponentNames(allResults);
    console.log(`📋 All jobs complete. Combined ${allResults.length} → ${dedupedSymbols.length} unique symbols`);

    setExtractedKeyPreview(dedupedSymbols);
    setSelectedKeyPreviewIndices(new Set(dedupedSymbols.map((_: any, i: number) => i)));
    setShowKeyExtractionPreview(true);
  }, [keyExtractionJobs, keyExtractionMode, showKeyExtractionPreview]);

  // Cancel multi-area key extraction entirely
  const handleCancelKeyExtractionJobs = useCallback(() => {
    keyExtractionJobs.forEach(j => {
      if (j.status === 'processing' && j.abortController) j.abortController.abort();
    });
    setKeyExtractionJobs([]);
    setKeyExtractionMode(false);
    setKeyExtractionSelection(null);
  }, [keyExtractionJobs]);

  const LIGHTING_FIXTURE_COLOR = getComponentColor({
    name: 'Lighting Group',
    symbol: '__LIGHTING_GROUP__',
    componentType: 'Lighting',
    symbolType: 'Lighting',
  });

  const findLightingSchedulePage = useCallback(() => {
    if (!pageIndex || pageIndex.length === 0) return null;
    const exact = pageIndex.find((entry) => (entry.label || '').trim().toLowerCase() === 'lighting fixture schedule');
    if (exact?.page) return exact.page;
    const contains = pageIndex.find((entry) => {
      const label = (entry.label || '').toLowerCase();
      return label.includes('lighting fixture schedule') || (label.includes('lighting') && label.includes('schedule'));
    });
    return contains?.page ?? null;
  }, [pageIndex]);

  const findFirstLightingPage = useCallback(() => {
    if (!pageIndex || pageIndex.length === 0) return null;
    const match = pageIndex.find((entry) => {
      const label = (entry.label || '').toLowerCase();
      return label.includes('lighting') || label.includes('reflected ceiling plan') || label.includes('rcp');
    });
    return match?.page ?? null;
  }, [pageIndex]);

  const areAllAnnotationCountsZero = useMemo(() => {
    if (!annotationCounts || annotationCounts.size === 0) return true;
    let totalAnnotations = 0;
    annotationCounts.forEach((pageCounts) => {
      pageCounts.forEach((count) => {
        const numericCount = Number(count || 0);
        if (Number.isFinite(numericCount) && numericCount > 0) {
          totalAnnotations += numericCount;
        }
      });
    });
    return totalAnnotations === 0;
  }, [annotationCounts]);

  const shouldPromptForLightingScheduleUpload = useCallback(() => {
    const hasLightingSchedulePage = !!findLightingSchedulePage();
    return !hasLightingSchedulePage && areAllAnnotationCountsZero;
  }, [areAllAnnotationCountsZero, findLightingSchedulePage]);

  const enterLightingScheduleSelectionMode = useCallback(() => {
    setLightingScheduleMode(true);
    setLightingScheduleSelection(null);
    setLightingScheduleSnapshot(null);
  }, []);

  // Handle starting lighting schedule extraction from modal
  const handleStartLightingScheduleExtraction = useCallback((options?: { guided?: boolean }) => {
    console.log('💡 Starting lighting schedule extraction mode from modal');

    if (options?.guided) {
      setIsGuidedLightingExtractionFlow(true);
    } else {
      setIsGuidedLightingExtractionFlow(false);
    }

    // Close any setup modal and enter extraction flow
    setShowLightingExtractionGuideModal(false);
    setShowLightingScheduleUploadModal(false);
    setLightingScheduleUploadError(null);
    setLightingScheduleUploadItems([]);
    setIsDraggingLightingScheduleUpload(false);
    setIsLightingScheduleUploadHovered(false);
    setLightingSchedulePreviewSnapshots([]);
    setShowNewComponentModal(false);
    setQuickCountSearch('');
    setLightingScheduleJobs([]);
    setLightingExtractionAnimationSnapshot(null);

    const schedulePage = findLightingSchedulePage() ?? findFirstLightingPage();
    if (schedulePage && schedulePage !== pageNumber && schedulePage >= 1 && schedulePage <= (numPages || 1)) {
      console.log(`📖 Jumping to detected lighting fixture schedule page ${schedulePage}`);
      activeRenderTokenRef.current += 1;
      shouldCenterAfterRenderRef.current = true;
      setPageNumber(schedulePage);
      setScale(DEFAULT_VIEW_SCALE);
      setPendingLightingScheduleStartPage(schedulePage);
      return;
    }

    enterLightingScheduleSelectionMode();
  }, [enterLightingScheduleSelectionMode, findFirstLightingPage, findLightingSchedulePage, numPages, pageNumber]);

  useEffect(() => {
    if (!pendingLightingScheduleStartPage) return;
    if (pageNumber !== pendingLightingScheduleStartPage) return;
    setPendingLightingScheduleStartPage(null);
    enterLightingScheduleSelectionMode();
  }, [enterLightingScheduleSelectionMode, pageNumber, pendingLightingScheduleStartPage]);

  // Upload one schedule snapshot to Blob, then extract fixtures from API
  const uploadLightingScheduleSnapshot = useCallback(async (snapshotBase64: string) => {
    const imageResponse = await fetch(snapshotBase64);
    const blob = await imageResponse.blob();
    const extension = blob.type === 'image/jpeg'
      ? 'jpg'
      : blob.type === 'image/webp'
        ? 'webp'
        : 'png';

    return upload(`lighting-schedules/schedule-${Date.now()}.${extension}`, blob, {
      access: 'public',
      contentType: blob.type || 'image/png',
      multipart: true,
      handleUploadUrl: '/api/lighting-schedule-upload',
    });
  }, []);

  const extractLightingScheduleFixturesFromSnapshot = useCallback(async (
    snapshotBase64: string
  ): Promise<ExtractedLightingFixture[]> => {
    let requestPayload: { imageUrl: string; cleanupBlob: boolean } | { imageBase64: string };

    try {
      const blobResult = await uploadLightingScheduleSnapshot(snapshotBase64);
      requestPayload = {
        imageUrl: blobResult.url,
        cleanupBlob: true,
      };
    } catch (uploadError) {
      const uploadMessage = uploadError instanceof Error ? uploadError.message : 'Unknown blob upload error';
      console.warn('⚠️ Blob upload unavailable, falling back to direct base64 payload:', uploadMessage);
      requestPayload = { imageBase64: snapshotBase64 };
    }

    const response = await fetch('/api/extract-lighting-schedule', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestPayload)
    });

    const isJson = (response.headers.get('content-type') || '').includes('application/json');
    if (!isJson) {
      const responseText = await response.text();
      throw new Error(`Lighting schedule API returned non-JSON (${response.status}): ${responseText.slice(0, 200)}`);
    }

    const data = await response.json();
    if (!data.success || !Array.isArray(data.fixtures)) {
      throw new Error(data.error || 'Lighting schedule extraction failed');
    }

    return data.fixtures.map((fixture: any, idx: number) => ({
      code: String(fixture?.code || '').trim(),
      name: String(fixture?.name || '').trim(),
      manufacturer: String(fixture?.manufacturer || '').trim(),
      description: String(fixture?.description || '').trim(),
      rowIndex: Number.isFinite(fixture?.rowIndex) ? fixture.rowIndex : idx,
    })).filter((fixture: ExtractedLightingFixture) => fixture.code.length > 0);
  }, [uploadLightingScheduleSnapshot]);

  const mergeLightingScheduleFixtures = useCallback((batches: ExtractedLightingFixture[]) => {
    const mergedByCode = new Map<string, ExtractedLightingFixture>();

    batches.forEach((fixture) => {
      const normalizedCode = fixture.code.trim().toUpperCase();
      if (!normalizedCode) return;

      const existing = mergedByCode.get(normalizedCode);
      if (!existing) {
        mergedByCode.set(normalizedCode, {
          code: fixture.code.trim(),
          name: fixture.name.trim() || fixture.code.trim(),
          manufacturer: fixture.manufacturer.trim(),
          description: fixture.description.trim(),
          rowIndex: mergedByCode.size,
        });
        return;
      }

      const betterName =
        existing.name && existing.name.length >= fixture.name.length ? existing.name : fixture.name;
      const betterDescription =
        existing.description && existing.description.length >= fixture.description.length
          ? existing.description
          : fixture.description;
      const betterManufacturer =
        existing.manufacturer && existing.manufacturer.length >= fixture.manufacturer.length
          ? existing.manufacturer
          : fixture.manufacturer;

      mergedByCode.set(normalizedCode, {
        ...existing,
        name: betterName || existing.code,
        manufacturer: betterManufacturer || existing.manufacturer,
        description: betterDescription || existing.description,
      });
    });

    return Array.from(mergedByCode.values()).map((fixture, idx) => ({
      ...fixture,
      rowIndex: idx,
    }));
  }, []);

  const handleQueueLightingScheduleAndContinue = useCallback(() => {
    if (!lightingScheduleSnapshot) return;
    setLightingScheduleJobs((prev) => [
      ...prev,
      { id: crypto.randomUUID(), snapshot: lightingScheduleSnapshot }
    ]);
    setShowLightingScheduleConfirmModal(false);
    setLightingScheduleSnapshot(null);
    setLightingScheduleSelection(null);
    setLightingScheduleMode(true);
  }, [lightingScheduleSnapshot]);

  const handleDeleteQueuedLightingSchedule = useCallback((jobId: string) => {
    setLightingScheduleJobs((prev) => prev.filter((job) => job.id !== jobId));
  }, []);

  const runLightingScheduleExtraction = useCallback(async (snapshots: string[]): Promise<boolean> => {
    if (snapshots.length === 0) return false;

    const firstSnapshot = snapshots[0] ?? null;
    console.log(`💡 Extracting lighting fixtures from ${snapshots.length} schedule area(s)...`);

    setLightingScheduleMode(false);
    setShowLightingScheduleConfirmModal(false);
    setLightingScheduleSelection(null);
    setLightingScheduleSnapshot(firstSnapshot);
    setLightingExtractionAnimationSnapshot(firstSnapshot);
    setLightingScheduleJobs([]);
    setIsExtractingLightingSchedule(true);

    try {
      const runBatch = async (batchSnapshots: string[]) => {
        const settled = await Promise.allSettled(
          batchSnapshots.map((snapshot) => extractLightingScheduleFixturesFromSnapshot(snapshot))
        );
        const successful = settled
          .filter((result): result is PromiseFulfilledResult<ExtractedLightingFixture[]> => result.status === 'fulfilled')
          .flatMap((result) => result.value);
        const failedSnapshots = settled.flatMap((result, idx) => (
          result.status === 'rejected' ? [batchSnapshots[idx]] : []
        ));
        return { successful, failedSnapshots };
      };

      const firstPass = await runBatch(snapshots);
      let successfulBatches = firstPass.successful;
      let failedSnapshots = firstPass.failedSnapshots;

      if (failedSnapshots.length > 0) {
        console.warn(
          `⚠️ ${failedSnapshots.length}/${snapshots.length} lighting schedule extractions failed on first pass. Retrying failed schedules once...`
        );
        const secondPass = await runBatch(failedSnapshots);
        successfulBatches = [...successfulBatches, ...secondPass.successful];
        failedSnapshots = secondPass.failedSnapshots;
      }

      const mergedFixtures = mergeLightingScheduleFixtures(successfulBatches);

      if (mergedFixtures.length === 0) {
        const errorMessage = failedSnapshots.length > 0
          ? 'All lighting schedule extractions failed.'
          : 'No fixtures were extracted from the selected schedules.';
        alert(`${errorMessage} Try selecting clearer schedule areas.`);
        return false;
      }

      if (failedSnapshots.length > 0) {
        const successCount = snapshots.length - failedSnapshots.length;
        console.warn(
          `⚠️ ${failedSnapshots.length}/${snapshots.length} lighting schedule extractions still failed after retry.`
        );
        alert(
          `Added fixtures from ${successCount}/${snapshots.length} schedules. ` +
          `${failedSnapshots.length} schedule${failedSnapshots.length === 1 ? '' : 's'} failed due to temporary AI service issues. ` +
          `Please retry those schedule areas.`
        );
      }

      console.log(`✅ Extracted ${mergedFixtures.length} unique lighting fixtures from ${snapshots.length} schedule area(s)`);
      setExtractedLightingFixtures(mergedFixtures);
      setSelectedLightingFixtureIndices(new Set(mergedFixtures.map((_, i) => i)));
      setLightingSchedulePreviewSnapshots(snapshots);
      setShowLightingSchedulePreview(true);
      return true;
    } catch (error) {
      console.error('❌ Lighting schedule batch extraction failed:', error);
      alert('Failed to call lighting schedule API: ' + (error instanceof Error ? error.message : 'Unknown error'));
      return false;
    } finally {
      setIsExtractingLightingSchedule(false);
      setLightingExtractionAnimationSnapshot(null);
    }
  }, [extractLightingScheduleFixturesFromSnapshot, mergeLightingScheduleFixtures]);

  const handleExtractQueuedLightingSchedules = useCallback(async () => {
    const queuedSnapshots = lightingScheduleJobs.map((job) => job.snapshot);
    if (lightingScheduleSnapshot) queuedSnapshots.push(lightingScheduleSnapshot);
    if (queuedSnapshots.length === 0) return;
    await runLightingScheduleExtraction(queuedSnapshots);
  }, [lightingScheduleJobs, lightingScheduleSnapshot, runLightingScheduleExtraction]);

  const mapKeyComponentToGuidedCount = useCallback((component: any): GuidedCountComponent | null => {
    const name = String(component?.name || '').trim();
    if (!name) return null;
    const symbol = String(component?.symbol || name);
    const componentType = component?.componentType;
    const symbolType = component?.symbolType;
    return {
      color: getComponentColor({
        name,
        symbol,
        componentType,
        symbolType,
      }),
      name,
      symbol,
      referenceImage: String(component?.referenceImage || ''),
      symbolType,
      componentType,
      subscript: component?.subscript,
      description: component?.description,
    };
  }, []);

  const getLightingComponentsFromKey = useCallback((projectKey: any): GuidedCountComponent[] => {
    const keyComponents: any[] = Array.isArray(projectKey?.components) ? projectKey.components : [];
    return keyComponents
      .map(mapKeyComponentToGuidedCount)
      .filter((c: GuidedCountComponent | null): c is GuidedCountComponent => !!c)
      .filter((c: GuidedCountComponent) => c.symbolType === 'Lighting' || c.componentType === 'Lighting');
  }, [mapKeyComponentToGuidedCount]);

  const getPowerComponentsFromKey = useCallback((projectKey: any): GuidedCountComponent[] => {
    const keyComponents: any[] = Array.isArray(projectKey?.components) ? projectKey.components : [];
    return keyComponents
      .map(mapKeyComponentToGuidedCount)
      .filter((c: GuidedCountComponent | null): c is GuidedCountComponent => !!c)
      .filter((c: GuidedCountComponent) => c.symbolType !== 'Lighting' && c.componentType !== 'Lighting');
  }, [mapKeyComponentToGuidedCount]);

  const upsertSelectedLightingFixturesIntoKey = useCallback(async () => {
    const selectedFixtures = extractedLightingFixtures.filter((_, idx) => selectedLightingFixtureIndices.has(idx));
    if (selectedFixtures.length === 0) return null;

    const newComponents = selectedFixtures.map((fixture) => {
      const symbol = fixture.code || fixture.name;
      return {
        color: getComponentColor({
          name: fixture.name,
          symbol,
          componentType: 'Lighting',
          symbolType: 'Lighting',
        }),
        name: fixture.name,
        symbol,
        symbolType: 'Lighting',
        componentType: 'Lighting',
        subscript: '',
        manufacturer: fixture.manufacturer,
        description: fixture.description,
        pages: [] as any[],
        totalCount: 0,
        referenceImage: '',
        hasReferenceImage: false,
      };
    });

    const dedupedNewComponents = Array.from(
      new Map(newComponents.map((component) => [String(component.symbol || '').toLowerCase(), component])).values()
    );
    const existingComponents = electricalKey?.components || [];
    const existingSymbols = new Set(existingComponents.map((c: any) => (c.symbol || '').toLowerCase()));
    const uniqueNew = dedupedNewComponents.filter((c: any) => !existingSymbols.has((c.symbol || '').toLowerCase()));
    const mergedComponents = [...existingComponents, ...uniqueNew];

    const projectKey = {
      components: mergedComponents,
      extractedAt: new Date().toISOString(),
      pdfHash: (electricalKey as any)?.pdfHash || '',
    };

    console.log(`📦 Added ${uniqueNew.length} lighting fixtures to key (${newComponents.length - uniqueNew.length} duplicates skipped, total: ${mergedComponents.length})`);

    setShowLightingSchedulePreview(false);
    setExtractedLightingFixtures([]);
    setSelectedLightingFixtureIndices(new Set());
    setLightingScheduleSnapshot(null);
    setLightingScheduleJobs([]);
    setLightingExtractionAnimationSnapshot(null);

    if (onKeyExtracted) {
      await onKeyExtracted(projectKey);
    }

    return {
      projectKey,
      uniqueNew: uniqueNew.map((component) => ({
        color: component.color,
        name: component.name,
        symbol: component.symbol,
        referenceImage: component.referenceImage,
        symbolType: component.symbolType,
        componentType: component.componentType,
        subscript: component.subscript,
        description: component.description,
      })) as GuidedCountComponent[],
    };
  }, [electricalKey, extractedLightingFixtures, onKeyExtracted, selectedLightingFixtureIndices]);

  // Handle adding extracted lighting fixtures to project key (without counting)
  const handleAddLightingFixturesToKey = useCallback(async () => {
    const upsertResult = await upsertSelectedLightingFixturesIntoKey();
    if (!upsertResult) return;
    console.log('💡 Added extracted lighting fixtures to key');
  }, [upsertSelectedLightingFixturesIntoKey]);

  const { countSingleComponent } = useCountSingleComponent({
    pdfDocument,
    numPages,
    pageNumber,
    canvasRef,
    file: file ?? null,
    lightingGroupComponents,
    teleDataGroupComponents,
    electricalKey,
    streamRunTokensRef,
    retryPayloadsRef,
    countTargetNamesRef,
    setCountAllComponentProgress,
    setMultiComponentDetections,
    setAllComponentSelections,
    setCountAllProgress,
    setIsCountingAll,
    setDetectionClassifications,
    setLightingProgress,
  });

  const handleRetryComponent = useCallback(async (componentIndex: number) => {
    const payload = retryPayloadsRef.current.get(componentIndex);
    if (!payload) {
      console.warn(`⚠️ No retry payload for component index ${componentIndex}`);
      return;
    }
    console.log(`🔄 Retrying component "${payload.component.name}" (index ${componentIndex})...`);
    setCountAllComponentProgress(prev => prev.map((p, i) =>
      i === componentIndex ? { ...p, status: 'processing' as const, failMessage: undefined, completedBatches: 0 } : p
    ));
    try {
      await countSingleComponent(payload.component, payload.componentIndex, payload.pagesToCount, payload.options);
    } catch (err) {
      console.error(`❌ Retry also failed for "${payload.component.name}":`, err);
    }
  }, [countSingleComponent]);

  const {
    getSmartPageSelection,
    startCountingComponents,
    startParallelCountingWithPageResolver,
    handleConfirmPageSelection,
    handleMultiComponentNext,
    handleMultiComponentBack,
    toggleMultiDetectionSelection,
  } = useCountAllFlow({
    pdfDocument,
    numPages,
    pageIndex,
    countSingleComponent,
    countTargetNamesRef,
    powerColor: POWER_COLOR,
    teleDataColor: TELE_DATA_COLOR,
    lightingFixtureColor: LIGHTING_FIXTURE_COLOR,
    pageSelectionContext,
    selectedPagesForCount,
    countAllComponentProgress,
    currentReviewComponentIndex,
    pendingKeyComponents,
    isParallelCountSession,
    setIsParallelCountSession,
    setDetectionClassifications,
    setReceptacleGroupComponents,
    setTeleDataGroupComponents,
    setLightingGroupComponents,
    setPendingKeyComponents,
    setSelectedPagesForCount,
    setPageSelectionContext,
    setShowPageSelectionModal,
    setCountAllComponentProgress,
    setMultiComponentDetections,
    setAllComponentSelections,
    setCurrentReviewComponentIndex,
    setReceptacleClassFilter,
    setShowMultiComponentReviewModal,
    setIsCountingAll,
    setCountAllProgress,
    setBulkSelectedDetections,
    setBulkReclassTarget,
    setComponentPagesByName,
  });

  const buildDefaultPagesForComponents = useCallback((components: GuidedCountComponent[]) => {
    const pageSet = new Set<number>();
    components.forEach((component) => {
      const suggested = getSmartPageSelection(component);
      suggested.forEach((page) => pageSet.add(page));
    });
    if (pageSet.size > 0) return pageSet;
    if (!numPages) return pageSet;
    return new Set(Array.from({ length: numPages }, (_, i) => i + 1));
  }, [getSmartPageSelection, numPages]);

  const applyTakeoffLightingDefaultPages = useCallback((components: GuidedCountComponent[]) => {
    const defaults = buildDefaultPagesForComponents(components);
    setTakeoffLightingPages(defaults);
  }, [buildDefaultPagesForComponents]);

  const applyTakeoffPowerDefaultPages = useCallback((components: GuidedCountComponent[]) => {
    const defaults = buildDefaultPagesForComponents(components);
    setTakeoffPowerPages(defaults);
  }, [buildDefaultPagesForComponents]);

  const resetGuidedTakeoffFlow = useCallback(() => {
    setGuidedTakeoffMode(null);
    setGuidedTakeoffComponents([]);
    setShowTakeoffLightingFixtureKeyModal(false);
    setTakeoffLightingFixtureKeyItems([]);
    setPendingTakeoffModeAfterLightingFixtureKeyModal(null);
    setShowLightingExtractionGuideModal(false);
    setShowLightingScheduleUploadModal(false);
    setIsUploadingLightingScheduleScreenshot(false);
    setLightingScheduleUploadError(null);
    setLightingScheduleUploadItems([]);
    setIsDraggingLightingScheduleUpload(false);
    setIsLightingScheduleUploadHovered(false);
    setLightingSchedulePreviewSnapshots([]);
    setIsGuidedLightingExtractionFlow(false);
    setTakeoffPageSelectionStage(null);
    setShowTakeoffPageSelectionModal(false);
    setTakeoffLightingPages(new Set());
    setTakeoffPowerPages(new Set());
  }, []);

  useEffect(() => {
    setHasDismissedTakeoffOnboardingPrompt(false);
    setShowTakeoffOnboardingStartModal(false);
    resetGuidedTakeoffFlow();
  }, [estimationId, resetGuidedTakeoffFlow]);

  const getTakeoffLightingFixtureKeyItems = useCallback(() => {
    const keyComponents: any[] = Array.isArray((electricalKey as any)?.components)
      ? (electricalKey as any).components
      : [];
    const fixturesByCode = new Map<string, {
      code: string;
      name: string;
      manufacturer: string;
      description: string;
    }>();

    keyComponents.forEach((component) => {
      const isLighting =
        component?.symbolType === 'Lighting' || component?.componentType === 'Lighting';
      if (!isLighting) return;

      const code = String(component?.symbol || '').trim();
      if (!code) return;
      const normalizedCode = code.toUpperCase();

      const candidate = {
        code,
        name: String(component?.name || '').trim() || code,
        manufacturer: String(component?.manufacturer || '').trim(),
        description: String(component?.description || '').trim(),
      };

      const existing = fixturesByCode.get(normalizedCode);
      if (!existing) {
        fixturesByCode.set(normalizedCode, candidate);
        return;
      }

      fixturesByCode.set(normalizedCode, {
        code: existing.code,
        name: existing.name.length >= candidate.name.length ? existing.name : candidate.name,
        manufacturer:
          existing.manufacturer.length >= candidate.manufacturer.length
            ? existing.manufacturer
            : candidate.manufacturer,
        description:
          existing.description.length >= candidate.description.length
            ? existing.description
            : candidate.description,
      });
    });

    return Array.from(fixturesByCode.values()).sort((a, b) => a.code.localeCompare(b.code));
  }, [electricalKey]);

  const startGuidedLightingScheduleAcquisition = useCallback(() => {
    setIsGuidedLightingExtractionFlow(true);
    if (shouldPromptForLightingScheduleUpload()) {
      setShowLightingExtractionGuideModal(false);
      setLightingScheduleUploadItems([]);
      setLightingScheduleUploadError(null);
      setIsDraggingLightingScheduleUpload(false);
      setIsLightingScheduleUploadHovered(false);
      setShowLightingScheduleUploadModal(true);
      return;
    }

    setShowLightingScheduleUploadModal(false);
    setLightingScheduleUploadError(null);
    setShowLightingExtractionGuideModal(true);
  }, [shouldPromptForLightingScheduleUpload]);

  const isPowerOnlyTakeoff = String(estimation?.takeoff_scope || estimation?.takeoffScope || '').toLowerCase() === 'power_only';

  const continueTakeoffOnboardingMode = useCallback((mode: TakeoffOnboardingMode) => {
    const resolvedMode: TakeoffOnboardingMode = isPowerOnlyTakeoff ? 'power' : mode;
    setGuidedTakeoffMode(resolvedMode);

    if (resolvedMode === 'power') {
      const powerComponents = getPowerComponentsFromKey(electricalKey);
      if (powerComponents.length === 0) {
        alert('No power components are available in the key yet.');
        resetGuidedTakeoffFlow();
        return;
      }
      setGuidedTakeoffComponents(powerComponents);
      applyTakeoffPowerDefaultPages(powerComponents);
      setTakeoffPageSelectionStage('power');
      setShowTakeoffPageSelectionModal(true);
      return;
    }

    const lightingComponents = getLightingComponentsFromKey(electricalKey);
    const hasExtractedLighting = lightingComponents.length > 0 && lightingComponents.some(
      (c) => c.symbol && c.symbol.trim().length > 0 && c.symbol !== c.name
    );

    if (hasExtractedLighting) {
      if (mode === 'lighting') {
        setGuidedTakeoffComponents(lightingComponents);
        applyTakeoffLightingDefaultPages(lightingComponents);
        setTakeoffPageSelectionStage('lighting');
        setShowTakeoffPageSelectionModal(true);
        return;
      }
      const powerComponents = getPowerComponentsFromKey(electricalKey);
      const allComponents = [...powerComponents, ...lightingComponents];
      setGuidedTakeoffComponents(allComponents);
      applyTakeoffLightingDefaultPages(lightingComponents);
      applyTakeoffPowerDefaultPages(powerComponents);
      setTakeoffPageSelectionStage('lighting');
      setShowTakeoffPageSelectionModal(true);
      return;
    }

    startGuidedLightingScheduleAcquisition();
  }, [
    applyTakeoffLightingDefaultPages,
    applyTakeoffPowerDefaultPages,
    electricalKey,
    getLightingComponentsFromKey,
    getPowerComponentsFromKey,
    isPowerOnlyTakeoff,
    resetGuidedTakeoffFlow,
    startGuidedLightingScheduleAcquisition,
  ]);

  const handleTakeoffOnboardingSelectMode = useCallback((mode: TakeoffOnboardingMode) => {
    const resolvedMode: TakeoffOnboardingMode = isPowerOnlyTakeoff ? 'power' : mode;
    setHasDismissedTakeoffOnboardingPrompt(true);
    setShowTakeoffOnboardingStartModal(false);

    if (resolvedMode === 'full') {
      const fixtureKeyItems = getTakeoffLightingFixtureKeyItems();
      if (fixtureKeyItems.length > 0) {
        setTakeoffLightingFixtureKeyItems(fixtureKeyItems);
        setPendingTakeoffModeAfterLightingFixtureKeyModal(resolvedMode);
        setShowTakeoffLightingFixtureKeyModal(true);
        return;
      }
    }

    continueTakeoffOnboardingMode(resolvedMode);
  }, [continueTakeoffOnboardingMode, getTakeoffLightingFixtureKeyItems, isPowerOnlyTakeoff]);

  const handleConfirmTakeoffLightingFixtureKeyModal = useCallback(() => {
    const nextMode = pendingTakeoffModeAfterLightingFixtureKeyModal || 'full';
    setShowTakeoffLightingFixtureKeyModal(false);
    setTakeoffLightingFixtureKeyItems([]);
    setPendingTakeoffModeAfterLightingFixtureKeyModal(null);
    continueTakeoffOnboardingMode(nextMode);
  }, [continueTakeoffOnboardingMode, pendingTakeoffModeAfterLightingFixtureKeyModal]);

  const handleScanLightingScheduleFromTakeoffKeyModal = useCallback(() => {
    const nextMode = pendingTakeoffModeAfterLightingFixtureKeyModal || 'full';
    setShowTakeoffLightingFixtureKeyModal(false);
    setTakeoffLightingFixtureKeyItems([]);
    setPendingTakeoffModeAfterLightingFixtureKeyModal(null);
    setGuidedTakeoffMode(nextMode);
    startGuidedLightingScheduleAcquisition();
  }, [pendingTakeoffModeAfterLightingFixtureKeyModal, startGuidedLightingScheduleAcquisition]);

  const startGuidedLightingExtraction = useCallback(() => {
    handleStartLightingScheduleExtraction({ guided: true });
  }, [handleStartLightingScheduleExtraction]);

  const readImageFileAsDataUrl = useCallback((file: File): Promise<string> => (
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string' && reader.result.length > 0) {
          resolve(reader.result);
          return;
        }
        reject(new Error('Unable to read image file'));
      };
      reader.onerror = () => reject(reader.error || new Error('Unable to read image file'));
      reader.readAsDataURL(file);
    })
  ), []);

  const getLightingScheduleClipboardImageFiles = useCallback((clipboardData: DataTransfer | null): File[] => {
    if (!clipboardData) return [];

    const imageFiles: File[] = [];
    const seen = new Set<string>();
    const appendFile = (file: File | null) => {
      if (!file || !file.type.startsWith('image/')) return;
      const key = `${file.name}:${file.size}:${file.lastModified}`;
      if (seen.has(key)) return;
      seen.add(key);
      imageFiles.push(file);
    };

    Array.from(clipboardData.files || []).forEach((file) => appendFile(file));
    Array.from(clipboardData.items || []).forEach((item) => {
      if (!item.type.startsWith('image/')) return;
      appendFile(item.getAsFile());
    });

    return imageFiles;
  }, []);

  const queueLightingScheduleUploadFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    if (isUploadingLightingScheduleScreenshot) return;

    const invalidFiles: string[] = [];
    const imageFiles = files.filter((file) => {
      if (file.type.startsWith('image/')) return true;
      invalidFiles.push(file.name);
      return false;
    });

    if (invalidFiles.length > 0) {
      alert(`The following files were skipped because they are not images:\n${invalidFiles.join('\n')}`);
    }

    if (imageFiles.length === 0) return;

    const existingNames = new Set(
      lightingScheduleUploadItems.map((item) => item.fileName.toLowerCase())
    );
    const duplicates: string[] = [];
    const uniqueFiles = imageFiles.filter((file) => {
      const normalizedName = file.name.toLowerCase();
      if (existingNames.has(normalizedName)) {
        duplicates.push(file.name);
        return false;
      }
      existingNames.add(normalizedName);
      return true;
    });

    if (duplicates.length > 0) {
      alert(`The following files already exist and were skipped:\n${duplicates.join('\n')}`);
    }

    if (uniqueFiles.length === 0) return;

    setLightingScheduleUploadError(null);
    setIsUploadingLightingScheduleScreenshot(true);

    try {
      const uploadedItems = await Promise.all(
        uniqueFiles.map(async (file) => ({
          id: crypto.randomUUID(),
          fileName: file.name,
          fileSize: file.size,
          snapshot: await readImageFileAsDataUrl(file),
        }))
      );
      setLightingScheduleUploadItems((prev) => [...prev, ...uploadedItems]);
    } catch (error) {
      console.error('❌ Failed to read uploaded lighting schedule screenshot(s):', error);
      setLightingScheduleUploadError('Could not read one or more screenshots. Please try again.');
    } finally {
      setIsUploadingLightingScheduleScreenshot(false);
    }
  }, [isUploadingLightingScheduleScreenshot, lightingScheduleUploadItems, readImageFileAsDataUrl]);

  const handleLightingScheduleScreenshotUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';
    if (selectedFiles.length === 0) return;
    void queueLightingScheduleUploadFiles(selectedFiles);
  }, [queueLightingScheduleUploadFiles]);

  const handleRemoveLightingScheduleUploadItem = useCallback((itemId: string) => {
    setLightingScheduleUploadItems((prev) => prev.filter((item) => item.id !== itemId));
  }, []);

  const handleExtractUploadedLightingScheduleScreenshots = useCallback(async () => {
    if (isUploadingLightingScheduleScreenshot) return;
    if (lightingScheduleUploadItems.length === 0) {
      setLightingScheduleUploadError('Upload at least one schedule screenshot to continue.');
      return;
    }

    setLightingScheduleUploadError(null);
    setShowLightingScheduleUploadModal(false);
    const snapshots = lightingScheduleUploadItems.map((item) => item.snapshot);
    const didExtractFixtures = await runLightingScheduleExtraction(snapshots);
    if (!didExtractFixtures) {
      setShowLightingScheduleUploadModal(true);
      return;
    }
    setLightingScheduleUploadItems([]);
  }, [isUploadingLightingScheduleScreenshot, lightingScheduleUploadItems, runLightingScheduleExtraction]);

  const handleLightingScheduleUploadDragOver = useCallback((event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleLightingScheduleUploadDragEnter = useCallback((event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingLightingScheduleUpload(true);
  }, []);

  const handleLightingScheduleUploadDragLeave = useCallback((event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingLightingScheduleUpload(false);
  }, []);

  const handleLightingScheduleUploadDrop = useCallback((event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingLightingScheduleUpload(false);
    const droppedFiles = Array.from(event.dataTransfer.files || []);
    if (droppedFiles.length === 0) return;
    void queueLightingScheduleUploadFiles(droppedFiles);
  }, [queueLightingScheduleUploadFiles]);

  useEffect(() => {
    if (!showLightingScheduleUploadModal || !isLightingScheduleUploadHovered) return;

    const handleDocumentPaste = (event: ClipboardEvent) => {
      const pastedFiles = getLightingScheduleClipboardImageFiles(event.clipboardData);
      if (pastedFiles.length === 0) return;
      event.preventDefault();
      event.stopPropagation();
      setLightingScheduleUploadError(null);
      void queueLightingScheduleUploadFiles(pastedFiles);
    };

    document.addEventListener('paste', handleDocumentPaste);
    return () => {
      document.removeEventListener('paste', handleDocumentPaste);
    };
  }, [
    getLightingScheduleClipboardImageFiles,
    isLightingScheduleUploadHovered,
    queueLightingScheduleUploadFiles,
    showLightingScheduleUploadModal,
  ]);

  const handleGuidedLightingExtractionRetry = useCallback(() => {
    setShowLightingSchedulePreview(false);
    setExtractedLightingFixtures([]);
    setSelectedLightingFixtureIndices(new Set());
    setLightingScheduleSnapshot(null);
    setLightingScheduleJobs([]);
    setLightingExtractionAnimationSnapshot(null);
    setLightingScheduleUploadError(null);
    setLightingScheduleUploadItems([]);
    setIsDraggingLightingScheduleUpload(false);
    setIsLightingScheduleUploadHovered(false);
    setLightingSchedulePreviewSnapshots([]);
    if (shouldPromptForLightingScheduleUpload()) {
      setShowLightingExtractionGuideModal(false);
      setShowLightingScheduleUploadModal(true);
      return;
    }
    setShowLightingScheduleUploadModal(false);
    handleStartLightingScheduleExtraction({ guided: true });
  }, [handleStartLightingScheduleExtraction, shouldPromptForLightingScheduleUpload]);

  const handleGuidedLightingExtractAndContinue = useCallback(async () => {
    const upsertResult = await upsertSelectedLightingFixturesIntoKey();
    if (!upsertResult) return;

    const lightingComponents = getLightingComponentsFromKey(upsertResult.projectKey);
    if (lightingComponents.length === 0) {
      alert('No lighting components were available after extraction.');
      resetGuidedTakeoffFlow();
      return;
    }

    if (guidedTakeoffMode === 'lighting') {
      setGuidedTakeoffComponents(lightingComponents);
      applyTakeoffLightingDefaultPages(lightingComponents);
      setTakeoffPageSelectionStage('lighting');
      setShowTakeoffPageSelectionModal(true);
      return;
    }

    if (guidedTakeoffMode === 'full') {
      const powerComponents = getPowerComponentsFromKey(upsertResult.projectKey);
      const allComponents = [...powerComponents, ...lightingComponents];
      setGuidedTakeoffComponents(allComponents);
      applyTakeoffLightingDefaultPages(lightingComponents);
      applyTakeoffPowerDefaultPages(powerComponents);
      setTakeoffPageSelectionStage('lighting');
      setShowTakeoffPageSelectionModal(true);
      return;
    }
  }, [
    applyTakeoffLightingDefaultPages,
    applyTakeoffPowerDefaultPages,
    getLightingComponentsFromKey,
    getPowerComponentsFromKey,
    guidedTakeoffMode,
    resetGuidedTakeoffFlow,
    upsertSelectedLightingFixturesIntoKey,
  ]);

  const handleGuidedTakeoffPageConfirm = useCallback(() => {
    if (guidedTakeoffComponents.length === 0 || !guidedTakeoffMode || !takeoffPageSelectionStage) return;

    if (takeoffPageSelectionStage === 'lighting') {
      if (guidedTakeoffMode === 'full') {
        const hasPowerComponents = guidedTakeoffComponents.some(
          (component) => component.symbolType !== 'Lighting' && component.componentType !== 'Lighting'
        );
        if (!hasPowerComponents) {
          startParallelCountingWithPageResolver(guidedTakeoffComponents, () => takeoffLightingPages);
          resetGuidedTakeoffFlow();
          return;
        }
        setTakeoffPageSelectionStage('power');
        return;
      }

      startParallelCountingWithPageResolver(guidedTakeoffComponents, () => takeoffLightingPages);
      resetGuidedTakeoffFlow();
      return;
    }

    const pageLabelByPage = new Map<number, string>();
    (pageIndex || []).forEach((entry) => {
      pageLabelByPage.set(entry.page, String(entry.label || '').toLowerCase());
    });
    const isLightingSheetPage = (page: number) => {
      const label = pageLabelByPage.get(page) || '';
      return (
        label.includes('lighting') ||
        label.includes('reflected ceiling plan') ||
        label.includes('rcp')
      );
    };

    const filteredPowerPages = new Set<number>(
      Array.from(takeoffPowerPages).filter((pg) => !isLightingSheetPage(pg))
    );
    const combinedLightingAndPowerPages = new Set<number>([
      ...Array.from(takeoffLightingPages),
      ...Array.from(takeoffPowerPages),
    ]);

    const classifyTakeoffComponent = (component: GuidedCountComponent) => {
      const componentNameLower = String(component.name || '')
        .toLowerCase()
        .replace(/[_-]/g, ' ')
        .trim();
      const componentTypeLower = String(component.componentType || '').toLowerCase();
      const isLighting =
        component.name === '__LIGHTING_GROUP__' ||
        component.symbolType === 'Lighting' ||
        component.componentType === 'Lighting';
      const isReceptacle =
        component.name === '__RECEPTACLES_GROUP__' ||
        componentTypeLower === 'receptacles' ||
        componentTypeLower === 'power' ||
        componentNameLower.includes('receptacle') ||
        componentNameLower.includes('duplex');
      const isTeleData =
        component.name === '__TELE_DATA_GROUP__' ||
        componentTypeLower === 'telecom' ||
        componentTypeLower === 'low voltage' ||
        componentNameLower.includes('tele/data') ||
        componentNameLower.includes('tele data') ||
        componentNameLower.includes('telecom') ||
        componentNameLower.includes('data') ||
        componentNameLower.includes('communication');
      const isJunctionBox =
        componentNameLower.includes('junction box') ||
        componentNameLower.includes('j box') ||
        componentNameLower.includes('j-box') ||
        componentNameLower.includes('jbox');
      const isSwitch =
        componentTypeLower === 'switches' ||
        (componentNameLower.includes('switch') && !isReceptacle);
      return { isLighting, isReceptacle, isTeleData, isJunctionBox, isSwitch };
    };

    startParallelCountingWithPageResolver(guidedTakeoffComponents, (component) => {
      const { isLighting, isReceptacle, isTeleData, isJunctionBox, isSwitch } = classifyTakeoffComponent(component);
      if (guidedTakeoffMode === 'power') {
        if (isReceptacle || isTeleData) return filteredPowerPages;
        return takeoffPowerPages;
      }
      if (isLighting) return takeoffLightingPages;
      if (isReceptacle || isTeleData) return filteredPowerPages;
      if (isJunctionBox || isSwitch) return combinedLightingAndPowerPages;
      return takeoffPowerPages;
    });
    resetGuidedTakeoffFlow();
  }, [
    guidedTakeoffComponents,
    guidedTakeoffMode,
    pageIndex,
    resetGuidedTakeoffFlow,
    startParallelCountingWithPageResolver,
    takeoffLightingPages,
    takeoffPageSelectionStage,
    takeoffPowerPages,
  ]);

  const handleGuidedTakeoffPageBack = useCallback(() => {
    if (guidedTakeoffMode === 'full' && takeoffPageSelectionStage === 'power') {
      setTakeoffPageSelectionStage('lighting');
    }
  }, [guidedTakeoffMode, takeoffPageSelectionStage]);

  const handleGuidedTakeoffCancel = useCallback(() => {
    setShowTakeoffOnboardingStartModal(false);
    setShowTakeoffLightingFixtureKeyModal(false);
    setTakeoffLightingFixtureKeyItems([]);
    setPendingTakeoffModeAfterLightingFixtureKeyModal(null);
    setHasDismissedTakeoffOnboardingPrompt(true);
    setLightingScheduleMode(false);
    setShowLightingScheduleConfirmModal(false);
    setShowLightingScheduleUploadModal(false);
    setShowLightingSchedulePreview(false);
    setLightingScheduleSelection(null);
    setLightingScheduleSnapshot(null);
    setLightingScheduleJobs([]);
    setLightingExtractionAnimationSnapshot(null);
    setLightingScheduleUploadError(null);
    setLightingScheduleUploadItems([]);
    setIsDraggingLightingScheduleUpload(false);
    setIsLightingScheduleUploadHovered(false);
    setLightingSchedulePreviewSnapshots([]);
    setPendingLightingScheduleStartPage(null);
    resetGuidedTakeoffFlow();
  }, [resetGuidedTakeoffFlow]);

  const takeoffPowerDevices = useMemo(() => {
    let hasJunctionBoxes = false;
    let hasOutlets = false;
    let hasSwitches = false;
    let junctionBoxesImage = '';
    let outletsImage = '';
    let switchesImage = '';
    let outletsImagePriority = 0;

    guidedTakeoffComponents.forEach((component) => {
      const isLighting =
        component.symbolType === 'Lighting' || component.componentType === 'Lighting';
      if (isLighting) return;

      const rawName = String(component.name || '').trim();
      if (!rawName) return;
      const normalizedName = rawName.replace(/[_-]/g, ' ').replace(/\s+/g, ' ').trim();
      const nameLower = normalizedName.toLowerCase();
      const referenceImage = String(component.referenceImage || '');
      const componentTypeLower = String(component.componentType || '').toLowerCase();

      const isReceptacle =
        component.componentType === 'Receptacles' ||
        component.symbolType === 'Receptacle' ||
        nameLower.includes('receptacle');

      const isTeleDataOutlet =
        componentTypeLower === 'telecom' ||
        componentTypeLower === 'low voltage' ||
        nameLower.includes('tele/data') ||
        nameLower.includes('tele data') ||
        nameLower.includes('telecom') ||
        (nameLower.includes('outlet') && (nameLower.includes('tele') || nameLower.includes('data')));

      const isJunctionBox =
        nameLower.includes('junction box') ||
        nameLower.includes('j-box') ||
        nameLower.includes('j box');

      const isSwitch =
        component.componentType === 'Switches' ||
        (nameLower.includes('switch') && !isReceptacle);

      if (isJunctionBox) {
        hasJunctionBoxes = true;
        if (!junctionBoxesImage && referenceImage) {
          junctionBoxesImage = referenceImage;
        }
      }

      if (isReceptacle || isTeleDataOutlet) {
        hasOutlets = true;

        let priority = 1;
        const isWallMountedDuplexReceptacle =
          nameLower.includes('wall mounted duplex receptacle') ||
          (nameLower.includes('wall mounted') && nameLower.includes('duplex receptacle'));
        if (isWallMountedDuplexReceptacle) priority = 3;
        else if (isTeleDataOutlet) priority = 2;

        if (referenceImage && priority > outletsImagePriority) {
          outletsImage = referenceImage;
          outletsImagePriority = priority;
        } else if (!outletsImage && referenceImage) {
          outletsImage = referenceImage;
        }
      }

      if (isSwitch) {
        hasSwitches = true;
        if (!switchesImage && referenceImage) {
          switchesImage = referenceImage;
        }
      }
    });

    const devices: Array<{ name: string; referenceImage: string }> = [];
    if (hasJunctionBoxes) {
      devices.push({ name: 'Junction Boxes', referenceImage: junctionBoxesImage });
    }
    if (hasOutlets) {
      devices.push({ name: 'Outlets', referenceImage: outletsImage });
    }
    if (hasSwitches) {
      devices.push({ name: 'Switches', referenceImage: switchesImage });
    }
    return devices;
  }, [guidedTakeoffComponents]);

  // Keyword-based type classification (mirrors handler.js)
  const classifyToTypeClass = useCallback((name: string): string => {
    const n = name.toLowerCase().replace(/[_\-]/g, ' ');
    if (n.includes('gfci') || n.includes('gfi') || n.includes('ground fault')) return 'gfci_receptacle';
    if (n.includes('square')) return 'duplex_receptacle';
    if (n.includes('quadruplex') || n.includes('quad') || n.includes('double duplex') || n.includes('fourplex')) return 'quadruplex_receptacle';
    if (n.includes('halfswitch') || n.includes('half switch') || n.includes('switched half')) return 'halfswitch_specific_circuit_receptacle';
    if (n.includes('dedicated') || n.includes('individual circuit') || n.includes('specified circuit') || n.includes('isolated') || n.includes('specific circuit') || n.includes('special circuit') || n.includes('special') || n.includes('emergency')) return 'dedicated_receptacle';
    return 'duplex_receptacle';
  }, []);

  // Keyword-based mounting detection
  const classifyToMounting = useCallback((name: string): string => {
    const n = name.toLowerCase().replace(/[_\-]/g, ' ');
    if (n.includes('ceiling')) return 'ceiling_mounted';
    if (n.includes('floor')) return 'floor_mounted';
    return 'wall_mounted';
  }, []);

  const RECEPTACLE_TYPES = ['duplex_receptacle', 'gfci_receptacle', 'dedicated_receptacle', 'halfswitch_specific_circuit_receptacle', 'quadruplex_receptacle'] as const;
  const MOUNTINGS = ['wall_mounted', 'ceiling_mounted', 'floor_mounted'] as const;

  // 16 combined standard classes: 5 types × 3 mountings + 1 furniture
  // Generate all standard receptacle entries — always adds all of them
  const generateStandardReceptacles = useCallback((components: Array<{ name: string; componentType?: string; referenceImage?: string; color?: string; isStandardClass?: boolean }>) => {
    // Grab the first ref image from any extracted receptacle (used for all standards)
    let defaultImage = '';
    for (const comp of components) {
      if (comp.componentType !== 'Receptacles' || (comp as any).isStandardClass) continue;
      if (comp.referenceImage) {
        defaultImage = comp.referenceImage;
        break;
      }
    }

    const standardComponents: Array<any> = [];
    for (const [combinedKey, standardName] of Object.entries(STANDARD_RECEPTACLE_CLASSES)) {
      standardComponents.push({
        color: getComponentColor({
          name: standardName,
          symbol: standardName,
          componentType: 'Receptacles',
        }),
        name: standardName,
        symbol: standardName,
        componentType: 'Receptacles',
        description: `Auto-generated standard class for ${combinedKey.replace(/__/g, ' + ').replace(/_/g, ' ')}`,
        subscript: '',
        pages: [] as any[],
        totalCount: 0,
        referenceImage: defaultImage,
        hasReferenceImage: !!defaultImage,
        isStandardClass: true,
      });
    }
    return standardComponents;
  }, []);

  // Handle adding extracted key components to the project key AND start counting (ONE AT A TIME)
  const handleAddToKeyAndCount = useCallback(async () => {
    if (extractedKeyPreview.length === 0) return;
    
    console.log('🔑 Adding extracted components to key and starting sequential count...');
    
    const componentsWithImages = extractedKeyPreview.filter((s, idx) => selectedKeyPreviewIndices.has(idx) && s.croppedImage);
    
    if (componentsWithImages.length === 0) {
      alert('No symbols with images to add. Try selecting the legend area again.');
      return;
    }
    
    const existingComponents = electricalKey?.components || [];
    const existingNames = existingComponents.map((c: any) => c.name);
    
    const newComponents = componentsWithImages.map((symbol) => {
      const uniqueName = getUniqueComponentName(symbol.name, existingNames);
      existingNames.push(uniqueName);
      const componentType = symbol.componentType || 'Other';
      const symbolValue = symbol.abbreviation || symbol.name;
      return {
        color: getComponentColor({
          name: uniqueName,
          symbol: symbolValue,
          componentType,
          symbolType: componentType === 'Lighting' ? 'Lighting' : undefined,
        }),
        name: uniqueName,
        symbol: symbolValue,
        componentType,
        description: symbol.description || '',
        subscript: symbol.subscript || '',
        pages: [] as any[],
        totalCount: 0,
        referenceImage: symbol.croppedImage as string,
        hasReferenceImage: true,
      };
    });

    // Generate standard receptacle classes from the new receptacle components
    const allReceptacleComponents = [...existingComponents, ...newComponents];
    const standardReceptacles = generateStandardReceptacles(allReceptacleComponents);
    
    // Filter out any pre-existing standard classes to avoid duplicates, 
    // and also filter out legacy standard classes that should be removed.
    const legacyNames = new Set(LEGACY_STANDARD_RECEPTACLE_NAMES);
    const filteredExisting = existingComponents.filter((c: any) => 
      !(c.isStandardClass && legacyNames.has(c.name))
    );
    
    const existingStandardNames = new Set(filteredExisting.filter((c: any) => c.isStandardClass).map((c: any) => c.name));
    const newStandards = standardReceptacles.filter(s => !existingStandardNames.has(s.name));
    if (newStandards.length > 0) {
      console.log(`⚡ Generated ${newStandards.length} standard receptacle classes: ${newStandards.map(s => s.name).join(', ')}`);
    }
    
    const mergedComponents = [...filteredExisting, ...newComponents, ...newStandards];
    
    const projectKey = {
      components: mergedComponents,
      extractedAt: new Date().toISOString(),
      pdfHash: (electricalKey as any)?.pdfHash || '',
    };
    
    console.log(`📦 Added ${newComponents.length} new components (total: ${mergedComponents.length})`);
    
    setShowKeyExtractionPreview(false);
    setExtractedKeyPreview([]);
    setSelectedKeyPreviewIndices(new Set());
    setKeyExtractionSnapshot(null);
    setKeyExtractionJobs([]);
    
    if (onKeyExtracted) {
      await onKeyExtracted(projectKey);
    }
    
    if (!pdfDocument || !numPages) {
      console.warn('PDF not ready for counting');
      return;
    }
    
    startCountingComponents(newComponents.map(c => ({
      color: c.color, name: c.name, symbol: c.symbol, referenceImage: c.referenceImage, componentType: c.componentType, subscript: c.subscript, description: c.description
    })));
  }, [extractedKeyPreview, selectedKeyPreviewIndices, onKeyExtracted, pdfDocument, numPages, electricalKey, startCountingComponents]);

  // Handle adding extracted key components to the project key WITHOUT counting
  const handleAddToKeyOnly = useCallback(async () => {
    if (extractedKeyPreview.length === 0) return;
    
    console.log('🔑 Adding extracted components to key (no counting)...');
    
    const componentsWithImages = extractedKeyPreview.filter((s, idx) => selectedKeyPreviewIndices.has(idx) && s.croppedImage);
    
    if (componentsWithImages.length === 0) {
      alert('No symbols with images to add. Try selecting the legend area again.');
      return;
    }
    
    const existingComponents = electricalKey?.components || [];
    const existingNames = existingComponents.map((c: any) => c.name);
    
    const newComponents = componentsWithImages.map((symbol) => {
      const uniqueName = getUniqueComponentName(symbol.name, existingNames);
      existingNames.push(uniqueName);
      const componentType = symbol.componentType || 'Other';
      const symbolValue = symbol.abbreviation || symbol.name;
      return {
        color: getComponentColor({
          name: uniqueName,
          symbol: symbolValue,
          componentType,
          symbolType: componentType === 'Lighting' ? 'Lighting' : undefined,
        }),
        name: uniqueName,
        symbol: symbolValue,
        componentType,
        description: symbol.description || '',
        subscript: symbol.subscript || '',
        pages: [] as any[],
        totalCount: 0,
        referenceImage: symbol.croppedImage as string,
        hasReferenceImage: true,
      };
    });

    // Generate standard receptacle classes
    const allReceptacleComponents = [...existingComponents, ...newComponents];
    const standardReceptacles = generateStandardReceptacles(allReceptacleComponents);
    const existingStandardNames = new Set(existingComponents.filter((c: any) => c.isStandardClass).map((c: any) => c.name));
    const newStandards = standardReceptacles.filter(s => !existingStandardNames.has(s.name));
    if (newStandards.length > 0) {
      console.log(`⚡ Generated ${newStandards.length} standard receptacle classes: ${newStandards.map(s => s.name).join(', ')}`);
    }
    
    const mergedComponents = [...existingComponents, ...newComponents, ...newStandards];
    
    const projectKey = {
      components: mergedComponents,
      extractedAt: new Date().toISOString(),
      pdfHash: (electricalKey as any)?.pdfHash || '',
    };
    
    console.log(`📦 Added ${newComponents.length} new components to key only (total: ${mergedComponents.length})`);
    
    setShowKeyExtractionPreview(false);
    setExtractedKeyPreview([]);
    setSelectedKeyPreviewIndices(new Set());
    setKeyExtractionSnapshot(null);
    setKeyExtractionJobs([]);
    
    if (onKeyExtracted) {
      await onKeyExtracted(projectKey);
    }
  }, [extractedKeyPreview, selectedKeyPreviewIndices, onKeyExtracted, electricalKey]);

  // Handle adding extracted lighting fixtures to project key AND start counting
  const handleAddLightingFixturesToKeyAndCount = useCallback(async () => {
    const upsertResult = await upsertSelectedLightingFixturesIntoKey();
    if (!upsertResult) return;
    
    if (!pdfDocument || !numPages) {
      console.warn('PDF not ready for counting');
      return;
    }

    startCountingComponents(upsertResult.uniqueNew.map(c => ({
      color: c.color, name: c.name, symbol: c.symbol, referenceImage: c.referenceImage, symbolType: c.symbolType, componentType: c.componentType, description: c.description
    })));
  }, [numPages, pdfDocument, startCountingComponents, upsertSelectedLightingFixturesIntoKey]);

  // Helper function to crop symbol images from the snapshot using bounding boxes
  const cropSymbolImages = async (
    snapshotBase64: string, 
    symbols: Array<{ name: string; abbreviation: string; description: string; componentType: string; subscript: string; box_2d: number[] | null; rowIndex: number }>
  ): Promise<Array<{ name: string; abbreviation: string; description: string; componentType: string; subscript: string; box_2d: number[] | null; croppedImage: string | null; rowIndex: number }>> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const imgWidth = img.width;
        const imgHeight = img.height;
        
        console.log(`📐 Snapshot dimensions: ${imgWidth}x${imgHeight}`);
        
        const symbolsWithImages = symbols.map((symbol) => {
          if (!symbol.box_2d || symbol.box_2d.length !== 4) {
            console.log(`⚠️ No bounding box for: ${symbol.name}`);
            return { ...symbol, croppedImage: null };
          }
          
          // Convert normalized coordinates (0-1000) to pixel coordinates
          const [ymin, xmin, ymax, xmax] = symbol.box_2d;
          const x = Math.round((xmin / 1000) * imgWidth);
          const y = Math.round((ymin / 1000) * imgHeight);
          const width = Math.round(((xmax - xmin) / 1000) * imgWidth);
          const height = Math.round(((ymax - ymin) / 1000) * imgHeight);
          
          // Validate dimensions
          if (width <= 0 || height <= 0 || x < 0 || y < 0 || x + width > imgWidth || y + height > imgHeight) {
            console.log(`⚠️ Invalid bounding box for: ${symbol.name}`, { x, y, width, height });
            return { ...symbol, croppedImage: null };
          }
          
          // Create canvas for cropped image
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          
          if (ctx) {
            ctx.drawImage(img, x, y, width, height, 0, 0, width, height);
            const croppedImage = canvas.toDataURL('image/png');
            console.log(`✅ Cropped image for: ${symbol.name} (${width}x${height})`);
            return { ...symbol, croppedImage };
          }
          
          return { ...symbol, croppedImage: null };
        });
        
        const withImages = symbolsWithImages.filter(s => s.croppedImage).length;
        console.log(`📦 Cropped ${withImages}/${symbols.length} symbol images`);
        
        resolve(symbolsWithImages);
      };
      
      img.onerror = () => {
        console.error('❌ Failed to load snapshot for cropping');
        resolve(symbols.map(s => ({ ...s, croppedImage: null })));
      };
      
      img.src = snapshotBase64;
    });
  };

  // Ref to track if we should auto-trigger from Quick Count
  const quickCountTriggerRef = useRef(false);
  
  // State to force redraw after annotations load
  const [forceRedraw, setForceRedraw] = useState(0);

  // Handle confirm autocount and run the detection (STREAMING VERSION)
  // Handle confirm autocount - uses shared counting flow
  const handleConfirmAddToKey = useCallback(async () => {
    if (!referenceAreaImage || !newComponentName.trim() || !electricalKey) return;
    
    setIsAddingComponent(true);
    try {
      // Assign next available color
      const COLOR_PALETTE = [
        '#FF0000', '#0080FF', '#00FF00', '#FFD700', '#FF00FF',
        '#00FFFF', '#FF6600', '#FF1493', '#7FFF00', '#FF69B4'
      ];
      const usedColors = (electricalKey.components || []).map((c: any) => c.color);
      const availableColor = COLOR_PALETTE.find(c => !usedColors.includes(c)) ||
                            COLOR_PALETTE[(electricalKey.components || []).length % COLOR_PALETTE.length];

      const componentToAdd = {
        name: newComponentName.toUpperCase().trim(),
        symbol: '',
        description: '',
        color: newComponentColor || availableColor,
        referenceImage: referenceAreaImage,
        symbolType: 'Power',
        componentType: 'user-added',
        pages: [],
        totalCount: 0
      };

      const updatedKey = {
        ...electricalKey,
        components: [...(electricalKey.components || []), componentToAdd]
      };

      // Save to Supabase
      const { error } = await supabase
        .from('estimations')
        .update({ project_key: updatedKey })
        .eq('id', estimationId);

      if (error) throw error;

      // Sync to library if user is logged in
      try {
        if (user) {
          await componentLibraryService.syncComponentToLibrary(user.id, {
            user_id: user.id,
            component_name: componentToAdd.name,
            symbol: componentToAdd.symbol,
            color: componentToAdd.color
          });
        }
      } catch (libErr) {
        console.warn('Failed to sync to library:', libErr);
      }

      console.log('✅ Component added to key with reference image:', componentToAdd.name);
      
      // Notify parent/sidebar to refresh without triggering bulk extraction UI
      // or any expensive counting logic.
      if (onAnnotationSaved) {
        // We pass an empty array to signal that no annotations were changed,
        // but the sidebar should still refresh its key data.
        await onAnnotationSaved([]);
      }

      // Close modal and reset
      setShowNewComponentModal(false);
      setNewComponentName('');
      setReferenceAreaImage(null);
      setIsAddingNewOnly(false);
      setIsAddingNewOnlyScanMode(false);
      setAutocountMode(null);
      setAutocountSelection(null);
      setPendingAutocountComponent(null);
      setQueuedComponents([]);
      setEditingQueuedName('');
      setShowAutocountConfirmModal(false);
      setShowAutocountReviewModal(false);
      setIsAutocounting(false);
      setAutocountProgress('');
      
    } catch (err) {
      console.error('Error adding component to key:', err);
      alert('Failed to add component to key. Please try again.');
    } finally {
      setIsAddingComponent(false);
    }
  }, [referenceAreaImage, newComponentName, newComponentColor, electricalKey, estimationId, onAnnotationSaved, user]);

  const {
    handleStartQuickCount,
    handleStartAreaSelection,
    handleRetakeAreaSelection,
    handleQuickAutocountFromReference,
    handleConfirmAutocount,
    handleBatchAutocount,
    toggleDetectionSelection,
  } = useAutocountFlow({
    newComponentName,
    newComponentColor,
    electricalKey,
    isAddingNewOnly,
    selectedComponentsForBatch,
    referenceAreaImage,
    pendingAutocountComponent,
    pdfDocument,
    numPages,
    onAddComponentToKey,
    onSwitchToCountingTab,
    setIsAddingComponent,
    setShowNewComponentModal,
    setNewComponentName,
    setNewComponentColor,
    setPendingAutocountComponent,
    setIsAddingNewOnlyScanMode,
    setAutocountMode,
    setAutocountSelection,
    setReferenceAreaImage,
    setQuickCountSearch,
    setShowAutocountConfirmModal,
    setSelectedComponentsForBatch,
    setSelectedDetectionsToKeep,
    quickCountTriggerRef,
    startCountingComponents,
  });

  // Auto-trigger handleConfirmAutocount when Quick Count sets state
  useEffect(() => {
    // Only trigger if both are set AND this is from Quick Count (not from manual area selection)
    if (referenceAreaImage && pendingAutocountComponent && !autocountMode && !showAutocountConfirmModal && quickCountTriggerRef.current) {
      console.log('🚀 Auto-triggering autocount from Quick Count');
      quickCountTriggerRef.current = false;
      
      // Small delay to ensure all state is settled
      setTimeout(() => {
        handleConfirmAutocount();
      }, 100);
    }
  }, [referenceAreaImage, pendingAutocountComponent, autocountMode, showAutocountConfirmModal, handleConfirmAutocount]);

  const { runConcurrentLaborEstimation, reportUsageForFinishReview } = useEstimationAndBilling({
          estimationId,
    userId: user?.id,
    setLaborEstimationProgress,
    setShowLaborEstimationModal,
  });

  const handleExportDatasetZip = useCallback(async () => {
    if (isExportingDataset) return;
    setIsExportingDataset(true);
    setDatasetExportError(null);

    const sanitizePathSegment = (raw: string, fallback: string): string => {
      const safe = (raw || '')
        .trim()
        .replace(/[/\\?%*:|"<>]/g, '_')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');
      return safe || fallback;
    };

    const extensionFromMime = (mimeType: string): string => {
      const normalized = (mimeType || '').toLowerCase();
      if (normalized.includes('jpeg') || normalized.includes('jpg')) return 'jpg';
      if (normalized.includes('webp')) return 'webp';
      if (normalized.includes('gif')) return 'gif';
      return 'png';
    };

    const readThumbnail = async (thumbnail: string): Promise<{ bytes: Uint8Array; extension: string } | null> => {
      if (!thumbnail || typeof thumbnail !== 'string') return null;

      if (thumbnail.startsWith('data:')) {
        const dataUrlMatch = thumbnail.match(/^data:([^;,]+)?(;base64)?,(.*)$/i);
        if (!dataUrlMatch) return null;
        const mimeType = (dataUrlMatch[1] || 'image/png').toLowerCase();
        const isBase64 = Boolean(dataUrlMatch[2]);
        const payload = dataUrlMatch[3] || '';
        let bytes: Uint8Array;

        if (isBase64) {
          const decoded = atob(payload);
          const arr = new Uint8Array(decoded.length);
          for (let i = 0; i < decoded.length; i += 1) arr[i] = decoded.charCodeAt(i);
          bytes = arr;
        } else {
          const decoded = decodeURIComponent(payload);
          bytes = new TextEncoder().encode(decoded);
        }

        return { bytes, extension: extensionFromMime(mimeType) };
      }

      const response = await fetch(thumbnail);
      if (!response.ok) return null;
      const blob = await response.blob();
      const buffer = await blob.arrayBuffer();
      return {
        bytes: new Uint8Array(buffer),
        extension: extensionFromMime(blob.type || 'image/png'),
      };
    };

    try {
      const zip = new JSZip();
      const manifest: Array<{
        detectionId: string;
        componentName: string;
        className: string;
        approved: boolean;
        page: number | null;
        confidence: number | null;
        file: string;
      }> = [];

      let writtenCount = 0;
      let skippedCount = 0;

      for (const componentData of multiComponentDetections) {
        const componentName = componentData?.componentName || 'unclassified';
        const componentNameLower = String(componentName).toLowerCase();
        const selections = allComponentSelections.get(componentName) || new Set<string>();
        const detections = Array.isArray(componentData?.detections) ? componentData.detections : [];
        const isReceptaclesClass =
          componentName === '__RECEPTACLES_GROUP__' ||
          componentNameLower.includes('receptacle') ||
          componentNameLower.includes('duplex');

        for (let idx = 0; idx < detections.length; idx += 1) {
          const det = detections[idx];
          const classOverride = detectionClassifications.get(det.id);
          const predictedClass = det?.prediction?.classifiedName;
          let classNameRaw = '';

          if (isReceptaclesClass) {
            classNameRaw = 'receptacles';
          } else if (componentName === '__TELE_DATA_GROUP__') {
            classNameRaw = classOverride || predictedClass || teleDataGroupComponents[0]?.name || 'tele_data';
          } else if (componentName === '__LIGHTING_GROUP__') {
            classNameRaw = classOverride || predictedClass || 'lighting';
          } else {
            classNameRaw = classOverride || predictedClass || componentName || 'unclassified';
          }

          const classFolder = sanitizePathSegment(classNameRaw, 'unclassified');
          const isApproved = selections.has(det.id);
          const folder = isApproved
            ? classFolder
            : (classFolder.startsWith('not_') ? classFolder : `not_${classFolder}`);
          const detId = sanitizePathSegment(String(det.id || `det_${idx + 1}`), `det_${idx + 1}`);
          const page = Number(det?.prediction?.page);
          const pagePart = Number.isFinite(page) && page > 0 ? `p${String(page).padStart(3, '0')}` : 'p000';
          const thumbPayload = await readThumbnail(det?.thumbnail || '');

          if (!thumbPayload) {
            skippedCount += 1;
            continue;
          }

          const fileName = `${pagePart}_${detId}.${thumbPayload.extension}`;
          const zipPath = `${folder}/${fileName}`;
          zip.file(zipPath, thumbPayload.bytes);
          writtenCount += 1;

          manifest.push({
            detectionId: String(det.id || ''),
            componentName: String(componentName),
            className: String(classNameRaw),
            approved: isApproved,
            page: Number.isFinite(page) ? page : null,
            confidence: Number.isFinite(Number(det?.prediction?.confidence))
              ? Number(det?.prediction?.confidence)
              : null,
            file: zipPath,
          });
        }
      }

      if (writtenCount === 0) {
        throw new Error('No detection thumbnails were available to export.');
      }

      zip.file(
        'manifest.json',
        JSON.stringify(
          {
            generatedAt: new Date().toISOString(),
            totalDetections: manifest.length,
            skippedDetections: skippedCount,
            detections: manifest,
          },
          null,
          2
        )
      );

      const zipBlob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 },
      });

      const baseName = sanitizePathSegment((file?.name || 'dataset').replace(/\.pdf$/i, ''), 'dataset');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const downloadName = `${baseName}_detections_${timestamp}.zip`;
      const url = URL.createObjectURL(zipBlob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = downloadName;
      anchor.click();
      URL.revokeObjectURL(url);
      console.log(`✅ Exported detection dataset zip (${writtenCount} files, ${skippedCount} skipped)`);
    } catch (error: any) {
      console.error('❌ Dataset export failed:', error);
      setDatasetExportError(error?.message || 'Dataset export failed. Please try again.');
    } finally {
      setIsExportingDataset(false);
    }
  }, [
    allComponentSelections,
    detectionClassifications,
    file?.name,
    isExportingDataset,
    multiComponentDetections,
    teleDataGroupComponents,
  ]);

  // Handle finish multi-component review and save all annotations
  const handleFinishMultiComponentReview = useCallback(async () => {
    if (isFinalizingReview) return;

    setIsFinalizingReview(true);
    setReviewFinalizeError(null);

    try {
    console.log('💾 Saving all reviewed components');
    console.log('🗺️ detectionClassifications at Finish:', detectionClassifications.size, 'entries');
    console.log('🗺️ detectionClassifications contents:', JSON.stringify(Array.from(detectionClassifications.entries())));
    console.log('🗺️ multiComponentDetections:', multiComponentDetections.map(cd => ({ name: cd.componentName, count: cd.detections.length })));
    console.log('🗺️ receptacleGroupComponents:', receptacleGroupComponents.map(rc => rc.name));
    console.log('🗺️ teleDataGroupComponents:', teleDataGroupComponents.map(tc => tc.name));
    console.log('🗺️ lightingGroupComponents:', lightingGroupComponents.map(lc => lc.name));
    
    // Prepare data
    const componentsToEstimate: Array<{ name: string; count: number }> = [];
    let finishUsageUnits = 0;
    
    // Get canvas dimensions once
    const annotationCanvas = canvasRef.current;
    const pdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
    const canvasWidth = pdfCanvas?.width || annotationCanvas?.width || 8000;
    const canvasHeight = pdfCanvas?.height || annotationCanvas?.height || 5714;
    
    // Collect all new annotations first (so we don't have state batching issues)
    const allNewAnnotations: any[] = [];
    const savePromises: Promise<any>[] = [];
    let updatedAnnotationsForSave: Annotation[] | null = null;
    
    // Helper to create save promise for a single component's annotations
    // Accumulate annotations per component to avoid race conditions on concurrent saves
    const pendingSavesByComponent = new Map<string, any[]>();
    const createSavePromise = (componentName: string, newAnnotations: any[]) => {
      // Allow save without file?.id - receptacles/tele/data use file_id=null (global)
      if (!supabase || !estimationId) return null;
      const existing = pendingSavesByComponent.get(componentName) || [];
      pendingSavesByComponent.set(componentName, [...existing, ...newAnnotations]);
      return null; // actual save happens below in flushSaves
    };
    const flushSaves = async () => {
      if (pendingSavesByComponent.size === 0) return;
      console.log('ℹ️ flushSaves legacy DB path disabled; unified v2 save pipeline is authoritative');
    };

    // Prepare annotations and database save promises for each component
    for (const componentData of multiComponentDetections) {
      console.log(`🔍 Processing component: "${componentData.componentName}" (${componentData.detections.length} total detections)`);
      const selections = allComponentSelections.get(componentData.componentName) || new Set();
      const approvedDetections = componentData.detections.filter(det => selections.has(det.id));
      console.log(`  ✅ ${approvedDetections.length} approved out of ${componentData.detections.length}`);
      finishUsageUnits += approvedDetections.length;
      
      if (approvedDetections.length === 0) {
        console.log(`⏭️ Skipping ${componentData.componentName} - no detections approved`);
        continue;
      }
      
      // Grouped receptacles: split by user-editable classification
      if (componentData.componentName === '__RECEPTACLES_GROUP__' && receptacleGroupComponents.length > 0) {
        console.log('🔌 HIT RECEPTACLES GROUP BRANCH');
        const defaultName = receptacleGroupComponents[0].name;
        const colorByName = new Map(receptacleGroupComponents.map(rc => [rc.name, rc.color]));

        // Group approved detections by their final classification (use state directly, not ref - avoids effect timing issues)
        const byClass = new Map<string, typeof approvedDetections>();
        for (const det of approvedDetections) {
          const className = detectionClassifications.get(det.id) || det.prediction.classifiedName || defaultName;
          console.log(`  🏷️ det ${det.id}: classMap=${detectionClassifications.get(det.id)}, classifiedName=${det.prediction.classifiedName}, final=${className}`);
          if (!byClass.has(className)) byClass.set(className, []);
          byClass.get(className)!.push(det);
        }

        console.log(`✅ Splitting ${approvedDetections.length} grouped receptacles into ${byClass.size} types:`, JSON.stringify(Array.from(byClass.keys())));

        Array.from(byClass.entries()).forEach(([className, dets]) => {
          const baseTimestamp = Date.now() + Math.random() * 1000;
          const compColor = colorByName.get(className) || componentData.componentColor;
          const newAnnotations = dets.map((det: any, idx: number) => {
            // Use relative coordinates directly from the prediction if available.
            // This prevents scaling corruption if the canvas size changed between detection and saving.
            const relStartX = det.prediction.relX !== undefined ? (det.prediction.relX - (det.prediction.relWidth || 0) / 2) : (det.prediction.x - (det.prediction.width || 40) / 2) / canvasWidth;
            const relStartY = det.prediction.relY !== undefined ? (det.prediction.relY - (det.prediction.relHeight || 0) / 2) : (det.prediction.y - (det.prediction.height || 40) / 2) / canvasHeight;
            const relWidth = det.prediction.relWidth !== undefined ? det.prediction.relWidth : (det.prediction.width || 40) / canvasWidth;
            const relHeight = det.prediction.relHeight !== undefined ? det.prediction.relHeight : (det.prediction.height || 40) / canvasHeight;

            const boxWidth = relWidth * canvasWidth;
            const boxHeight = relHeight * canvasHeight;
            const startX = relStartX * canvasWidth;
            const startY = relStartY * canvasHeight;

            return {
              id: `auto-${baseTimestamp}-${idx}-${Math.random().toString(36).substring(2, 9)}`,
              type: 'rectangle' as const,
              component_type: className,
              page: det.prediction.page || pageNumber,
              number: idx + 1,
              startX, startY,
              width: boxWidth, height: boxHeight,
              color: compColor,
              thickness: 2,
              confidence: det.prediction.confidence,
              auto_detected: true,
              timestamp: new Date().toISOString(),
              relativeStartX: relStartX,
              relativeStartY: relStartY,
              relativeWidth: relWidth,
              relativeHeight: relHeight,
              savedCanvasWidth: canvasWidth,
              savedCanvasHeight: canvasHeight
            };
          });

          allNewAnnotations.push(...newAnnotations);
          const sp = createSavePromise(className, newAnnotations);
          if (sp) savePromises.push(sp);
          componentsToEstimate.push({ name: className, count: dets.length });
          console.log(`  📦 ${className}: ${dets.length} detections`);
        });

        // Ensure every known receptacle type has an entry in pendingSavesByComponent,
        // even if all its detections were reclassified away. This forces flushSaves to
        // overwrite (and clear) any stale auto-detected annotations left from a prior run.
        for (const rc of receptacleGroupComponents) {
          if (!pendingSavesByComponent.has(rc.name)) {
            pendingSavesByComponent.set(rc.name, []);
            console.log(`  🧹 ${rc.name}: cleared stale annotations (no detections this run)`);
          }
        }

        continue;
      }

      // Grouped tele/data: split by user-editable classification (defaults to top type)
      if (componentData.componentName === '__TELE_DATA_GROUP__' && teleDataGroupComponents.length > 0) {
        const defaultName = teleDataGroupComponents[0].name;
        const colorByName = new Map(teleDataGroupComponents.map(tc => [tc.name, tc.color]));

        const byClass = new Map<string, typeof approvedDetections>();
        for (const det of approvedDetections) {
          const className = detectionClassifications.get(det.id) || det.prediction.classifiedName || defaultName;
          if (!byClass.has(className)) byClass.set(className, []);
          byClass.get(className)!.push(det);
        }

        console.log(`✅ Splitting ${approvedDetections.length} grouped tele/data into ${byClass.size} types`);

        Array.from(byClass.entries()).forEach(([className, dets]) => {
          const baseTimestamp = Date.now() + Math.random() * 1000;
          const compColor = colorByName.get(className) || componentData.componentColor;
          const newAnnotations = dets.map((det: any, idx: number) => {
            // Use relative coordinates directly from the prediction if available.
            // This prevents scaling corruption if the canvas size changed between detection and saving.
            const relStartX = det.prediction.relX !== undefined ? (det.prediction.relX - (det.prediction.relWidth || 0) / 2) : (det.prediction.x - (det.prediction.width || 40) / 2) / canvasWidth;
            const relStartY = det.prediction.relY !== undefined ? (det.prediction.relY - (det.prediction.relHeight || 0) / 2) : (det.prediction.y - (det.prediction.height || 40) / 2) / canvasHeight;
            const relWidth = det.prediction.relWidth !== undefined ? det.prediction.relWidth : (det.prediction.width || 40) / canvasWidth;
            const relHeight = det.prediction.relHeight !== undefined ? det.prediction.relHeight : (det.prediction.height || 40) / canvasHeight;

            const boxWidth = relWidth * canvasWidth;
            const boxHeight = relHeight * canvasHeight;
            const startX = relStartX * canvasWidth;
            const startY = relStartY * canvasHeight;

            return {
              id: `auto-${baseTimestamp}-${idx}-${Math.random().toString(36).substring(2, 9)}`,
              type: 'rectangle' as const,
              component_type: className,
              page: det.prediction.page || pageNumber,
              number: idx + 1,
              startX, startY,
              width: boxWidth, height: boxHeight,
              color: compColor,
              thickness: 2,
              confidence: det.prediction.confidence,
              auto_detected: true,
              timestamp: new Date().toISOString(),
              relativeStartX: relStartX,
              relativeStartY: relStartY,
              relativeWidth: relWidth,
              relativeHeight: relHeight,
              savedCanvasWidth: canvasWidth,
              savedCanvasHeight: canvasHeight
            };
          });

          allNewAnnotations.push(...newAnnotations);
          const sp = createSavePromise(className, newAnnotations);
          if (sp) savePromises.push(sp);
          componentsToEstimate.push({ name: className, count: dets.length });
          console.log(`  📦 ${className}: ${dets.length} detections`);
        });

        continue;
      }

      // Grouped lighting: split by fixture code classification
      if (componentData.componentName === '__LIGHTING_GROUP__' && lightingGroupComponents.length > 0) {
        const colorBySymbol = new Map(lightingGroupComponents.map(lc => [lc.symbol.toUpperCase(), lc.color]));
        const nameBySymbol = new Map(lightingGroupComponents.map(lc => [lc.symbol.toUpperCase(), lc.name]));

        const byCode = new Map<string, typeof approvedDetections>();
        for (const det of approvedDetections) {
          const code = detectionClassifications.get(det.id) || det.prediction.classifiedName || '';
          if (!byCode.has(code)) byCode.set(code, []);
          byCode.get(code)!.push(det);
        }

        console.log(`✅ Splitting ${approvedDetections.length} grouped lighting into ${byCode.size} types`);

        Array.from(byCode.entries()).forEach(([code, dets]) => {
          const baseTimestamp = Date.now() + Math.random() * 1000;
          const compName = nameBySymbol.get(code) || code;
          const compColor = colorBySymbol.get(code) || componentData.componentColor;
          // Use symbol code as component_type to match manual placement (which uses symbol for lighting)
          const componentType = code;
          const newAnnotations = dets.map((det: any, idx: number) => {
            // Use relative coordinates directly from the prediction if available.
            const relStartX = det.prediction.relX !== undefined ? (det.prediction.relX - (det.prediction.relWidth || 0) / 2) : (det.prediction.x - (det.prediction.width || 40) / 2) / canvasWidth;
            const relStartY = det.prediction.relY !== undefined ? (det.prediction.relY - (det.prediction.relHeight || 0) / 2) : (det.prediction.y - (det.prediction.height || 40) / 2) / canvasHeight;
            const relWidth = det.prediction.relWidth !== undefined ? det.prediction.relWidth : (det.prediction.width || 40) / canvasWidth;
            const relHeight = det.prediction.relHeight !== undefined ? det.prediction.relHeight : (det.prediction.height || 40) / canvasHeight;

            const boxWidth = relWidth * canvasWidth;
            const boxHeight = relHeight * canvasHeight;
            const startX = relStartX * canvasWidth;
            const startY = relStartY * canvasHeight;

            return {
              id: `auto-${baseTimestamp}-${idx}-${Math.random().toString(36).substring(2, 9)}`,
              type: 'rectangle' as const,
              component_type: componentType,
              page: det.prediction.page || pageNumber,
              number: idx + 1,
              startX, startY,
              width: boxWidth, height: boxHeight,
              color: compColor,
              thickness: 2,
              confidence: det.prediction.confidence,
              auto_detected: true,
              timestamp: new Date().toISOString(),
              relativeStartX: relStartX,
              relativeStartY: relStartY,
              relativeWidth: relWidth,
              relativeHeight: relHeight,
              savedCanvasWidth: canvasWidth,
              savedCanvasHeight: canvasHeight
            };
          });

          allNewAnnotations.push(...newAnnotations);
          createSavePromise(componentType, newAnnotations);
          componentsToEstimate.push({ name: componentType, count: dets.length });
          console.log(`  💡 ${code} (${compName}): ${dets.length} detections`);
        });

        continue;
      }
      
      console.log(`⚠️ GENERIC PATH for "${componentData.componentName}" (${approvedDetections.length} detections) — NOT receptacles/tele/lighting group`);
      
      // Safety net: if any detections have been reclassified, split by classification
      const hasReclassifications = approvedDetections.some(det => detectionClassifications.has(det.id));
      console.log(`  🔀 hasReclassifications: ${hasReclassifications}`);
      if (hasReclassifications) {
        const byClass = new Map<string, typeof approvedDetections>();
        for (const det of approvedDetections) {
          const className = detectionClassifications.get(det.id) || det.prediction.classifiedName || componentData.componentName;
          if (!byClass.has(className)) byClass.set(className, []);
          byClass.get(className)!.push(det);
        }
        console.log(`🔀 Splitting ${approvedDetections.length} ${componentData.componentName} into ${byClass.size} types (fallback classification)`);
        Array.from(byClass.entries()).forEach(([className, dets]) => {
          const baseTimestamp = Date.now() + Math.random() * 1000;
          const newAnnotations = dets.map((det: any, idx: number) => {
            // Use relative coordinates directly from the prediction if available.
            const relStartX = det.prediction.relX !== undefined ? (det.prediction.relX - (det.prediction.relWidth || 0) / 2) : (det.prediction.x - (det.prediction.width || 40) / 2) / canvasWidth;
            const relStartY = det.prediction.relY !== undefined ? (det.prediction.relY - (det.prediction.relHeight || 0) / 2) : (det.prediction.y - (det.prediction.height || 40) / 2) / canvasHeight;
            const relWidth = det.prediction.relWidth !== undefined ? det.prediction.relWidth : (det.prediction.width || 40) / canvasWidth;
            const relHeight = det.prediction.relHeight !== undefined ? det.prediction.relHeight : (det.prediction.height || 40) / canvasHeight;

            const boxWidth = relWidth * canvasWidth;
            const boxHeight = relHeight * canvasHeight;
            const startX = relStartX * canvasWidth;
            const startY = relStartY * canvasHeight;

            return {
              id: `auto-${baseTimestamp}-${idx}-${Math.random().toString(36).substring(2, 9)}`,
              type: 'rectangle' as const,
              component_type: className,
              page: det.prediction.page || pageNumber,
              number: idx + 1,
              startX, startY,
              width: boxWidth, height: boxHeight,
              color: componentData.componentColor,
              thickness: 2,
              confidence: det.prediction.confidence,
              auto_detected: true,
              timestamp: new Date().toISOString(),
              relativeStartX: relStartX,
              relativeStartY: relStartY,
              relativeWidth: relWidth,
              relativeHeight: relHeight,
              savedCanvasWidth: canvasWidth,
              savedCanvasHeight: canvasHeight
            };
          });
          allNewAnnotations.push(...newAnnotations);
          const sp = createSavePromise(className, newAnnotations);
          if (sp) savePromises.push(sp);
          componentsToEstimate.push({ name: className, count: dets.length });
          console.log(`  📦 ${className}: ${dets.length} detections (fallback)`);
        });
      } else {
        const baseTimestamp = Date.now() + Math.random() * 1000;
        const newAnnotations = approvedDetections.map((det, idx: number) => {
          // Use relative coordinates directly from the prediction if available.
          const relStartX = det.prediction.relX !== undefined ? (det.prediction.relX - (det.prediction.relWidth || 0) / 2) : (det.prediction.x - (det.prediction.width || 40) / 2) / canvasWidth;
          const relStartY = det.prediction.relY !== undefined ? (det.prediction.relY - (det.prediction.relHeight || 0) / 2) : (det.prediction.y - (det.prediction.height || 40) / 2) / canvasHeight;
          const relWidth = det.prediction.relWidth !== undefined ? det.prediction.relWidth : (det.prediction.width || 40) / canvasWidth;
          const relHeight = det.prediction.relHeight !== undefined ? det.prediction.relHeight : (det.prediction.height || 40) / canvasHeight;

          const boxWidth = relWidth * canvasWidth;
          const boxHeight = relHeight * canvasHeight;
          const startX = relStartX * canvasWidth;
          const startY = relStartY * canvasHeight;
          
          return {
            id: `auto-${baseTimestamp}-${idx}-${Math.random().toString(36).substring(2, 9)}`,
            type: 'rectangle' as const,
            component_type: componentData.componentName,
            page: det.prediction.page || pageNumber,
            number: idx + 1,
            startX, startY,
            width: boxWidth, height: boxHeight,
            color: componentData.componentColor,
            thickness: 2,
            confidence: det.prediction.confidence,
            auto_detected: true,
            timestamp: new Date().toISOString(),
            relativeStartX: relStartX,
            relativeStartY: relStartY,
            relativeWidth: relWidth,
            relativeHeight: relHeight,
            savedCanvasWidth: canvasWidth,
            savedCanvasHeight: canvasHeight
          };
        });
        
        allNewAnnotations.push(...newAnnotations);
        
        const sp = createSavePromise(componentData.componentName, newAnnotations);
        if (sp) savePromises.push(sp);
        
        componentsToEstimate.push({
          name: componentData.componentName,
          count: approvedDetections.length
        });
      }
      
      // Save reference image to key (also async, non-blocking)
      if (componentData.referenceImage && estimationId) {
        const refSavePromise = (async () => {
          try {
            const { data: estimation } = await supabase
              .from('estimations')
              .select('project_key')
              .eq('id', estimationId)
              .single();
            
            if (estimation?.project_key) {
              const key = estimation.project_key;
              const component = key.components?.find((c: any) => 
                c.name.toLowerCase() === componentData.componentName.toLowerCase()
              );
              
              if (component && !component.referenceImage) {
                component.referenceImage = componentData.referenceImage;
                
                await supabase
                  .from('estimations')
                  .update({ project_key: key })
                  .eq('id', estimationId);
              }
            }
          } catch (error) {
            console.error(`Failed to save reference image for ${componentData.componentName}:`, error);
          }
        })();
        
        savePromises.push(refSavePromise);
      }
    }
    
    // Update annotations in UI and persist through explicit commit path.
    if (allNewAnnotations.length > 0) {
      const { next } = await commitCreateAnnotations(
        allNewAnnotations as Annotation[],
        buildCommitContext()
      );
      void refreshPageAnnotationCountsFromDb();
      updatedAnnotationsForSave = next;
      console.log(`✅ Added ${allNewAnnotations.length} total annotations from ${componentsToEstimate.length} components`);
    }

    // Save reference-image side effects, then refresh key.
    if (savePromises.length > 0 || updatedAnnotationsForSave) {
      await Promise.all(savePromises);
      console.log('✅ Side effects completed after multi-component finish');

      // Refetch the key from database and notify parent to refresh
      if (estimationId && onKeyExtracted) {
        try {
          const { data } = await supabase
            .from('estimations')
            .select('project_key')
            .eq('id', estimationId)
            .single();

          if (data?.project_key) {
            console.log('🔄 Refreshing key with', data.project_key.components?.length, 'components');
            onKeyExtracted(data.project_key);
          }
        } catch (error) {
          console.error('Failed to refresh key:', error);
        }
      }
    }

    // Record usage after successful save + key side effects.
    if (finishUsageUnits > 0 && updatedAnnotationsForSave) {
      await reportUsageForFinishReview(finishUsageUnits);
    }

    // Kick off estimate work after successful save/billing.
    if (componentsToEstimate.length > 0 && estimationId) {
      // runConcurrentLaborEstimation(componentsToEstimate);
    }

    // Navigate to the first page that received new annotations in this finish run.
    // Uses only newly created annotations, so it can't drift from prior session state.
    const firstAnnotatedPage = allNewAnnotations
      .map((annotation: any) => Number(annotation?.page))
      .filter((p: number) => Number.isFinite(p) && p > 0)
      .reduce((min: number, p: number) => Math.min(min, p), Number.POSITIVE_INFINITY);

    // Now that all linked finish work succeeded, close modal and cleanup.
    setIsParallelCountSession(false);
    setShowMultiComponentReviewModal(false);
    setDatasetExportError(null);
    if (Number.isFinite(firstAnnotatedPage) && firstAnnotatedPage !== pageNumber) {
      setPageNumber(firstAnnotatedPage);
    }

    setHighlightedComponentType(null);
    setMultiComponentDetections([]);
    setAllComponentSelections(new Map());
    setCurrentReviewComponentIndex(0);
    setComponentPagesByName(new Map());
    setCountAllPageThumbnails(new Map());
    countAllPageThumbnailsRef.current = new Map();
    countAllThumbnailRenderInFlightRef.current.clear();
    setPendingKeyComponents([]);
    setReceptacleGroupComponents([]);
    setLightingGroupComponents([]);
    setDetectionClassifications(new Map());
    setBulkSelectedDetections(new Set());
    setBulkReclassTarget('');
    countTargetNamesRef.current = new Set();
  } catch (error: any) {
    console.error('❌ Finalize review failed:', error);
    setReviewFinalizeError(error?.message || 'Save or billing failed. Please try again.');
  } finally {
    setIsFinalizingReview(false);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiComponentDetections, allComponentSelections, pageNumber, annotations, supabase, estimationId, file, canvasRef, onAnnotationSaved, onKeyExtracted, receptacleGroupComponents, teleDataGroupComponents, lightingGroupComponents, activeMode, currentDrawingFileId, detectionClassifications, isFinalizingReview, reportUsageForFinishReview]);

  // Auto-dismiss estimate indicator after all items finish
  useEffect(() => {
    if (!showLaborEstimationModal || laborEstimationProgress.length === 0) return;
    const allDone = laborEstimationProgress.every(i => i.status === 'complete' || i.status === 'error');
    if (!allDone) return;
    const timer = setTimeout(() => {
      setShowLaborEstimationModal(false);
      setShowEstimatePopover(false);
    }, 4000);
    return () => clearTimeout(timer);
  }, [showLaborEstimationModal, laborEstimationProgress]);

  // Handle final confirmation after review
  const handleFinalAutocountConfirm = useCallback(async () => {
    if (!pendingAutocountComponent) return;
    
    console.log('💾 Saving reviewed autocount detections (multi-page)');
    
    // Filter to only keep selected detections
    const approvedDetections = autocountDetections.filter(det => 
      selectedDetectionsToKeep.has(det.id)
    );
    
    console.log(`✅ User approved ${approvedDetections.length} of ${autocountDetections.length} detections`);
    
    // Group detections by page for proper canvas dimensions
    const detectionsByPage = approvedDetections.reduce((acc, det) => {
      const page = det.prediction.page || pageNumber; // Fallback to current page if not set
      if (!acc[page]) acc[page] = [];
      acc[page].push(det);
      return acc;
    }, {} as Record<number, typeof approvedDetections>);
    
    console.log(`📄 Detections spread across ${Object.keys(detectionsByPage).length} pages`);
    
    // Get canvas dimensions (use standard dimensions for multi-page)
    const annotationCanvas = canvasRef.current;
    const pdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
    const canvasWidth = pdfCanvas?.width || annotationCanvas?.width || 8000;
    const canvasHeight = pdfCanvas?.height || annotationCanvas?.height || 5714;
    
    // Convert approved detections to annotations
    // CRITICAL: Preserve page numbers from predictions!
    // Convert to rectangles with bounding box data so they can be resized
    const baseTimestamp = Date.now();
    const newAnnotations = approvedDetections.map((det, idx: number) => {
      // Use relative coordinates directly from the prediction if available.
      // This prevents scaling corruption if the canvas size changed between detection and saving.
      const relStartX = det.prediction.relX !== undefined ? (det.prediction.relX - (det.prediction.relWidth || 0) / 2) : (det.prediction.x - (det.prediction.width || 40) / 2) / canvasWidth;
      const relStartY = det.prediction.relY !== undefined ? (det.prediction.relY - (det.prediction.relHeight || 0) / 2) : (det.prediction.y - (det.prediction.height || 40) / 2) / canvasHeight;
      const relWidth = det.prediction.relWidth !== undefined ? det.prediction.relWidth : (det.prediction.width || 40) / canvasWidth;
      const relHeight = det.prediction.relHeight !== undefined ? det.prediction.relHeight : (det.prediction.height || 40) / canvasHeight;

      const boxWidth = relWidth * canvasWidth;
      const boxHeight = relHeight * canvasHeight;
      const startX = relStartX * canvasWidth;
      const startY = relStartY * canvasHeight;
      
      return {
        id: `auto-${baseTimestamp}-${idx}-${Math.random().toString(36).substring(2, 9)}`,
        type: 'rectangle' as const,
        component_type: pendingAutocountComponent.symbolType === 'Lighting' 
          ? (pendingAutocountComponent.symbol || pendingAutocountComponent.name)
          : pendingAutocountComponent.name,
        page: det.prediction.page || pageNumber, // Use page from prediction!
        number: idx + 1,
        startX: startX,
        startY: startY,
        width: boxWidth,
        height: boxHeight,
        color: pendingAutocountComponent.color,
        thickness: 2,
        confidence: det.prediction.confidence,
        auto_detected: true,
        timestamp: new Date().toISOString(),
        relativeStartX: relStartX,
        relativeStartY: relStartY,
        relativeWidth: relWidth,
        relativeHeight: relHeight,
        savedCanvasWidth: canvasWidth,
        savedCanvasHeight: canvasHeight
      };
    });
    
    console.log('📊 Annotations by page:', 
      Object.entries(detectionsByPage).map(([page, dets]) => 
        `Page ${page}: ${dets.length} annotations`
      ).join(', ')
    );
    
    // Persist via explicit commit path (single write trigger).
    await commitCreateAnnotations(
      newAnnotations as Annotation[],
      buildCommitContext()
    );
    void refreshPageAnnotationCountsFromDb();
    
    // NOW save reference image to component in key (after annotations are saved)
    console.log('🔑 Attempting to save reference to key:', { 
      hasReferenceImage: !!referenceAreaImage, 
      hasEstimationId: !!estimationId,
      componentName: pendingAutocountComponent.name 
    });
    
    if (referenceAreaImage && estimationId) {
      try {
        console.log('🔑 Fetching current key from database...');
        // Get current key from database
        const { data: estimation, error: fetchError } = await supabase
          .from('estimations')
          .select('project_key')
          .eq('id', estimationId)
          .single();
        
        if (fetchError) {
          console.error('❌ Error fetching estimation:', fetchError);
          return;
        }
        
        console.log('🔑 Current key:', estimation?.project_key);
        
        if (estimation) {
          // Initialize key if it doesn't exist
          const currentKey = estimation.project_key || { components: [], extractedAt: new Date().toISOString() };
          
          // Check if component already exists - case-insensitive match
          const componentIndex = currentKey.components.findIndex(
            (c: any) => c.name.toLowerCase() === pendingAutocountComponent.name.toLowerCase()
          );
          
          const isNewComponent = componentIndex < 0;
          
          if (componentIndex >= 0) {
            // Update existing component with reference image
            currentKey.components[componentIndex].referenceImage = referenceAreaImage;
            console.log(`✅ Updating existing component "${pendingAutocountComponent.name}" with reference image at index`, componentIndex);
          } else {
            // Add new component with reference image - deduplicate name
            const existingNames = currentKey.components.map((c: any) => c.name);
            const uniqueName = getUniqueComponentName(pendingAutocountComponent.name, existingNames);
            currentKey.components.push({
              name: uniqueName,
              color: pendingAutocountComponent.color,
              symbol: pendingAutocountComponent.name,
              referenceImage: referenceAreaImage,
              pages: [],
              totalCount: 0
            });
            console.log(`✅ Adding new component "${pendingAutocountComponent.name}" with reference image. Total components:`, currentKey.components.length);
          }
          
          // Save updated key to database
          console.log('🔑 Saving updated key to database...');
          const { error: updateError } = await supabase
            .from('estimations')
            .update({ project_key: currentKey })
            .eq('id', estimationId);
          
          // NEW: Sync to rolling key library
          if (user?.id && !updateError) {
            if (isNewComponent) {
              // New component - sync everything including reference image
              await componentLibraryService.syncComponentToLibrary(user.id, {
                user_id: user.id,
                component_name: pendingAutocountComponent.name, // Use exact name
                symbol: pendingAutocountComponent.name,
                color: pendingAutocountComponent.color,
                reference_image: referenceAreaImage
              });
              console.log(`✅ Synced new component "${pendingAutocountComponent.name}" with reference image to library`);
            } else {
              // Existing component - just update reference image
              await componentLibraryService.syncReferenceImage(
                user.id,
                pendingAutocountComponent.name, // Use exact name
                referenceAreaImage
              );
              console.log(`✅ Synced reference image for "${pendingAutocountComponent.name}" to library`);
            }
          }
          
          if (updateError) {
            console.error('❌ Error saving key with reference image:', updateError);
          } else {
            console.log('✅ Reference image saved to database successfully');
          }
        } else {
          console.warn('⚠️ No estimation found');
        }
      } catch (error) {
        console.error('❌ Failed to save reference image:', error);
      }
    } else {
      console.warn('⚠️ Skipping key save - missing referenceImage or estimationId');
    }
    
    // Reset to NONE so post-review selection behavior matches normal filter rules
    setHighlightedComponentType(null);
    setShowAutocountReviewModal(false);
    setAutocountDetections([]);
    setSelectedDetectionsToKeep(new Set());
    setAutocountTotalBatches(0);
    setAutocountCompletedBatches(0);
    
    // Auto-estimate component and add to estimate table (use modal instead of chat)
    if (approvedDetections.length > 0 && estimationId) {
      // Store component info before cleanup
      const componentToEstimate = {
        name: pendingAutocountComponent.name,
        count: approvedDetections.length
      };
      
      // Clean up before async work
      setPendingAutocountComponent(null);
      setReferenceAreaImage(null);
      setAutocountSelection(null);
      
      // Show labor estimation modal immediately and run estimation
      // runConcurrentLaborEstimation([componentToEstimate]);
    } else {
      // No auto-estimation needed, just clean up
      setPendingAutocountComponent(null);
      setReferenceAreaImage(null);
      setAutocountSelection(null);
    }
    
    console.log('✅ Autocount complete! Added to canvas. Counts should refresh.');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autocountDetections, selectedDetectionsToKeep, pendingAutocountComponent, pageNumber, annotations, referenceAreaImage, estimationId, onAnnotationSaved]);

  const runVectorizationCheckForCurrentPage = useCallback(async () => {
    const cached = vectorizationCacheRef.current[pageNumber];
    if (cached) {
      setVectorizationStatus(cached.status);
      setVectorizationMessage(cached.message);
      return;
    }

    const page = pdfPageRef.current;
    if (!page) {
      setVectorizationStatus('unknown');
      setVectorizationMessage('PDF page is not ready yet. Try again in a moment.');
      return;
    }

    setVectorizationStatus('checking');
    setVectorizationMessage('Inspecting PDF operators...');

    try {
      const operatorList = await page.getOperatorList();
      const ops = operatorList?.fnArray || [];

      let vectorOps = 0;
      let imageOps = 0;
      for (const op of ops) {
        if (
          op === OPS.constructPath ||
          op === OPS.stroke ||
          op === OPS.closePath ||
          op === OPS.fill ||
          op === OPS.eoFill ||
          op === OPS.fillStroke ||
          op === OPS.eoFillStroke ||
          op === OPS.rectangle
        ) {
          vectorOps += 1;
        }
        if (
          op === OPS.paintImageXObject ||
          op === OPS.paintInlineImageXObject ||
          op === OPS.paintInlineImageXObjectGroup
        ) {
          imageOps += 1;
        }
      }

      const textContent = await page.getTextContent();
      const textOps = Array.isArray(textContent?.items) ? textContent.items.length : 0;
      const looksVectorized =
        vectorOps >= 40 ||
        (vectorOps >= 15 && textOps >= 10) ||
        (vectorOps >= 12 && vectorOps > imageOps * 1.5);

      const status: VectorizationStatus = looksVectorized ? 'vector' : 'raster';
      const message = looksVectorized
        ? `Detected vector geometry (${vectorOps} vector ops, ${imageOps} image ops).`
        : `This sheet looks rasterized (${imageOps} image ops, ${vectorOps} vector ops). Use self calibration instead.`;

      vectorizationCacheRef.current[pageNumber] = { status, message };
      setVectorizationStatus(status);
      setVectorizationMessage(message);
    } catch (error) {
      console.error('Error while checking vectorization:', error);
      setVectorizationStatus('unknown');
      setVectorizationMessage('Vector check failed. You can still use self calibration.');
    }
  }, [pageNumber]);

  const startManualCalibration = useCallback(() => {
    console.log('🔧 Starting manual calibration mode');
    setShowCalibrationDropdown(false);
    setShowCalibrationModal(false);
    setCalibrationLine(null);
    setIsCalibrating(true);
  }, []);

  const applyPresetCalibration = useCallback(async (presetId: string) => {
    const preset = CALIBRATION_PRESET_MAP[presetId];
    if (!preset) return;
    if (vectorizationStatus !== 'vector') return;

    const page = pdfPageRef.current;
    if (!page) {
      setVectorizationStatus('unknown');
      setVectorizationMessage('PDF page is not ready yet. Try again in a moment.');
      return;
    }

    const fallbackRotation = page.rotate || 0;
    const rotation = pageRotations[pageNumber] ?? fallbackRotation;
    const viewport = page.getViewport({ scale: 1, rotation });
    const pageWidthPt = viewport.width;
    const pageHeightPt = viewport.height;
    const realUnitsPerPdfPoint = preset.realUnitsPerDrawingInch / 72;

    await saveCalibrationToSupabase(pageNumber, {
      mode: 'preset',
      presetKey: preset.id,
      presetLabel: preset.label,
      unit: preset.unit,
      realUnitsPerDrawingInch: preset.realUnitsPerDrawingInch,
      realUnitsPerPdfPoint,
      pageWidthPt,
      pageHeightPt,
      rotationAtCalibration: rotation,
      createdAt: new Date().toISOString(),
      showReferenceLine: false,
      // Keep legacy fields populated for older UI surfaces and backwards compatibility.
      canvasPercentage: 100,
      realWorldValue: parseFloat((pageWidthPt * realUnitsPerPdfPoint).toFixed(6)),
    });

    setShowCalibrationDropdown(false);
    setIsCalibrating(false);
    setCalibrationLine(null);
  }, [pageNumber, pageRotations, saveCalibrationToSupabase, vectorizationStatus]);

  useEffect(() => {
    if (!showCalibrationDropdown) return;
    void runVectorizationCheckForCurrentPage();
  }, [showCalibrationDropdown, pageNumber, runVectorizationCheckForCurrentPage]);

  // Shared calibration entrypoint used by toolbar + sidebar warning CTA.
  const startScaleCalibration = useCallback(() => {
    console.log('🔧 Opening calibration setup modal');
    setShowToolDropdown({});
    setShowCalibrationDropdown(true);
    setShowCalibrationModal(false);
    setIsCalibrating(false);
    void runVectorizationCheckForCurrentPage();
  }, [runVectorizationCheckForCurrentPage]);

  // Helper function to calculate distance from point to line segment
  const pointToLineDistance = (px: number, py: number, x1: number, y1: number, x2: number, y2: number): number => {
    const A = px - x1;
    const B = py - y1;
    const C = x2 - x1;
    const D = y2 - y1;
    
    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = -1;
    
    if (lenSq !== 0) {
      param = dot / lenSq;
    }
    
    let xx, yy;
    
    if (param < 0) {
      xx = x1;
      yy = y1;
    } else if (param > 1) {
      xx = x2;
      yy = y2;
    } else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }
    
    const dx = px - xx;
    const dy = py - yy;
    
    return Math.sqrt(dx * dx + dy * dy);
  };

  const constrainTo90Degrees = (
    anchor: { x: number; y: number },
    target: { x: number; y: number }
  ): { x: number; y: number } => {
    const dx = target.x - anchor.x;
    const dy = target.y - anchor.y;
    if (Math.abs(dx) >= Math.abs(dy)) {
      return { x: target.x, y: anchor.y };
    }
    return { x: anchor.x, y: target.y };
  };

  const findWireRunVertexIndexAtPoint = (annotation: Annotation, x: number, y: number, tolerance = 14): number | null => {
    if (annotation.type !== 'wire-run' || !annotation.points || annotation.points.length < 2) return null;
    let closestIndex: number | null = null;
    let closestDistance = tolerance;
    for (let i = 0; i < annotation.points.length; i++) {
      const point = annotation.points[i];
      const distance = Math.hypot(x - point.x, y - point.y);
      if (distance <= closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    }
    return closestIndex;
  };

  const findWireRunSegmentAtPoint = (
    annotation: Annotation,
    x: number,
    y: number,
    tolerance = 12
  ): { segmentIndex: number; insertPoint: { x: number; y: number }; t: number } | null => {
    if (annotation.type !== 'wire-run' || !annotation.points || annotation.points.length < 2) return null;
    let bestHit: { segmentIndex: number; insertPoint: { x: number; y: number }; t: number } | null = null;
    let bestDistance = tolerance;

    for (let i = 0; i < annotation.points.length - 1; i++) {
      const p1 = annotation.points[i];
      const p2 = annotation.points[i + 1];
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const lengthSquared = dx * dx + dy * dy;
      if (lengthSquared <= 0) continue;

      const rawT = ((x - p1.x) * dx + (y - p1.y) * dy) / lengthSquared;
      const t = Math.max(0, Math.min(1, rawT));
      const nearestX = p1.x + t * dx;
      const nearestY = p1.y + t * dy;
      const distance = Math.hypot(x - nearestX, y - nearestY);

      if (distance <= bestDistance) {
        bestDistance = distance;
        bestHit = {
          segmentIndex: i,
          insertPoint: { x: nearestX, y: nearestY },
          t
        };
      }
    }

    return bestHit;
  };

  const findWireRunEndpointAtPoint = (annotation: Annotation, x: number, y: number, tolerance = 14): 'start' | 'end' | null => {
    if (annotation.type !== 'wire-run' || !annotation.points || annotation.points.length < 2) return null;
    const start = annotation.points[0];
    const end = annotation.points[annotation.points.length - 1];
    if (Math.hypot(x - start.x, y - start.y) <= tolerance) return 'start';
    if (Math.hypot(x - end.x, y - end.y) <= tolerance) return 'end';
    return null;
  };

  const applyWireRunPointMutation = (
    annotationId: string,
    pointMutator: (points: { x: number; y: number }[]) => { x: number; y: number }[] | null
  ): boolean => {
    const canvas = canvasRef.current;
    const cw = canvas?.width || 16000;
    const ch = canvas?.height || 10666;
    const currentAnnotations = annotationsRef.current;
    let didUpdate = false;

    const nextAnnotations = currentAnnotations.map(annotation => {
      if (annotation.id !== annotationId || annotation.type !== 'wire-run' || !annotation.points || annotation.points.length < 2) {
        return annotation;
      }

      const basePoints = annotation.points.map(point => ({ x: point.x, y: point.y }));
      const nextPoints = pointMutator(basePoints);
      if (!nextPoints || nextPoints.length < 2) return annotation;

      didUpdate = true;
      return normalizeWireRunGeometry(annotation, nextPoints, cw, ch);
    });

    if (!didUpdate) return false;

    annotationsRef.current = nextAnnotations;
    setAnnotations(nextAnnotations);
    addToHistory(nextAnnotations);
    void persistAnnotations(nextAnnotations, [annotationId]);
    void syncWireRunsToDatabase([annotationId], nextAnnotations);
    return true;
  };

  const openWireRunEditContextMenuAtPoint = (
    clientX: number,
    clientY: number,
    canvasX: number,
    canvasY: number
  ): boolean => {
    const canvas = canvasRef.current;
    const cw = canvas?.width || 16000;
    const ch = canvas?.height || 10666;

    const wireRuns = annotations
      .filter(a => a.page === pageNumber && a.type === 'wire-run')
      .map(a => rescaleAnnotation(a, cw, ch))
      .sort((a, b) => {
        const aSelected = a.id === selectedAnnotation || selectedAnnotations.has(a.id);
        const bSelected = b.id === selectedAnnotation || selectedAnnotations.has(b.id);
        if (aSelected === bSelected) return 0;
        return aSelected ? -1 : 1;
      });

    for (const wireRun of wireRuns) {
      if (!wireRun.points || wireRun.points.length < 2) continue;

      const vertexIndex = findWireRunVertexIndexAtPoint(wireRun, canvasX, canvasY, 16);
      const segmentHit = findWireRunSegmentAtPoint(wireRun, canvasX, canvasY, 16);
      const endpointHit = findWireRunEndpointAtPoint(wireRun, canvasX, canvasY, 16);
      const hitWireRun = vertexIndex !== null || segmentHit !== null || endpointHit !== null;
      if (!hitWireRun) continue;

      let removableVertexIndex: number | null = null;
      if (wireRun.points.length > 2) {
        if (vertexIndex !== null && vertexIndex > 0 && vertexIndex < wireRun.points.length - 1) {
          removableVertexIndex = vertexIndex;
        } else if (segmentHit && !endpointHit) {
          // Delete the segment that was clicked (not the most recent segment):
          // remove the trailing interior vertex for that exact segment index.
          const trailingVertexIndex = segmentHit.segmentIndex + 1;
          removableVertexIndex = Math.max(1, Math.min(wireRun.points.length - 2, trailingVertexIndex));
        }
      }

      let extendEndpoint: 'start' | 'end' | null = endpointHit;
      if (!extendEndpoint && segmentHit) {
        const startPoint = wireRun.points[0];
        const endPoint = wireRun.points[wireRun.points.length - 1];
        const startDistance = Math.hypot(canvasX - startPoint.x, canvasY - startPoint.y);
        const endDistance = Math.hypot(canvasX - endPoint.x, canvasY - endPoint.y);
        extendEndpoint = startDistance <= endDistance ? 'start' : 'end';
      }

      setSelectedAnnotation(wireRun.id);
      setSelectedAnnotations(new Set([wireRun.id]));
      setSelectedHandDrawnStrokes(new Set());
      setCurrentTool('select');
      setWireRunEditContextMenu({
        x: clientX,
        y: clientY,
        annotationId: wireRun.id,
        wireTypeName: wireRun.wireTypeName,
        wireTypeColor: wireRun.wireTypeColor || wireRun.color,
        segmentHit,
        endpointHit,
        extendEndpoint,
        removableVertexIndex
      });
      return true;
    }

    return false;
  };

  const handleDeleteWireRunFromContextMenu = () => {
    if (!wireRunEditContextMenu) return;

    const currentAnnotations = annotationsRef.current;
    const deletedIds = new Set<string>([wireRunEditContextMenu.annotationId]);
    currentAnnotations.forEach((annotation) => {
      if (annotation.type !== 'piping') return;
      const startDeleted = annotation.startAnnotationId && deletedIds.has(annotation.startAnnotationId);
      const endDeleted = annotation.endAnnotationId && deletedIds.has(annotation.endAnnotationId);
      if (startDeleted || endDeleted) deletedIds.add(annotation.id);
    });

    const filtered = currentAnnotations.filter((annotation) => !deletedIds.has(annotation.id));
    const newAnnotations = renumberAnnotations(filtered);
    const deletedComponentTypes = Array.from(new Set(
      currentAnnotations
        .filter((annotation) => deletedIds.has(annotation.id))
        .map((annotation) => annotation.component_type)
        .filter(Boolean)
    )) as string[];
    annotationsRef.current = newAnnotations;
    setAnnotations(newAnnotations);
    addToHistory(newAnnotations);
    setSelectedAnnotation(null);
    setSelectedAnnotations(new Set());
    setWireRunEditContextMenu(null);
    void persistDeletedAnnotations(Array.from(deletedIds), deletedComponentTypes);
    void deletePersistedWireRunsForAnnotationIds(deletedIds);
  };

  const handleAddWireRunSegmentFromContextMenu = () => {
    if (!wireRunEditContextMenu) return;

    const { annotationId, extendEndpoint } = wireRunEditContextMenu;
    if (!extendEndpoint) return;

    // Make "Add New Segment" behave like normal drawing: choose endpoint, then click to extend.
    setWireRunExtendMode({ annotationId, endpoint: extendEndpoint });
    setWireRunExtendPreviewPoint(null);
    setSelectedAnnotation(annotationId);
    setSelectedAnnotations(new Set([annotationId]));
    setWireRunEditContextMenu(null);
  };

  const handleDeleteWireRunSegmentFromContextMenu = () => {
    if (!wireRunEditContextMenu || wireRunEditContextMenu.removableVertexIndex === null) return;

    const { annotationId, removableVertexIndex } = wireRunEditContextMenu;
    applyWireRunPointMutation(annotationId, points => {
      if (points.length <= 2) return null;
      if (removableVertexIndex <= 0 || removableVertexIndex >= points.length - 1) return null;
      return points.filter((_, index) => index !== removableVertexIndex);
    });
    setWireRunEditContextMenu(null);
  };

  // Check if point is inside annotation (handles both radius and width/height formats)
  const isPointInAnnotation = (annotation: Annotation, x: number, y: number, padding: number = 0): boolean => {
    if (annotation.type === 'hand-square-footage') {
      const sqMinX = Math.min(annotation.startX, annotation.endX || annotation.startX) - padding;
      const sqMaxX = Math.max(annotation.startX, annotation.endX || annotation.startX) + padding;
      const sqMinY = Math.min(annotation.startY, annotation.endY || annotation.startY) - padding;
      const sqMaxY = Math.max(annotation.startY, annotation.endY || annotation.startY) + padding;
      return x >= sqMinX && x <= sqMaxX && y >= sqMinY && y <= sqMaxY;
    }
    if (annotation.type === 'rectangle' || annotation.type === 'circle') {
      // AI-generated circles have radius, manual annotations have width/height
      if (annotation.radius) {
        // AI-generated circle - use radius and center point
        const centerX = annotation.x || annotation.startX;
        const centerY = annotation.y || annotation.startY;
        const distance = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
        return distance <= (annotation.radius + padding);
      } else {
        // Manual annotation - use width/height bounding box
        const minX = Math.min(annotation.startX, annotation.startX + (annotation.width || 0)) - padding;
        const maxX = Math.max(annotation.startX, annotation.startX + (annotation.width || 0)) + padding;
        const minY = Math.min(annotation.startY, annotation.startY + (annotation.height || 0)) - padding;
        const maxY = Math.max(annotation.startY, annotation.startY + (annotation.height || 0)) + padding;
        return x >= minX && x <= maxX && y >= minY && y <= maxY;
      }
    }
    return false;
  };

  // Helper function to find annotation at a point (returns center of annotation)
  // Uses interaction filter rules (single-component edit vs edit-all).
  const findAnnotationAtPoint = useCallback((x: number, y: number): { id: string; centerX: number; centerY: number; isWireRun?: boolean } | null => {
    const canvas = canvasRef.current;
    const cw = canvas?.width || 16000;
    const ch = canvas?.height || 10666;
    const pageAnns = annotations.filter(a => {
      if (a.page !== pageNumber) return false;
      if (a.type === 'wire-run') return true;
      if (!interactionHighlightedComponentType) return false;
      if (interactionIsHighlightAll) return true;
      if (a.type === 'piping') {
        const startAnn = annotations.find(ann => ann.id === a.startAnnotationId);
        const endAnn = annotations.find(ann => ann.id === a.endAnnotationId);
        return (startAnn?.component_type === interactionHighlightedComponentType) ||
               (endAnn?.component_type === interactionHighlightedComponentType);
      }
      return a.component_type === interactionHighlightedComponentType;
    }).map(a => rescaleAnnotation(a, cw, ch));
    
    for (const ann of pageAnns) {
      let centerX: number, centerY: number;
      
      // Handle wire runs - check if point is near any segment
      if (ann.type === 'wire-run' && ann.points && ann.points.length >= 2) {
        const hitDistance = 15; // pixels from line to register a hit
        
        for (let i = 0; i < ann.points.length - 1; i++) {
          const p1 = ann.points[i];
          const p2 = ann.points[i + 1];
          
          // Calculate distance from point to line segment
          const dx = p2.x - p1.x;
          const dy = p2.y - p1.y;
          const lengthSquared = dx * dx + dy * dy;
          
          let t = 0;
          if (lengthSquared > 0) {
            t = Math.max(0, Math.min(1, ((x - p1.x) * dx + (y - p1.y) * dy) / lengthSquared));
          }
          
          const nearestX = p1.x + t * dx;
          const nearestY = p1.y + t * dy;
          const distance = Math.sqrt((x - nearestX) ** 2 + (y - nearestY) ** 2);
          
          if (distance <= hitDistance) {
            // Find center of the wire run
            const midIndex = Math.floor(ann.points.length / 2);
            return { 
              id: ann.id, 
              centerX: ann.points[midIndex].x, 
              centerY: ann.points[midIndex].y,
              isWireRun: true
            };
          }
        }
        continue;
      }
      
      // Handle square footage rectangles - use startX/startY/endX/endY bounds
      if (ann.type === 'hand-square-footage') {
        const sqMinX = Math.min(ann.startX, ann.endX || ann.startX);
        const sqMaxX = Math.max(ann.startX, ann.endX || ann.startX);
        const sqMinY = Math.min(ann.startY, ann.endY || ann.startY);
        const sqMaxY = Math.max(ann.startY, ann.endY || ann.startY);
        const sqPad = 0;
        
        if (x >= sqMinX - sqPad && x <= sqMaxX + sqPad && y >= sqMinY - sqPad && y <= sqMaxY + sqPad) {
          return {
            id: ann.id,
            centerX: (sqMinX + sqMaxX) / 2,
            centerY: (sqMinY + sqMaxY) / 2,
          };
        }
        continue;
      }

      if (ann.type === 'circle') {
        if (ann.x !== undefined && ann.y !== undefined) {
          centerX = ann.x;
          centerY = ann.y;
        } else {
          centerX = ann.startX + (ann.width || 0) / 2;
          centerY = ann.startY + (ann.height || 0) / 2;
        }
      } else if (ann.type === 'rectangle') {
        centerX = ann.startX + (ann.width || 0) / 2;
        centerY = ann.startY + (ann.height || 0) / 2;
      } else {
        continue; // Skip non-shape annotations
      }
      
      // Check if point is within annotation bounds (with padding)
      const padding = 0;
      const minX = Math.min(ann.startX, ann.startX + (ann.width || 0)) - padding;
      const maxX = Math.max(ann.startX, ann.startX + (ann.width || 0)) + padding;
      const minY = Math.min(ann.startY, ann.startY + (ann.height || 0)) - padding;
      const maxY = Math.max(ann.startY, ann.startY + (ann.height || 0)) + padding;
      
      if (x >= minX && x <= maxX && y >= minY && y <= maxY) {
        return { id: ann.id, centerX, centerY };
      }
    }
    
    return null;
  }, [annotations, pageNumber, interactionHighlightedComponentType, interactionIsHighlightAll]);

  // Helper function to get annotation center
  const getAnnotationCenter = useCallback((annotation: Annotation): { centerX: number; centerY: number } | null => {
    if (annotation.type === 'circle') {
      if (annotation.x !== undefined && annotation.y !== undefined) {
        return { centerX: annotation.x, centerY: annotation.y };
      } else {
        return {
          centerX: annotation.startX + (annotation.width || 0) / 2,
          centerY: annotation.startY + (annotation.height || 0) / 2
        };
      }
    } else if (annotation.type === 'rectangle') {
      return {
        centerX: annotation.startX + (annotation.width || 0) / 2,
        centerY: annotation.startY + (annotation.height || 0) / 2
      };
    }
    return null;
  }, []);

  // Helper function to find PDF element at a point (simplified for SVG bounding boxes)
  const findPDFElementAtPoint = useCallback((x: number, y: number, elements: PDFVectorElement[]): PDFVectorElement | null => {
    const TOLERANCE = 10; // pixels
    
    // Prioritize: 1) xobjects (electrical symbols), 2) text, 3) curves, 4) everything else
    const prioritized = [...elements].sort((a, b) => {
      const priorityA = a.type === 'xobject' ? 0 : a.type === 'text' ? 1 : a.type === 'curve' ? 2 : 3;
      const priorityB = b.type === 'xobject' ? 0 : b.type === 'text' ? 1 : b.type === 'curve' ? 2 : 3;
      return priorityA - priorityB;
    });
    
    // Simple bounding box hit test for all elements
    for (const element of prioritized) {
      const { x: ex, y: ey, width, height } = element.bounds;
      
      if (x >= ex - TOLERANCE && x <= ex + width + TOLERANCE &&
          y >= ey - TOLERANCE && y <= ey + height + TOLERANCE) {
        return element;
      }
    }
    
    return null;
  }, []);

  // Helper function to calculate real-world measurement from a pixel-space pipe
  const calculatePipeMeasurement = useCallback((canvasDistance: number, pxDx?: number, pxDy?: number): { value: number; unit: string } | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const converted = calculateRealDistanceFromCanvasDelta(
      pxDx !== undefined ? pxDx : canvasDistance,
      pxDy !== undefined ? pxDy : 0,
      pageNumber,
      canvas.width,
      canvas.height
    );
    if (!converted) return null;

    return {
      value: parseFloat(converted.value.toFixed(2)),
      unit: converted.unit
    };
  }, [calculateRealDistanceFromCanvasDelta, pageNumber]);

  // Helper function to update pipes connected to moved annotations
  const updateConnectedPipes = useCallback((annotationsList: Annotation[], movedAnnotationIds: Set<string>): Annotation[] => {
    return annotationsList.map(ann => {
      if (ann.type === 'piping') {
        let updated = false;
        let newStartX = ann.startX;
        let newStartY = ann.startY;
        let newEndX = ann.endX;
        let newEndY = ann.endY;
        let newMeasurement = ann.measurement;
        let newMeasurementUnit = ann.measurementUnit;
        
        // Check if start annotation moved
        if (ann.startAnnotationId && movedAnnotationIds.has(ann.startAnnotationId)) {
          const startAnn = annotationsList.find(a => a.id === ann.startAnnotationId);
          if (startAnn) {
            const center = getAnnotationCenter(startAnn);
            if (center) {
              newStartX = center.centerX;
              newStartY = center.centerY;
              updated = true;
            }
          }
        }
        
        // Check if end annotation moved
        if (ann.endAnnotationId && movedAnnotationIds.has(ann.endAnnotationId)) {
          const endAnn = annotationsList.find(a => a.id === ann.endAnnotationId);
          if (endAnn) {
            const center = getAnnotationCenter(endAnn);
            if (center) {
              newEndX = center.centerX;
              newEndY = center.centerY;
              updated = true;
            }
          }
        }
        
        // Recalculate measurement if pipe was updated
        if (updated) {
          const dx = (newEndX || newStartX) - newStartX;
          const dy = (newEndY || newStartY) - newStartY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const measurement = calculatePipeMeasurement(distance, dx, dy);
          
          if (measurement) {
            newMeasurement = measurement.value;
            newMeasurementUnit = measurement.unit;
          }
          
          return {
            ...ann,
            startX: newStartX,
            startY: newStartY,
            endX: newEndX,
            endY: newEndY,
            measurement: newMeasurement,
            measurementUnit: newMeasurementUnit
          };
        }
      }
      return ann;
    });
  }, [getAnnotationCenter, calculatePipeMeasurement]);

  const refreshPageAnnotationCountsFromDb = useCallback(async () => {
    if (!supabase || !estimationId) {
      setAnnotationCountsByPage({});
      return;
    }

    const requestId = ++annotationCountLoadRequestIdRef.current;
    const isStale = () => requestId !== annotationCountLoadRequestIdRef.current;
    const scopedDrawingFileId = currentDrawingFileId || file?.id || null;

    const mergeCountRows = (
      target: Record<number, number>,
      rows: Array<{ page_number?: number | null; count?: number | null }>
    ) => {
      for (const row of rows || []) {
        const page = Number(row?.page_number);
        const count = Number(row?.count || 0);
        if (!Number.isFinite(page) || page <= 0) continue;
        if (!Number.isFinite(count) || count <= 0) continue;
        target[page] = (target[page] || 0) + count;
      }
    };

    const mergeLegacyRows = (
      target: Record<number, number>,
      rows: Array<{ page_number?: number | null }>
    ) => {
      for (const row of rows || []) {
        const page = Number(row?.page_number);
        if (!Number.isFinite(page) || page <= 0) continue;
        target[page] = (target[page] || 0) + 1;
      }
    };

    try {
      const nextCounts: Record<number, number> = {};

      if (activeMode === 'draw') {
        if (!scopedDrawingFileId) {
          if (!isStale()) setAnnotationCountsByPage({});
          return;
        }

        const [fileScopedCounts, globalScopedCounts] = await Promise.all([
          supabase
            .from('annotation_counts_v2')
            .select('page_number, count')
            .eq('estimation_id', estimationId)
            .eq('scope_type', 'file')
            .eq('file_id', scopedDrawingFileId),
          supabase
            .from('annotation_counts_v2')
            .select('page_number, count')
            .eq('estimation_id', estimationId)
            .eq('scope_type', 'global')
            .is('file_id', null),
        ]);

        const needsLegacyFallback = Boolean(fileScopedCounts.error || globalScopedCounts.error);

        if (!needsLegacyFallback) {
          mergeCountRows(nextCounts, fileScopedCounts.data || []);
          mergeCountRows(nextCounts, globalScopedCounts.data || []);
        } else {
          if (fileScopedCounts.error || globalScopedCounts.error) {
            console.warn('⚠️ annotation_counts_v2 unavailable for sidebar badges, falling back to annotation_entities_v2');
          }

          const [fileScopedLegacy, globalScopedLegacy] = await Promise.all([
            supabase
              .from('annotation_entities_v2')
              .select('page_number')
              .eq('estimation_id', estimationId)
              .eq('scope_type', 'file')
              .eq('file_id', scopedDrawingFileId)
              .is('deleted_at', null),
            supabase
              .from('annotation_entities_v2')
              .select('page_number')
              .eq('estimation_id', estimationId)
              .eq('scope_type', 'global')
              .is('file_id', null)
              .is('deleted_at', null),
          ]);

          if (fileScopedLegacy.error || globalScopedLegacy.error) {
            throw fileScopedLegacy.error || globalScopedLegacy.error;
          }

          mergeLegacyRows(nextCounts, fileScopedLegacy.data || []);
          mergeLegacyRows(nextCounts, globalScopedLegacy.data || []);
        }
      } else {
        const globalCounts = await supabase
          .from('annotation_counts_v2')
          .select('page_number, count')
          .eq('estimation_id', estimationId)
          .eq('scope_type', 'global')
          .is('file_id', null);

        if (!globalCounts.error) {
          mergeCountRows(nextCounts, globalCounts.data || []);
        } else {
          const legacyCounts = await supabase
            .from('annotation_entities_v2')
            .select('page_number')
            .eq('estimation_id', estimationId)
            .eq('scope_type', 'global')
            .is('file_id', null)
            .is('deleted_at', null);

          if (legacyCounts.error) throw legacyCounts.error;
          mergeLegacyRows(nextCounts, legacyCounts.data || []);
        }
      }

      if (isStale()) return;
      setAnnotationCountsByPage(nextCounts);
    } catch (error) {
      if (isStale()) return;
      console.error('Error loading page annotation counts:', error);
      setAnnotationCountsByPage({});
    }
  }, [supabase, estimationId, activeMode, currentDrawingFileId, file?.id]);

  useEffect(() => {
    void refreshPageAnnotationCountsFromDb();
  }, [refreshPageAnnotationCountsFromDb]);

  const VERBOSE_ANNOTATION_LOAD_LOGS = false;

  // Load annotations from database
  const loadAnnotations = useCallback(async () => {
    if (!estimationId || !supabase) return;

    // Single-flight: avoid overlapping loads that repeatedly cancel each other.
    if (isAnnotationLoadInFlightRef.current) {
      queuedAnnotationReloadRef.current = true;
      return;
    }

    isAnnotationLoadInFlightRef.current = true;

    try {
      const loadRequestId = ++annotationLoadRequestIdRef.current;
      const isStaleLoad = () => loadRequestId !== annotationLoadRequestIdRef.current;

      // Don't reset loaded flags here - page/mode/file transitions handle that.
      if (activeMode === 'draw' && !currentDrawingFileId) {
        // Wait for file context before loading scoped annotations.
        console.log('⚠️ Draw mode but no file_id yet - waiting for file to load...');
        return;
      }

      const { data, error } = await loadGroupedAnnotationsFromV2({
        supabase: supabase as any,
        estimationId,
        activeMode,
        currentDrawingFileId: currentDrawingFileId || null,
        pageNumber,
      });

      if (isStaleLoad()) {
        console.log('⏭️ Ignoring stale annotation load result');
        return;
      }

      let wireRunRows: any[] = [];
      if (activeMode === 'draw') {
        const wireRunFileId = currentDrawingFileId || file?.id;
        if (wireRunFileId) {
          try {
            const { data: wireRows, error: wireRowsError } = await supabaseClient
              .from('wire_runs')
              .select('id, wire_type, points, page_number, total_length, created_at')
              .eq('estimation_id', estimationId)
              .eq('file_id', wireRunFileId)
              .eq('page_number', pageNumber);

            if (wireRowsError) {
              console.error('Error loading wire runs for annotation hydration:', wireRowsError);
            } else {
              wireRunRows = Array.isArray(wireRows) ? wireRows : [];
            }
          } catch (wireRowsErr) {
            console.error('Error loading wire runs for annotation hydration:', wireRowsErr);
          }
        }
      }

      if (isStaleLoad()) {
        console.log('⏭️ Ignoring stale annotation load result after wire run hydration query');
        return;
      }

      if (error) {
        console.error('Error loading annotations:', error);
        setAnnotationsDataLoaded(false);
        return;
      }

      setAnnotationsDataLoaded(true);

      if (data && data.length > 0) {
        console.log(`📥 Loading annotations from ${data.length} database records`);

        // Track unique IDs to detect duplicates
        const seenIds = new Set<string>();
        const duplicateIds = new Set<string>();

        // Get current canvas dimensions for scaling
        const canvas = canvasRef.current;
        if (!canvas) {
          console.warn('⚠️ Canvas not ready for loading annotations (will retry when ready)');
          setAnnotationsLoaded(false);
          return;
        }

        // Combine all annotations from different component types
        const allAnnotations: Annotation[] = [];
        let skippedTempWireRuns = 0;

        for (const record of data) {
          if (record.annotations) {
            let annotations = record.annotations;
            // Parse if it's a string
            if (typeof annotations === 'string') {
              try {
                annotations = JSON.parse(annotations);
              } catch (e) {
                console.error('Error parsing annotations:', e);
                continue;
              }
            }

            if (Array.isArray(annotations)) {
              if (VERBOSE_ANNOTATION_LOAD_LOGS) {
                console.log(`  - ${record.component_type}: ${annotations.length} annotations`);
              }

              // Process each annotation and scale if needed
              for (const savedAnn of annotations) {
                const savedAnnotationId = String(savedAnn?.id || '').trim();
                if (!savedAnnotationId) continue;

                // Ignore stale optimistic wire-run IDs that can be autosaved before DB ID linking.
                // These can reappear as visual-only "ghost" duplicates after page reload.
                if (savedAnnotationId.startsWith(WIRE_RUN_TEMP_ID_PREFIX)) {
                  skippedTempWireRuns += 1;
                  continue;
                }

                if (seenIds.has(savedAnnotationId)) {
                  duplicateIds.add(savedAnnotationId);
                  console.warn(`⚠️ Duplicate annotation ID found: ${savedAnnotationId}`);
                } else {
                  seenIds.add(savedAnnotationId);

                  let loadedAnnotation: Annotation = { ...savedAnn } as Annotation;

                  // Check if we have relative positions (new format)
                  if (savedAnn.relativeX !== undefined || savedAnn.relativeStartX !== undefined) {
                    // Use relative positions to calculate current absolute positions
                    const newX = savedAnn.relativeX ? savedAnn.relativeX * canvas.width : savedAnn.x;
                    const newY = savedAnn.relativeY ? savedAnn.relativeY * canvas.height : savedAnn.y;
                    const newStartX = savedAnn.relativeStartX ? savedAnn.relativeStartX * canvas.width : savedAnn.startX;
                    const newStartY = savedAnn.relativeStartY ? savedAnn.relativeStartY * canvas.height : savedAnn.startY;

                    if (VERBOSE_ANNOTATION_LOAD_LOGS) {
                      console.log('📍 Loading annotation with relative coords:', {
                        id: savedAnn.id,
                        type: savedAnn.type,
                        currentCanvas: {
                          width: canvas.width,
                          height: canvas.height
                        },
                        calculated: {
                          newX,
                          newY,
                          newStartX,
                          newStartY
                        }
                      });
                    }

                    loadedAnnotation = {
                      ...savedAnn,
                      x: newX,
                      y: newY,
                      startX: newStartX,
                      startY: newStartY,
                      width: savedAnn.relativeWidth ? savedAnn.relativeWidth * canvas.width : savedAnn.width,
                      height: savedAnn.relativeHeight ? savedAnn.relativeHeight * canvas.height : savedAnn.height
                    } as Annotation;
                  } else if (savedAnn.savedCanvasWidth) {
                    // Fallback: scale from saved canvas size
                    const scaleX = canvas.width / savedAnn.savedCanvasWidth;
                    const scaleY = canvas.height / savedAnn.savedCanvasHeight;

                    if (VERBOSE_ANNOTATION_LOAD_LOGS) {
                      console.log('📏 Scaling annotation from saved canvas size:', {
                        id: savedAnn.id,
                        scale: { x: scaleX, y: scaleY }
                      });
                    }

                    loadedAnnotation = {
                      ...savedAnn,
                      x: savedAnn.x ? savedAnn.x * scaleX : savedAnn.x,
                      y: savedAnn.y ? savedAnn.y * scaleY : savedAnn.y,
                      startX: savedAnn.startX ? savedAnn.startX * scaleX : savedAnn.startX,
                      startY: savedAnn.startY ? savedAnn.startY * scaleY : savedAnn.startY,
                      width: savedAnn.width ? savedAnn.width * scaleX : savedAnn.width,
                      height: savedAnn.height ? savedAnn.height * scaleY : savedAnn.height
                    } as Annotation;
                  } else {
                    // Old format - assume 16000px canvas and scale if needed
                    if (canvas.width !== 16000) {
                      const assumedScale = canvas.width / 16000;

                      if (VERBOSE_ANNOTATION_LOAD_LOGS) {
                        console.log('⚠️ Loading old format annotation, scaling from assumed 16000px:', {
                          id: savedAnn.id,
                          scale: assumedScale,
                        });
                      }

                      loadedAnnotation = {
                        ...savedAnn,
                        x: savedAnn.x ? savedAnn.x * assumedScale : savedAnn.x,
                        y: savedAnn.y ? savedAnn.y * assumedScale : savedAnn.y,
                        startX: savedAnn.startX ? savedAnn.startX * assumedScale : savedAnn.startX,
                        startY: savedAnn.startY ? savedAnn.startY * assumedScale : savedAnn.startY,
                        width: savedAnn.width ? savedAnn.width * assumedScale : savedAnn.width,
                        height: savedAnn.height ? savedAnn.height * assumedScale : savedAnn.height
                      } as Annotation;
                    }
                  }

                  allAnnotations.push(loadedAnnotation);
                }
              }
            }
          }
        }

        let hydratedWireRuns = 0;
        if (wireRunRows.length > 0) {
          for (const wireRunRow of wireRunRows) {
            const hydratedWireRun = buildWireRunAnnotationFromRow(wireRunRow, canvas.width, canvas.height);
            if (!hydratedWireRun) continue;
            if (seenIds.has(hydratedWireRun.id)) continue;
            seenIds.add(hydratedWireRun.id);
            allAnnotations.push(hydratedWireRun);
            hydratedWireRuns += 1;
          }
        }

        if (duplicateIds.size > 0) {
          console.error(`❌ Found ${duplicateIds.size} duplicate annotation IDs in database`);
          console.error('Duplicate IDs:', Array.from(duplicateIds));
        }
        if (skippedTempWireRuns > 0) {
          console.warn(`⚠️ Ignored ${skippedTempWireRuns} stale temporary wire-run annotation(s) while loading`);
        }
        if (hydratedWireRuns > 0) {
          console.log(`🔁 Rehydrated ${hydratedWireRuns} wire run annotation(s) from wire key`);
        }

        // Recalculate pipe positions based on their connected annotations
        const recalculatedAnnotations = allAnnotations.map(ann => {
          if (ann.type === 'piping' && ann.startAnnotationId && ann.endAnnotationId) {
            // Find the connected annotations
            const startAnn = allAnnotations.find(a => a.id === ann.startAnnotationId);
            const endAnn = allAnnotations.find(a => a.id === ann.endAnnotationId);

            if (startAnn && endAnn) {
              // Recalculate centers
              const startCenter = getAnnotationCenter(startAnn);
              const endCenter = getAnnotationCenter(endAnn);

              if (startCenter && endCenter) {
                // Calculate new distance and measurement inline
                // (avoids depending on calculatePipeMeasurement which changes with pageNumber)
                const dx = endCenter.centerX - startCenter.centerX;
                const dy = endCenter.centerY - startCenter.centerY;

                // Inline pipe measurement: read calibration for the pipe's page
                let measurement: { value: number; unit: string } | null = null;
                const pipePage = ann.page || 1;
                if (canvas) {
                  const converted = calculateRealDistanceFromCanvasDelta(
                    dx,
                    dy,
                    pipePage,
                    canvas.width,
                    canvas.height
                  );
                  if (converted) {
                    measurement = {
                      value: parseFloat(converted.value.toFixed(2)),
                      unit: converted.unit
                    };
                  }
                }

                if (VERBOSE_ANNOTATION_LOAD_LOGS) {
                  console.log(`📏 Recalculated pipe ${ann.id}:`, {
                    oldStart: { x: ann.startX, y: ann.startY },
                    newStart: { x: startCenter.centerX, y: startCenter.centerY },
                    oldEnd: { x: ann.endX, y: ann.endY },
                    newEnd: { x: endCenter.centerX, y: endCenter.centerY },
                    measurement: measurement ? `${measurement.value}${measurement.unit}` : 'none'
                  });
                }

                return {
                  ...ann,
                  startX: startCenter.centerX,
                  startY: startCenter.centerY,
                  endX: endCenter.centerX,
                  endY: endCenter.centerY,
                  measurement: measurement?.value,
                  measurementUnit: measurement?.unit
                };
              }
            }
          }
          return ann;
        });

        if (isStaleLoad()) {
          console.log('⏭️ Ignoring stale annotation load result before state commit');
          return;
        }
        setAnnotations(recalculatedAnnotations);
        setHistory([recalculatedAnnotations]);
        setHistoryIndex(0);
        setAnnotationsLoaded(true);
        persistedAnnotationSignaturesRef.current = new Map(
          recalculatedAnnotations
            .filter((annotation) => annotation?.id)
            .map((annotation) => [String(annotation.id), getAnnotationSignature(annotation)])
        );

        // Update the last save hash to prevent immediate re-save
        lastSaveHashRef.current = getAnnotationsHash(recalculatedAnnotations);

        console.log(`✅ Loaded ${recalculatedAnnotations.length} unique annotations from database`);
      } else {
        const canvas = canvasRef.current;
        if (wireRunRows.length > 0) {
          if (!canvas) {
            console.warn('⚠️ Canvas not ready for loading wire run annotations (will retry when ready)');
            setAnnotationsLoaded(false);
            return;
          }

          const hydratedWireRunAnnotations = wireRunRows
            .map((wireRunRow) => buildWireRunAnnotationFromRow(wireRunRow, canvas.width, canvas.height))
            .filter((annotation): annotation is Annotation => !!annotation);

          if (hydratedWireRunAnnotations.length > 0) {
            if (isStaleLoad()) {
              console.log('⏭️ Ignoring stale wire-run-only annotation load result');
              return;
            }
            lastLoadWasEmptyRef.current = false;
            setAnnotations(hydratedWireRunAnnotations);
            setHistory([hydratedWireRunAnnotations]);
            setHistoryIndex(0);
            setAnnotationsLoaded(true);
            persistedAnnotationSignaturesRef.current = new Map(
              hydratedWireRunAnnotations
                .filter((annotation) => annotation?.id)
                .map((annotation) => [String(annotation.id), getAnnotationSignature(annotation)])
            );
            lastSaveHashRef.current = getAnnotationsHash(hydratedWireRunAnnotations);
            console.log(`✅ Loaded ${hydratedWireRunAnnotations.length} wire run annotation(s) from wire key`);
            return;
          }
        }

        // No annotations in database - set flag to prevent saveAnnotations from deleting
        if (isStaleLoad()) {
          console.log('⏭️ Ignoring stale empty annotation load result');
          return;
        }
        lastLoadWasEmptyRef.current = true;
        setAnnotations([]);
        setHistory([[]]);
        setHistoryIndex(0);
        setAnnotationsLoaded(true);
        persistedAnnotationSignaturesRef.current = new Map();
        lastSaveHashRef.current = getAnnotationsHash([]);
      }
    } catch (error) {
      console.error('Could not load from Supabase:', error);
      setAnnotationsDataLoaded(false);
    } finally {
      isAnnotationLoadInFlightRef.current = false;
      if (queuedAnnotationReloadRef.current) {
        queuedAnnotationReloadRef.current = false;
        setTimeout(() => {
          void loadAnnotationsRef.current();
        }, 0);
      }
    }
  }, [
    estimationId,
    activeMode,
    currentDrawingFileId,
    file?.id,
    pageNumber,
    getAnnotationCenter,
    calibrationData,
    buildWireRunAnnotationFromRow,
    VERBOSE_ANNOTATION_LOAD_LOGS,
    calculateRealDistanceFromCanvasDelta
  ]);

  useEffect(() => {
    loadAnnotationsRef.current = loadAnnotations;
  }, [loadAnnotations]);
  
  // Load saved annotations from Supabase on mount or when estimationId changes
  useEffect(() => {
    void loadAnnotations();
  }, [loadAnnotations]);

  useEffect(() => {
    let isMounted = true;
    if (!estimationId || !supabaseClient) return;

    setHasCheckedProjectV2Annotations(false);

    const checkProjectV2Annotations = async () => {
      try {
        const { count, error } = await supabaseClient
          .from('annotation_entities_v2')
          .select('annotation_uid', { count: 'exact', head: true })
          .eq('estimation_id', estimationId)
          .is('deleted_at', null);

        if (error) {
          console.warn('⚠️ Failed exact count for annotation_entities_v2, falling back to existence check:', error);
          const { data: fallbackRows, error: fallbackError } = await supabaseClient
            .from('annotation_entities_v2')
            .select('annotation_uid')
            .eq('estimation_id', estimationId)
            .is('deleted_at', null)
            .limit(1);
          if (fallbackError) throw fallbackError;
          if (!isMounted) return;
          setProjectHasNoV2Annotations((fallbackRows || []).length === 0);
          setHasCheckedProjectV2Annotations(true);
          return;
        }

        if (!isMounted) return;
        setProjectHasNoV2Annotations((count ?? 0) === 0);
        setHasCheckedProjectV2Annotations(true);
      } catch (error) {
        console.error('❌ Could not check project annotation_entities_v2 rows:', error);
        if (!isMounted) return;
        setProjectHasNoV2Annotations(false);
        setHasCheckedProjectV2Annotations(true);
      }
    };

    void checkProjectV2Annotations();
    return () => {
      isMounted = false;
    };
  }, [estimationId]);

  useEffect(() => {
    if (annotations.length > 0) {
      setProjectHasNoV2Annotations(false);
    }
  }, [annotations.length]);

  useEffect(() => {
    if (
      activeMode === 'draw' &&
      hasCheckedProjectV2Annotations &&
      projectHasNoV2Annotations &&
      annotationsLoaded &&
      annotationsDataLoaded &&
      !hasDismissedTakeoffOnboardingPrompt &&
      !guidedTakeoffMode &&
      !showTakeoffPageSelectionModal &&
      !showTakeoffLightingFixtureKeyModal &&
      !showLightingExtractionGuideModal &&
      !showLightingScheduleUploadModal
    ) {
      setShowTakeoffOnboardingStartModal(true);
    }
  }, [
    activeMode,
    annotationsDataLoaded,
    annotationsLoaded,
    guidedTakeoffMode,
    hasCheckedProjectV2Annotations,
    hasDismissedTakeoffOnboardingPrompt,
    projectHasNoV2Annotations,
    showTakeoffLightingFixtureKeyModal,
    showLightingExtractionGuideModal,
    showLightingScheduleUploadModal,
    showTakeoffPageSelectionModal,
  ]);

  useEffect(() => {
    if (!projectHasNoV2Annotations) {
      setShowTakeoffOnboardingStartModal(false);
    }
  }, [projectHasNoV2Annotations]);

  const isLightingScheduleExtractionActive =
    showTakeoffOnboardingStartModal ||
    showTakeoffLightingFixtureKeyModal ||
    showLightingExtractionGuideModal ||
    showLightingScheduleUploadModal ||
    lightingScheduleMode ||
    !!pendingLightingScheduleStartPage ||
    showLightingScheduleConfirmModal ||
    isExtractingLightingSchedule ||
    showLightingSchedulePreview;

  useEffect(() => {
    onLightingScheduleExtractionActiveChange?.(isLightingScheduleExtractionActive);
  }, [isLightingScheduleExtractionActive, onLightingScheduleExtractionActiveChange]);

  useEffect(() => {
    return () => {
      onLightingScheduleExtractionActiveChange?.(false);
    };
  }, [onLightingScheduleExtractionActiveChange]);
  
  // Reload annotations when switching drawing files in draw mode
  const prevDrawingFileIdRef = useRef<string | undefined>(currentDrawingFileId);
  useEffect(() => {
    if (activeMode === 'draw' && currentDrawingFileId && prevDrawingFileIdRef.current !== currentDrawingFileId) {
      console.log('🔄 Drawing file changed from', prevDrawingFileIdRef.current, 'to', currentDrawingFileId);
      console.log('   Clearing current annotations and reloading...');

      annotationLoadRequestIdRef.current += 1; // Invalidate any in-flight load for the previous file.
      queuedAnnotationReloadRef.current = false;
      annotationRetryInFlightRef.current = false;
      if (annotationRetryTimerRef.current) {
        clearTimeout(annotationRetryTimerRef.current);
        annotationRetryTimerRef.current = null;
      }

      // Clear current annotations first
      setAnnotations([]);
      setHistory([[]]);
      setHistoryIndex(0);
      setAnnotationsLoaded(false); // Reset flag so retry mechanism works
      setAnnotationsDataLoaded(false);

      // Then reload for the new file
      void loadAnnotations();
    }
    prevDrawingFileIdRef.current = currentDrawingFileId;
  }, [currentDrawingFileId, activeMode, loadAnnotations]);
  
  // Reset annotations loaded flag when switching to draw mode to trigger reload
  const prevActiveModeRef = useRef(activeMode);
  useEffect(() => {
    if (activeMode === 'draw' && prevActiveModeRef.current !== 'draw') {
      console.log('🔄 Switched to draw mode - resetting annotations loaded flag');
      annotationLoadRequestIdRef.current += 1;
      queuedAnnotationReloadRef.current = false;
      annotationRetryInFlightRef.current = false;
      if (annotationRetryTimerRef.current) {
        clearTimeout(annotationRetryTimerRef.current);
        annotationRetryTimerRef.current = null;
      }
      setAnnotationsLoaded(false);
      setAnnotationsDataLoaded(false);
    }
    prevActiveModeRef.current = activeMode;
  }, [activeMode]);

  // Retry loading annotations when canvas becomes ready after tab switch
  // This fixes the issue where annotations fail to load because canvas isn't ready yet
  useEffect(() => {
    const hasCanvas = Boolean(canvasRef.current);
    const canRetryLoad =
      activeMode === 'draw' &&
      !isTransitioning &&
      hasCanvas &&
      Boolean(currentDrawingFileId) &&
      isPDFRendered &&
      isCanvasSynced &&
      annotationsDataLoaded &&
      !annotationsLoaded;

    if (!canRetryLoad) {
      annotationRetryInFlightRef.current = false;
      if (annotationRetryTimerRef.current) {
        clearTimeout(annotationRetryTimerRef.current);
        annotationRetryTimerRef.current = null;
      }
      return;
    }

    if (annotationRetryInFlightRef.current || isAnnotationLoadInFlightRef.current) return;
    annotationRetryInFlightRef.current = true;

    if (annotationRetryTimerRef.current) {
      clearTimeout(annotationRetryTimerRef.current);
    }
    annotationRetryTimerRef.current = setTimeout(() => {
      console.log('🔄 Canvas ready - retrying annotation load');
      void loadAnnotations().then(() => {
        if (annotationsLoadedRef.current) {
          console.log('🎨 Triggering redraw after annotation load');
          setForceRedraw(prev => prev + 1);
        }
      }).finally(() => {
        annotationRetryInFlightRef.current = false;
      });
      annotationRetryTimerRef.current = null;
    }, 0);

    return () => {
      if (annotationRetryTimerRef.current) {
        clearTimeout(annotationRetryTimerRef.current);
        annotationRetryTimerRef.current = null;
      }
    };
  }, [activeMode, isTransitioning, currentDrawingFileId, isPDFRendered, isCanvasSynced, annotationsDataLoaded, annotationsLoaded, loadAnnotations]);

  useEffect(() => {
    return () => {
      if (annotationRetryTimerRef.current) {
        clearTimeout(annotationRetryTimerRef.current);
        annotationRetryTimerRef.current = null;
      }
    }
  }, []);
  
  // Removed: Electrical key reload useEffect
  // The key only affects annotation colors, not positions or data
  // Annotations automatically redraw with new colors because drawAnnotations depends on electricalKey
  // Reloading from database was causing race conditions where freshly saved annotations
  // would be deleted if the reload happened before the save transaction committed

  // Track if we've already prompted for auto-annotation on each page
  const promptedPagesRef = useRef<Set<number>>(new Set());
  
  // Expose method to mark page as prompted (for when user dismisses)
  useEffect(() => {
    if (ref && typeof ref === 'object' && 'current' in ref && ref.current) {
      ref.current.markPageAsPrompted = (page: number) => {
        promptedPagesRef.current.add(page);
        console.log(`Marked page ${page} as prompted (won't ask again)`);
      };
    }
  }, [ref]);
  
  // DISABLED: Auto-annotation feature - commented out for now
  // Check for auto-annotation when page changes
  // useEffect(() => {
  //   const checkAndTriggerAutoAnnotation = async () => {
  //     if (!estimationId || !pageNumber || !onAutoAnnotationTrigger || !supabase) return;
  //     
  //     // Wait for annotations to be loaded from database before checking
  //     if (!annotationsLoaded) {
  //       console.log('⏳ Waiting for annotations to load before checking auto-annotation...');
  //       return;
  //     }
  //     
  //     // Don't prompt again if we already prompted for this page in this session
  //     if (promptedPagesRef.current.has(pageNumber)) {
  //       console.log(`Already prompted for auto-annotation on page ${pageNumber} in this session, skipping`);
  //       return;
  //     }
  //     
  //     // Check if page has any message history - don't show auto-annotation if there are any messages
  //     try {
  //       const { data: pageMessages, error: messagesError } = await supabase
  //         .from('estimation_chat_messages')
  //         .select('id')
  //         .eq('estimation_id', estimationId)
  //         .eq('page_number', pageNumber);
  //       
  //       if (messagesError) {
  //         console.error('Error checking page messages:', messagesError);
  //         return;
  //       }
  //       
  //       if (pageMessages && pageMessages.length > 0) {
  //         console.log(`Page ${pageNumber} has ${pageMessages.length} message(s), skipping auto-annotation prompt`);
  //         promptedPagesRef.current.add(pageNumber); // Mark as prompted for this session too
  //         return;
  //       }
  //     } catch (error) {
  //       console.error('Error checking page messages:', error);
  //       return;
  //     }
  //     
  //     // Wait a bit to ensure annotations are loaded from database
  //     await new Promise(resolve => setTimeout(resolve, 800));
  //     
  //     // Check current in-memory annotations first (most accurate)
  //     const pageHasAnnotations = annotations.some(a => a.page === pageNumber);
  //     if (pageHasAnnotations) {
  //       console.log(`Page ${pageNumber} has ${annotations.filter(a => a.page === pageNumber).length} annotations in memory, skipping auto-annotation prompt`);
  //       return;
  //     }
  //     
  //     // Double-check with database
  //     const { shouldRun, electricalKey } = await shouldAutoAnnotate(
  //       pageNumber,
  //       estimationId
  //     );
  //     
  //     if (shouldRun && electricalKey) {
  //       console.log(`🎯 Triggering auto-annotation prompt for page ${pageNumber}`);
  //       // Note: We'll add to promptedPagesRef when user dismisses OR accepts
  //       onAutoAnnotationTrigger({
  //         pageNumber,
  //         electricalKey,
  //         estimationId,
  //         onDismiss: () => {
  //           promptedPagesRef.current.add(pageNumber);
  //           console.log(`User dismissed auto-annotation for page ${pageNumber}`);
  //         }
  //       });
  //     }
  //   };
  //   
  //   checkAndTriggerAutoAnnotation();
  // }, [pageNumber, estimationId, onAutoAnnotationTrigger, annotations, annotationsLoaded]);

  // Handle sending snapshot to agent
  const handleSendToAgent = async () => {
    await captureSnapshotToClipboard();
    setSnapshotSelection(null);
    setShowSnapshotActions(false);
  };

  // Handle extracting key from snapshot
  const handleExtractKey = async () => {
    if (!canvasRef.current || !snapshotSelection) return;

    const annotationCanvas = canvasRef.current;
    const pdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
    
    if (!pdfCanvas) {
      console.error('PDF canvas not found');
      alert('Could not capture PDF area. Please try again.');
      return;
    }

    // Calculate the selection bounds
    const x = Math.min(snapshotSelection.startX, snapshotSelection.endX);
    const y = Math.min(snapshotSelection.startY, snapshotSelection.endY);
    const width = Math.abs(snapshotSelection.endX - snapshotSelection.startX);
    const height = Math.abs(snapshotSelection.endY - snapshotSelection.startY);

    // Create temporary canvas to capture the selected area
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = width;
    tempCanvas.height = height;
    const tempCtx = tempCanvas.getContext('2d');

    if (!tempCtx) return;

    // Draw the selected portion of the PDF
    tempCtx.drawImage(pdfCanvas, x, y, width, height, 0, 0, width, height);

    // Convert to data URL
    const snapshotDataUrl = tempCanvas.toDataURL('image/jpeg', 0.95);

    // Clear snapshot UI immediately
    setSnapshotSelection(null);
    setShowSnapshotActions(false);

    // Send progress updates
    const updateProgress = (msg: string) => {
      if (onKeyExtractionProgress) {
        onKeyExtractionProgress(msg);
      }
      if (onScreenshotCaptured) {
        onScreenshotCaptured(msg);
      }
    };

    updateProgress('Understanding table...');

    // Import and call the key extraction service
    const { extractKeyFromSnapshot, convertToProjectKeyFormat } = await import('../services/keyExtractionService');
    
    try {
      console.log('🔑 Extracting key from snapshot...');
      
      const extractedKey = await extractKeyFromSnapshot(snapshotDataUrl);
      
      updateProgress('Extracting elements...');
      
      const projectKey = convertToProjectKeyFormat(extractedKey);
      
      console.log('✅ Key extracted successfully:', projectKey);
      
      // Call the onKeyExtracted callback
      if (onKeyExtracted) {
        await onKeyExtracted(projectKey);
      }
      
      // Send success message - brief confirmation
      updateProgress(`Extracted ${projectKey.components.length} symbols`);
      
    } catch (error) {
      console.error('❌ Error extracting key:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      
      updateProgress(`❌ Extraction failed: ${errorMsg}`);
    }
  };

  // Handle canceling snapshot
  const handleCancelSnapshot = () => {
    setSnapshotSelection(null);
    setShowSnapshotActions(false);
  };

  // Capture snapshot to clipboard
  const captureSnapshotToClipboard = async () => {
    if (!canvasRef.current || !snapshotSelection) return;

    const annotationCanvas = canvasRef.current;
    const pdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
    
    if (!pdfCanvas) {
      console.error('PDF canvas not found');
      return;
    }

    // Calculate the selection bounds (handle negative width/height)
    const x = Math.min(snapshotSelection.startX, snapshotSelection.endX);
    const y = Math.min(snapshotSelection.startY, snapshotSelection.endY);
    const width = Math.abs(snapshotSelection.endX - snapshotSelection.startX);
    const height = Math.abs(snapshotSelection.endY - snapshotSelection.startY);

    // Don't capture if the selection is too small
    if (width < 10 || height < 10) {
      console.log('Selection too small');
      return;
    }
    
    // Define minimum size for AI recognition
    // 300px gives Claude much better material to work with
    const MIN_SIZE = 300;

    console.log('📸 Capturing area:', { x, y, width, height });
    console.log('📐 Canvas resolution:', {
      annotationCanvas: `${annotationCanvas.width}x${annotationCanvas.height}`,
      pdfCanvas: `${pdfCanvas.width}x${pdfCanvas.height}`,
      capturedArea: `${width}x${height} pixels`
    });

    try {
      // Create a combined canvas that includes both PDF and annotations
      const combinedCanvas = document.createElement('canvas');
      combinedCanvas.width = annotationCanvas.width;
      combinedCanvas.height = annotationCanvas.height;
      const combinedCtx = combinedCanvas.getContext('2d')!;

      // Draw the PDF content first
      combinedCtx.drawImage(pdfCanvas, 0, 0, annotationCanvas.width, annotationCanvas.height);
      
      // Draw the annotations on top
      combinedCtx.drawImage(annotationCanvas, 0, 0);

      // Create a temporary canvas for the cropped area
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = width;
      tempCanvas.height = height;
      const tempCtx = tempCanvas.getContext('2d')!;

      // Draw the selected area from the combined canvas
      tempCtx.drawImage(
        combinedCanvas,
        x, y, width, height,  // Source rectangle
        0, 0, width, height   // Destination rectangle
      );

      // Smart upscaling for small images
      let finalCanvas = tempCanvas;
      let finalCtx = tempCtx;
      
      if (width < MIN_SIZE || height < MIN_SIZE) {
        console.log(`🔍 Image too small (${width}x${height}), upscaling for better recognition...`);
        
        // Calculate scale factor to reach minimum size while maintaining aspect ratio
        const scale = Math.max(MIN_SIZE / width, MIN_SIZE / height);
        const upscaledWidth = Math.round(width * scale);
        const upscaledHeight = Math.round(height * scale);
        
        // Create upscaled canvas
        const upscaledCanvas = document.createElement('canvas');
        upscaledCanvas.width = upscaledWidth;
        upscaledCanvas.height = upscaledHeight;
        const upscaledCtx = upscaledCanvas.getContext('2d')!;
        
        // For electrical symbols, we want sharp edges
        // Try different interpolation methods based on content
        
        // Use bicubic-like interpolation for smoother upscaling
        // This preserves the shape much better than nearest neighbor
        upscaledCtx.imageSmoothingEnabled = true;
        upscaledCtx.imageSmoothingQuality = 'high';
        upscaledCtx.drawImage(tempCanvas, 0, 0, upscaledWidth, upscaledHeight);
        
        // Only apply mild enhancement - don't destroy the image!
        if (width < 100 || height < 100) {
          console.log('🔍 Applying mild enhancement for clarity...');
          const imageData = upscaledCtx.getImageData(0, 0, upscaledWidth, upscaledHeight);
          const data = imageData.data;
          
          // Just a gentle contrast boost - nothing aggressive
          const factor = 1.2; // Much gentler than before
          for (let i = 0; i < data.length; i += 4) {
            // Enhance contrast slightly
            data[i] = Math.min(255, Math.max(0, (data[i] - 128) * factor + 128));
            data[i + 1] = Math.min(255, Math.max(0, (data[i + 1] - 128) * factor + 128));
            data[i + 2] = Math.min(255, Math.max(0, (data[i + 2] - 128) * factor + 128));
          }
          
          upscaledCtx.putImageData(imageData, 0, 0);
        }
        
        console.log(`📐 Upscaled from ${width}x${height} to ${upscaledWidth}x${upscaledHeight} (${scale.toFixed(1)}x)`);
        finalCanvas = upscaledCanvas;
        finalCtx = upscaledCtx;
      }

      // Convert to blob
      const blob = await new Promise<Blob | null>(resolve => {
        finalCanvas.toBlob(resolve, 'image/png');
      });

      if (blob) {
        // Convert blob to base64 data URL
        const reader = new FileReader();
        reader.onload = async () => {
          const base64 = reader.result as string;
          
          // If we have a callback, send the image directly to the chat
          if (onScreenshotCaptured) {
            onScreenshotCaptured(base64);
            
            // Log whether image was upscaled
            if (width < MIN_SIZE || height < MIN_SIZE) {
              const scale = Math.max(MIN_SIZE / width, MIN_SIZE / height);
              console.log(`✅ Screenshot sent to chat (upscaled ${scale.toFixed(1)}x for better recognition)`);
            } else {
              console.log('✅ Screenshot sent directly to chat!');
            }
          }
          
          // Optionally try to copy to clipboard (will fail on HTTP, but that's OK)
          try {
            // Only attempt clipboard if we're on HTTPS
            if (window.location.protocol === 'https:' && navigator.clipboard && window.ClipboardItem) {
              await navigator.clipboard.write([
                new ClipboardItem({
                  'image/png': blob
                })
              ]);
              console.log('✅ Screenshot also copied to clipboard!');
            }
          } catch (error) {
            // Silently ignore clipboard errors - it's just a nice-to-have feature
            // The main functionality (sending to chat) already worked
          }

          // Show success feedback
          const ctx = annotationCanvas.getContext('2d');
          if (ctx) {
            // Flash the selection area briefly
            ctx.save();
            ctx.fillStyle = 'rgba(0, 255, 0, 0.3)';
            ctx.fillRect(x, y, width, height);
            ctx.restore();

            // Clear the flash after a moment
            setTimeout(() => {
              drawAnnotations();
            }, 200);
          }
        };
        
        reader.readAsDataURL(blob);
      } else {
        console.error('Failed to create screenshot blob');
      }
    } catch (error) {
      console.error('Error capturing screenshot:', error);
    }
  };

  const lastSaveHashRef = useRef<string>('');
  const lastLoadWasEmptyRef = useRef<boolean>(false);
  const annotationsRef = useRef<Annotation[]>([]);
  const annotationsLoadedRef = useRef<boolean>(false);
  const persistedAnnotationSignaturesRef = useRef<Map<string, string>>(new Map());
  const isPersistableAnnotation = useCallback((annotation: Annotation) => {
    const annotationId = String(annotation?.id || '').trim();
    if (!annotationId) return false;
    return !annotationId.startsWith(WIRE_RUN_TEMP_ID_PREFIX);
  }, []);

  const buildCommitContext = useCallback(
    (beforeLocalCommit?: () => void) => ({
      supabase: supabase as any,
      estimationId,
      activeMode,
      currentDrawingFileId: currentDrawingFileId || null,
      annotationsRef,
      setAnnotations,
      onAnnotationSaved,
      acquireSaveLock,
      releaseSaveLock,
      setIsSaving,
      canvasRef,
      persistFilter: isPersistableAnnotation,
      ...(beforeLocalCommit ? { beforeLocalCommit } : {}),
    }),
    [
      supabase,
      estimationId,
      activeMode,
      currentDrawingFileId,
      onAnnotationSaved,
      acquireSaveLock,
      releaseSaveLock,
      setIsSaving,
      isPersistableAnnotation,
    ]
  );

  const buildPatchByIdFromSnapshot = useCallback(
    (snapshot: Annotation[], changedIds?: string[]) => {
      const byId = new Map<string, Annotation>();
      for (const annotation of snapshot || []) {
        const id = String(annotation?.id || '').trim();
        if (!id) continue;
        byId.set(id, annotation);
      }

      const candidateIds = changedIds && changedIds.length
        ? Array.from(new Set(changedIds.map((id) => String(id || '').trim()).filter(Boolean)))
        : Array.from(byId.keys());

      const patchById: Record<string, Partial<Annotation>> = {};
      for (const id of candidateIds) {
        const annotation = byId.get(id);
        if (!annotation) continue;
        if (!isPersistableAnnotation(annotation)) continue;
        patchById[id] = annotation;
      }
      return patchById;
    },
    [isPersistableAnnotation]
  );

  const persistCreatedAnnotations = useCallback(async (
    newItems: Annotation[]
  ): Promise<{ next: Annotation[]; changedComponentTypes: string[] }> => {
    const incoming = (newItems || []).filter((annotation) => {
      const id = String(annotation?.id || '').trim();
      return Boolean(id);
    });
    if (!incoming.length) {
      return { next: annotationsRef.current, changedComponentTypes: [] };
    }

    try {
      const result = await commitCreateAnnotations(incoming, buildCommitContext());
      const incomingIds = new Set(incoming.map((annotation) => annotation.id));
      for (const annotation of result.next) {
        if (!incomingIds.has(annotation.id)) continue;
        if (!isPersistableAnnotation(annotation)) continue;
        persistedAnnotationSignaturesRef.current.set(
          annotation.id,
          getAnnotationSignature(annotation)
        );
      }
      lastSaveHashRef.current = getAnnotationsHash(result.next.filter(isPersistableAnnotation));
      console.log('✅ Annotations saved successfully');
      void refreshPageAnnotationCountsFromDb();
      return result;
    } catch (error) {
      console.error('❌ Error saving created annotations:', error);
      try {
        localStorage.setItem(`annotations_${estimationId}`, JSON.stringify(annotationsRef.current));
        console.log('💾 Saved to localStorage as fallback');
      } catch (storageError) {
        console.warn('⚠️ localStorage fallback also failed (quota exceeded):', storageError);
      }
      throw error;
    }
  }, [
    buildCommitContext,
    estimationId,
    isPersistableAnnotation,
    refreshPageAnnotationCountsFromDb,
  ]);

  const persistAnnotations = useCallback((
    snapshot?: Annotation[],
    changedIds?: string[]
  ) => {
    if (!annotationsLoadedRef.current) {
      console.log('⏭️ persistAnnotations ignored - annotations not loaded');
      return;
    }

    const snapshotToPersist = snapshot ? [...snapshot] : [...annotationsRef.current];
    const patchById = buildPatchByIdFromSnapshot(snapshotToPersist, changedIds);
    const patchIds = Object.keys(patchById);
    if (!patchIds.length) {
      return;
    }

    void (async () => {
      try {
        const result = await commitUpdateAnnotations(patchById, buildCommitContext());
        const nextById = new Map(result.next.map((annotation) => [annotation.id, annotation]));
        for (const id of result.updatedIds) {
          const annotation = nextById.get(id);
          if (!annotation || !isPersistableAnnotation(annotation)) continue;
          persistedAnnotationSignaturesRef.current.set(id, getAnnotationSignature(annotation));
        }
        lastSaveHashRef.current = getAnnotationsHash(result.next.filter(isPersistableAnnotation));
        console.log('✅ Annotations saved successfully');
        void refreshPageAnnotationCountsFromDb();
      } catch (error) {
        console.error('❌ Error saving annotations:', error);
        try {
          localStorage.setItem(`annotations_${estimationId}`, JSON.stringify(snapshotToPersist));
          console.log('💾 Saved to localStorage as fallback');
        } catch (storageError) {
          console.warn('⚠️ localStorage fallback also failed (quota exceeded):', storageError);
        }
      }
    })();
  }, [
    buildPatchByIdFromSnapshot,
    buildCommitContext,
    estimationId,
    isPersistableAnnotation,
    refreshPageAnnotationCountsFromDb,
  ]);

  const persistDeletedAnnotations = useCallback(async (
    annotationIds: string[],
    knownComponentTypes?: string[]
  ) => {
    if (!supabase || !estimationId) return;
    const ids = Array.from(new Set((annotationIds || []).map((id) => String(id)).filter(Boolean)));
    if (!ids.length) return;

    try {
      await commitDeleteAnnotations(ids, buildCommitContext(), knownComponentTypes);

      ids.forEach((id) => persistedAnnotationSignaturesRef.current.delete(id));

      // Force next snapshot save to re-evaluate after explicit deletions.
      lastSaveHashRef.current = '';
      void refreshPageAnnotationCountsFromDb();
    } catch (error) {
      console.error('❌ Error persisting explicit annotation delete:', error);
    }
  }, [
    supabase,
    estimationId,
    activeMode,
    currentDrawingFileId,
    acquireSaveLock,
    releaseSaveLock,
    onAnnotationSaved,
    setIsSaving,
    buildCommitContext,
    refreshPageAnnotationCountsFromDb,
  ]);

  const clearPersistedAnnotations = useCallback(async () => {
    if (!supabase || !estimationId) return;
    const result = await commitClearScopeAnnotations(buildCommitContext());
    console.log('✅ Scoped annotations cleared from database successfully', {
      deletedRowCount: result.deletedCount,
      scope: activeMode === 'draw' ? 'file' : 'global',
      fileId: activeMode === 'draw' ? currentDrawingFileId || null : null,
    });
    persistedAnnotationSignaturesRef.current.clear();
    lastSaveHashRef.current = '';
    void refreshPageAnnotationCountsFromDb();
  }, [
    supabase,
    estimationId,
    buildCommitContext,
    activeMode,
    currentDrawingFileId,
    refreshPageAnnotationCountsFromDb,
  ]);

  // Helper function to calculate pop-in animation scale
  const getAnimationScale = useCallback((annotationId: string): number => {
    const timestamp = annotationTimestamps.get(annotationId);
    if (!timestamp) return 1;
    
    const elapsed = Date.now() - timestamp;
    const duration = 700; // 700ms animation for smoother, more visible effect
    
    if (elapsed >= duration) return 1;
    
    // Ease-out elastic effect with bounce
    const progress = elapsed / duration;
    // Creates a bounce effect: overshoots slightly then settles
    const scale = 1 + Math.sin(progress * Math.PI) * 0.3 * (1 - progress);
    
    return scale;
  }, [annotationTimestamps]);

  const { drawAnnotations } = useDrawAnnotations({
    canvasRef,
    debugTileGridRef,
    annotations,
    pageNumber,
    selectedAnnotation,
    isDrawing,
    currentAnnotation,
    dragStart,
    resizeHandle,
    currentColor,
    thickness,
    highlightedComponentType: interactionHighlightedComponentType,
    viewAllMode: editAllMode,
    animatingAnnotations,
    selectionRect,
    isSelecting,
    selectedAnnotations,
    snapshotSelection,
    showSnapshotActions,
    calibrationLine,
    isCalibrating,
    isGridSnapActive,
    calibrationData,
    pipingPreview,
    pipingSnapTarget,
    currentTool,
    isElementDetectionMode,
    hoveredPDFElement,
    autocountSelection,
    autocountMode,
    keyExtractionSelection,
    keyExtractionMode,
    lightingScheduleSelection,
    lightingScheduleMode,
    showDebugTileGrid,
    wireRunExtendMode,
    wireRunExtendPreviewPoint,
    hoveredAnnotation,
    hoveredAnnotationPoint,
    rescaleAnnotation,
    getColorForAnnotation,
    getAnimationScale,
    calculatePipeMeasurement,

  });

  // Redraw canvas when annotation data/state changes.
  // Exclude zoom scale here: the stage already uses CSS transform for zoom/pan.
  useEffect(() => {
    drawAnnotations();
  }, [drawAnnotations, annotations, electricalKey, forceRedraw]); // Added electricalKey and forceRedraw dependencies

  // Sync annotation canvas dimensions with PDF canvas using MutationObserver
  useEffect(() => {
    let resizeObserver: ResizeObserver | null = null;
    let mutationObserver: MutationObserver | null = null;
    let syncTimeout: NodeJS.Timeout | null = null;

    const syncCanvasDimensions = () => {
      const pdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
      
      if (pdfCanvas && canvasRef.current) {
        // Reduced logging for performance - only log when dimensions actually change
        const needsSync = canvasRef.current.width !== pdfCanvas.width || 
                         canvasRef.current.height !== pdfCanvas.height;
        
        // Check if dimensions actually changed
        if (needsSync) {
          
          console.log('📐 PDF canvas dimensions changed, syncing annotation canvas:', {
            oldDimensions: {
              width: canvasRef.current.width,
              height: canvasRef.current.height
            },
            newDimensions: {
              width: pdfCanvas.width,
              height: pdfCanvas.height
            },
            rotation: pageRotations[pageNumber] || 0
          });
          
          // Match the PDF canvas dimensions exactly
          canvasRef.current.width = pdfCanvas.width;
          canvasRef.current.height = pdfCanvas.height;
          
          // CRITICAL: Also match the visual display size
          // This ensures the annotation canvas overlays the PDF exactly
          const pdfStyles = window.getComputedStyle(pdfCanvas);
          canvasRef.current.style.width = pdfStyles.width;
          canvasRef.current.style.height = pdfStyles.height;
          
          console.log('📏 Syncing visual dimensions:', {
            canvasInternalDimensions: { width: pdfCanvas.width, height: pdfCanvas.height },
            canvasVisualDimensions: { width: pdfStyles.width, height: pdfStyles.height }
          });
          
          // Update tracked dimensions
          setCurrentCanvasWidth(pdfCanvas.width);
          setCurrentCanvasHeight(pdfCanvas.height);
          
          // Recalculate annotation positions locally from relative coords
          // (avoids a Supabase round-trip on every canvas resize)
          const newW = pdfCanvas.width;
          const newH = pdfCanvas.height;
          setAnnotations(prev => prev.map(ann => rescaleAnnotation(ann, newW, newH)));
          
          // Mark canvas as synced
          setIsCanvasSynced(true);
        } else {
          // Canvas is already synced
          setIsCanvasSynced(true);
        }
      }
    };

    // Watch for PDF canvas being added/changed in DOM
    mutationObserver = new MutationObserver((mutations) => {
      // Debounce to avoid multiple rapid syncs
      if (syncTimeout) clearTimeout(syncTimeout);
      syncTimeout = setTimeout(() => {
        syncCanvasDimensions();
        
        // Also set up ResizeObserver on the PDF canvas once it exists
        const pdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
        if (pdfCanvas && !resizeObserver) {
          resizeObserver = new ResizeObserver(() => {
            syncCanvasDimensions();
          });
          resizeObserver.observe(pdfCanvas);
        }
      }, 50); // Small debounce delay
    });

    // Start observing the container for canvas changes
    if (containerRef.current) {
      mutationObserver.observe(containerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['width', 'height', 'style']
      });
    }

    // Initial sync attempt
    syncCanvasDimensions();

    // Cleanup
    return () => {
      if (syncTimeout) clearTimeout(syncTimeout);
      mutationObserver?.disconnect();
      resizeObserver?.disconnect();
    };
  }, [pageNumber, pageRotations, drawAnnotations]);

  // Animation loop for smooth number animations
  useEffect(() => {
    if (animatingAnnotations.size > 0) {
      let animationId: number;
      let isRunning = true;

      const animate = () => {
        if (isRunning && animatingAnnotations.size > 0) {
          drawAnnotations();
          animationId = requestAnimationFrame(animate);
        }
      };

      animationId = requestAnimationFrame(animate);

      return () => {
        isRunning = false;
        if (animationId) {
          cancelAnimationFrame(animationId);
        }
      };
    }
  }, [animatingAnnotations.size, drawAnnotations]);

  // Animation loop for piping snap target pulsing
  useEffect(() => {
    if (pipingSnapTarget) {
      let animationId: number;
      let isRunning = true;

      const animate = () => {
        if (isRunning && pipingSnapTarget) {
          drawAnnotations();
          animationId = requestAnimationFrame(animate);
        }
      };

      animationId = requestAnimationFrame(animate);

      return () => {
        isRunning = false;
        if (animationId) {
          cancelAnimationFrame(animationId);
        }
      };
    }
  }, [pipingSnapTarget, drawAnnotations]);

  const {
    addToHistory,
    renumberAnnotations,
    handleCanvasDrop,
    undo,
    redo,
    clearAll,
  } = useAnnotationCanvasOperations({
    history,
    setHistory,
    historyIndex,
    setHistoryIndex,
    annotationsRef,
    setAnnotations,
    canvasRef,
    pageNumber,
    setSelectedAnnotation,
    setSelectedAnnotations,
    persistCreatedAnnotations,
    promptedPagesRef,
    supabase,
      estimationId,
      activeMode,
    currentDrawingFileId,
    clearPersistedAnnotations,
  });

  const {
    handleMouseDown,
    handleMouseMove,
    handleContextMenu,
    handleMouseUp,
  } = useCanvasInteractionHandlers({
    canvasRef,
    annotationsRef,
    lastMousePosRef: lastMousePosRef as React.MutableRefObject<{ x: number; y: number } | null>,
    lastWheelTimeRef,
    dirtyWireRunAnnotationIdsRef,
    lastPlacedAnnotationsRef,
    getScreenToCanvasCoords,
    findAnnotationAtPoint,
    findPDFElementAtPoint,
    addToHistory,
    persistAnnotations,
    persistCreatedAnnotations,
    syncWireRunsToDatabase,
    handleCancelSnapshot,
    handleExtractKey,
    drawAnnotations,
    rescaleAnnotation,
    normalizeWireRunGeometry,
    constrainTo90Degrees,
    findWireRunVertexIndexAtPoint,
    findWireRunSegmentAtPoint,
    findWireRunEndpointAtPoint,
    updateConnectedPipes,
    openWireRunEditContextMenuAtPoint,
    renumberAnnotations,
    persistDeletedAnnotations,
    deletePersistedWireRunsForAnnotationIds,
    calculatePipeMeasurement,
    calculateCanvasPercentage,
    isPointInAnnotation,
    pointToLineDistance,
    pageNumber,
    annotations,
    panX,
    panY,
    isElementDetectionMode,
    wireRunExtendMode,
    currentTool,
    hoveredAnnotation,
    isWireRunMode,
    selectedWireType,
    autocountMode,
    keyExtractionMode,
    lightingScheduleMode,
    isCalibrating,
    handDrawnEnabled,
    pipingStart,
    pipingSnapTarget,
    pipingPreview,
    showSnapshotActions,
    snapshotSelection,
    wireRunContextMenu,
    wireRunEditContextMenu,
    activePlacementComponent,
    countingModeActive,
    selectedAnnotation,
    selectedAnnotations,
    highlightedComponentType: interactionHighlightedComponentType,
    isHighlightAll: interactionIsHighlightAll,
    hasActiveFilter: interactionHasActiveFilter,
    viewAllMode: editAllMode,
    pdfVectorElements,
    isPanning,
    panStart,
    resizeHandle,
    wireRunVertexDrag,
    autocountSelection,
    keyExtractionSelection,
    lightingScheduleSelection,
    calibrationLine,
    isDrawing,
    currentAnnotation,
    currentColor,
    thickness,
    dragStart,
    selectionRect,
    isSelecting,
    isCapturingSnapshot,
    wireRunExtendPreviewPoint,
    hoverCursor,
    isRightClickPanning,
    autoExtractKeyOnSnapshot,
    isAddingNewOnlyScanMode,
    isAddingNewOnly,
    pendingAutocountComponent,
    setHoveredAnnotation,
    setHoveredAnnotationPoint,
    setIsRightClickPanning,
    setIsPanning,
    setPanStart,
    setAutocountSelection,
    setKeyExtractionSelection,
    setLightingScheduleSelection,
    setCalibrationLine,
    setIsDrawing,
    setPipingStart,
    setPipingPreview,
    setPipingSnapTarget,
    setSnapshotSelection,
    setWireRunContextMenu,
    setWireRunEditContextMenu,
    setCurrentAnnotation,
    setResizeHandle,
    setWireRunExtendMode,
    setWireRunExtendPreviewPoint,
    setSelectedAnnotation,
    setSelectedAnnotations,
    setWireRunVertexDrag,
    setSelectionRect,
    setIsSelecting,
    setDragStart,
    setIsCapturingSnapshot,
    setShowSnapshotActions,
    setHoverCursor,
    setHoveredPDFElement,
    setShowCalibrationModal,
    setReferenceAreaImage,
    setEditingQueuedName,
    setAutocountMode,
    setShowNewComponentModal,
    setShowAutocountConfirmModal,
    setKeyExtractionSnapshot,
    setShowKeyExtractionConfirmModal,
    setLightingScheduleSnapshot,
    setShowLightingScheduleConfirmModal,
    setAnnotations,
    setPanX,
    setPanY,
    onSwitchToWiresTab,
    onPlacementComplete,
  });

  // NEW: Photoshop-style Zoom to Point
  const zoomAtPoint = useCallback((zoomFactor: number, screenX: number, screenY: number) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    
    // Position relative to viewport
    const rx = screenX - rect.left;
    const ry = screenY - rect.top;

    setScale(prev => {
      const newScale = Math.max(MIN_VIEW_SCALE, Math.min(10, prev * zoomFactor));
      const actualFactor = newScale / prev;
      
      // Adjust pan to keep point under cursor
      setPanX(rx - (rx - panX) * actualFactor);
      setPanY(ry - (ry - panY) * actualFactor);
      
      return newScale;
    });
  }, [panX, panY]);

  const zoomIn = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    
    // Use last mouse position if available, otherwise center of viewport
    const zoomX = lastMousePosRef.current?.x ?? (rect.left + rect.width / 2);
    const zoomY = lastMousePosRef.current?.y ?? (rect.top + rect.height / 2);
    
    zoomAtPoint(1.5, zoomX, zoomY);
  }, [zoomAtPoint]);

  const zoomOut = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    
    // Use last mouse position if available, otherwise center of viewport
    const zoomX = lastMousePosRef.current?.x ?? (rect.left + rect.width / 2);
    const zoomY = lastMousePosRef.current?.y ?? (rect.top + rect.height / 2);
    
    zoomAtPoint(1/1.5, zoomX, zoomY);
  }, [zoomAtPoint]);

  // Toolbar zoom should always anchor to viewport center.
  // Wheel/keyboard zoom intentionally keep mouse-anchor behavior.
  const zoomInFromToolbar = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    zoomAtPoint(1.5, rect.left + rect.width / 2, rect.top + rect.height / 2);
  }, [zoomAtPoint]);

  const zoomOutFromToolbar = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    zoomAtPoint(1 / 1.5, rect.left + rect.width / 2, rect.top + rect.height / 2);
  }, [zoomAtPoint]);

  const centerView = useCallback((targetScale?: number) => {
    const container = containerRef.current;
    if (!container) return;
    const viewport = container.querySelector('.pdf-viewport') as HTMLDivElement | null;
    const annotationCanvas = canvasRef.current;
    const pdfCanvas = container.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement | null;
    if (!viewport || (!annotationCanvas && !pdfCanvas)) return;

    const contentWidth =
      (annotationCanvas?.offsetWidth || annotationCanvas?.clientWidth || 0) ||
      (pdfCanvas?.offsetWidth || pdfCanvas?.clientWidth || 0);
    const contentHeight =
      (annotationCanvas?.offsetHeight || annotationCanvas?.clientHeight || 0) ||
      (pdfCanvas?.offsetHeight || pdfCanvas?.clientHeight || 0);
    if (!contentWidth || !contentHeight) return;

    const effectiveScale = targetScale ?? scale;
    setPanX((viewport.clientWidth - contentWidth * effectiveScale) / 2);
    setPanY((viewport.clientHeight - contentHeight * effectiveScale) / 2);
  }, [scale]);

  const resetZoom = useCallback(() => {
    setScale(DEFAULT_VIEW_SCALE);
    setTimeout(() => centerView(DEFAULT_VIEW_SCALE), 0);
  }, [centerView]);

  const handleDownloadOriginalPDF = useCallback(async () => {
    if (!file?.url) return;
    try {
      const response = await fetch(file.url);
      if (!response.ok) throw new Error(`Failed to fetch PDF (${response.status})`);

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name || 'drawing.pdf';
      a.click();
      URL.revokeObjectURL(url);
      console.log('✅ Downloaded original PDF');
    } catch (err) {
      console.error('Failed to download PDF:', err);
    }
  }, [file]);

  // Function to rotate the current page
  const rotateCurrentPage = useCallback(() => {
    setPageRotations(prev => {
      const currentRotation = prev[pageNumber] || 0;
      const newRotation = (currentRotation + 90) % 360;
      console.log(`🔄 Rotating page ${pageNumber}: ${currentRotation}° → ${newRotation}°`);

      // Save rotation to localStorage for persistence
      const updatedRotations = { ...prev, [pageNumber]: newRotation };
      if (estimationId && file?.name) {
        try {
          const storageKey = `page-rotations-${estimationId}-${file.name}`;
          localStorage.setItem(storageKey, JSON.stringify(updatedRotations));
        } catch { /* quota exceeded — ignore */ }
      }

      // Re-center the view after rotation
      setTimeout(() => {
        centerView();
        console.log('📍 Re-centered after rotation');
      }, 500); // Wait for rotation animation to complete

      return updatedRotations;
    });

    // Redraw annotations with new rotation
    setTimeout(() => drawAnnotations(), 10);
  }, [pageNumber, estimationId, file?.name, drawAnnotations, centerView]);

  // Function to rotate specs pages
  const rotateSpecsPage = useCallback(() => {
    setSpecsPageRotations(prev => {
      const currentRotation = prev[specsPageNumber] || 0;
      const newRotation = (currentRotation + 90) % 360;
      console.log(`🔄 Rotating specs page ${specsPageNumber}: ${currentRotation}° → ${newRotation}°`);

      // Save rotation to localStorage for persistence
      const updatedRotations = { ...prev, [specsPageNumber]: newRotation };
      if (estimationId && file?.name) {
        try {
          const storageKey = `specs-rotations-${estimationId}-${file.name}`;
          localStorage.setItem(storageKey, JSON.stringify(updatedRotations));
        } catch { /* quota exceeded — ignore */ }
      }

      return updatedRotations;
    });
  }, [specsPageNumber, estimationId, file]);

  const deleteSelected = async () => {
    // UNIFIED SELECT: Delete from both annotation layers
    const hasCountingAnnotations = selectedAnnotation || selectedAnnotations.size > 0;
    const hasHandDrawnStrokes = selectedHandDrawnStrokes.size > 0;
    
    if (hasCountingAnnotations || hasHandDrawnStrokes) {
      // Delete counting annotations
      let newAnnotations;
      let deletedIds: Set<string>;
      const currentAnnotations = annotationsRef.current;

      if (hasCountingAnnotations) {
        if (selectedAnnotations.size > 0) {
          // Delete multiple selected annotations
          deletedIds = new Set(selectedAnnotations);
          newAnnotations = currentAnnotations.filter(a => !selectedAnnotations.has(a.id));
          setSelectedAnnotations(new Set());
        } else {
          // Delete single selected annotation
          deletedIds = new Set([selectedAnnotation!]);
          newAnnotations = currentAnnotations.filter(a => a.id !== selectedAnnotation);
        }
      } else {
        newAnnotations = currentAnnotations;
        deletedIds = new Set();
      }

      // Also mark pipes connected to deleted annotations for explicit DB hard-delete.
      currentAnnotations.forEach((annotation) => {
        if (annotation.type !== 'piping') return;
        const startDeleted = annotation.startAnnotationId && deletedIds.has(annotation.startAnnotationId);
        const endDeleted = annotation.endAnnotationId && deletedIds.has(annotation.endAnnotationId);
        if (startDeleted || endDeleted) {
          deletedIds.add(annotation.id);
        }
      });

      // Check if any deleted annotations are wire runs and delete from database
      for (const id of Array.from(deletedIds)) {
        if (id.startsWith(WIRE_RUN_ANNOTATION_PREFIX)) {
          const wireRunDbId = getWireRunDbIdFromAnnotationId(id);
          if (!wireRunDbId) {
            console.log('Skipping wire run DB delete for unsaved annotation:', id);
            continue;
          }
          console.log('🗑️ Deleting wire run from database:', wireRunDbId);
          
          try {
            const { error } = await supabaseClient
              .from('wire_runs')
              .delete()
              .eq('id', wireRunDbId);
            
            if (error) {
              console.error('Error deleting wire run from database:', error);
            } else {
              console.log('✅ Wire run deleted from database');
            }
          } catch (err) {
            console.error('Error deleting wire run:', err);
          }
        }
      }
      
      // Trigger refresh of WireRunsPanel if any wire runs were deleted
      const hasPersistedWireRuns = Array.from(deletedIds).some(id => !!getWireRunDbIdFromAnnotationId(id));
      if (hasPersistedWireRuns) {
        bumpWireRunRefresh();
      }

      // Also delete any pipes connected to the deleted annotations
      newAnnotations = newAnnotations.filter(a => {
        if (a.type === 'piping') {
          // Delete pipe if either endpoint was deleted
          const startDeleted = a.startAnnotationId && deletedIds.has(a.startAnnotationId);
          const endDeleted = a.endAnnotationId && deletedIds.has(a.endAnnotationId);
          return !startDeleted && !endDeleted;
        }
        return true;
      });

      // Renumber annotations by component type
      newAnnotations = renumberAnnotations(newAnnotations);

      const deletedComponentTypes = Array.from(new Set(
        currentAnnotations
          .filter((annotation) => deletedIds.has(annotation.id))
          .map((annotation) => annotation.component_type)
          .filter(Boolean)
      )) as string[];

      annotationsRef.current = newAnnotations;
      setAnnotations(newAnnotations);
      addToHistory(newAnnotations);
      setSelectedAnnotation(null);

      void persistDeletedAnnotations(Array.from(deletedIds), deletedComponentTypes);
      
      // Delete hand-drawn strokes if any selected
      if (hasHandDrawnStrokes) {
        console.log('🗑️ Deleting', selectedHandDrawnStrokes.size, 'hand-drawn strokes');
        // Call the hand-drawn deletion function
        if ((window as any).handDrawnDeleteSelected) {
          (window as any).handDrawnDeleteSelected();
        }
        setSelectedHandDrawnStrokes(new Set());
      }
    }
  };

  const copySelectedRectangles = useCallback((): boolean => {
    const selectedIds = new Set<string>();
    if (selectedAnnotations.size > 0) {
      selectedAnnotations.forEach((id) => selectedIds.add(id));
    } else if (selectedAnnotation) {
      selectedIds.add(selectedAnnotation);
    }

    if (!selectedIds.size) return false;

    const selectedRectangles = annotationsRef.current.filter(
      (annotation) => selectedIds.has(annotation.id) && annotation.type === 'rectangle'
    );
    if (!selectedRectangles.length) return false;

    const normalized = selectedRectangles.map((annotation) => {
      const rawWidth = typeof annotation.width === 'number' ? annotation.width : 0;
      const rawHeight = typeof annotation.height === 'number' ? annotation.height : 0;
      const width = Math.abs(rawWidth);
      const height = Math.abs(rawHeight);
      const startX = rawWidth < 0 ? annotation.startX + rawWidth : annotation.startX;
      const startY = rawHeight < 0 ? annotation.startY + rawHeight : annotation.startY;
      return {
        startX,
        startY,
        width,
        height,
        color: annotation.color,
        thickness: typeof annotation.thickness === 'number' ? annotation.thickness : 3,
        componentType: annotation.component_type || 'annotation',
        text: annotation.text,
        autoDetected: annotation.auto_detected,
        confidence: annotation.confidence,
      };
    });

    const minX = Math.min(...normalized.map((item) => item.startX));
    const minY = Math.min(...normalized.map((item) => item.startY));
    const maxX = Math.max(...normalized.map((item) => item.startX + item.width));
    const maxY = Math.max(...normalized.map((item) => item.startY + item.height));

    copiedRectanglesRef.current = {
      items: normalized.map((item) => ({
        offsetX: item.startX - minX,
        offsetY: item.startY - minY,
        width: item.width,
        height: item.height,
        color: item.color,
        thickness: item.thickness,
        componentType: item.componentType,
        text: item.text,
        autoDetected: item.autoDetected,
        confidence: item.confidence,
      })),
      width: Math.max(maxX - minX, 1),
      height: Math.max(maxY - minY, 1),
    };

    console.log(`📋 Copied ${selectedRectangles.length} rectangle(s)`);
    return true;
  }, [selectedAnnotation, selectedAnnotations]);

  const pasteCopiedRectanglesAtCursor = useCallback(async (): Promise<boolean> => {
    const clipboard = copiedRectanglesRef.current;
    if (!clipboard || !clipboard.items.length) return false;

    const canvas = canvasRef.current;
    if (!canvas) return false;

    const mousePos = lastMousePosRef.current;
    const targetPoint = mousePos
      ? getScreenToCanvasCoords(mousePos.x, mousePos.y)
      : { x: canvas.width / 2, y: canvas.height / 2 };
    const groupOriginX = targetPoint.x - clipboard.width / 2;
    const groupOriginY = targetPoint.y - clipboard.height / 2;

    const createAnnotationId = () => {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return `manual-${crypto.randomUUID()}`;
      }
      return `manual-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    };

    const existingCountByType = new Map<string, number>();
    const getNextCount = (componentType: string) => {
      if (!existingCountByType.has(componentType)) {
        const baseCount = annotationsRef.current.filter(
          (annotation) =>
            annotation.component_type === componentType &&
            annotation.page === pageNumber
        ).length;
        existingCountByType.set(componentType, baseCount);
      }
      const nextCount = (existingCountByType.get(componentType) || 0) + 1;
      existingCountByType.set(componentType, nextCount);
      return nextCount;
    };

    const nowIso = new Date().toISOString();
    const pastedIds: string[] = [];
    const pastedRectangles: Annotation[] = clipboard.items.map((item) => {
      const componentType = item.componentType || 'annotation';
      const id = createAnnotationId();
      pastedIds.push(id);
      return {
        id,
        type: 'rectangle',
        page: pageNumber,
        startX: groupOriginX + item.offsetX,
        startY: groupOriginY + item.offsetY,
        width: item.width > 0 ? item.width : 60,
        height: item.height > 0 ? item.height : 60,
        color: item.color,
        thickness: item.thickness,
        timestamp: nowIso,
        component_type: componentType,
        number: getNextCount(componentType),
        text: item.text,
        auto_detected: item.autoDetected,
        confidence: item.confidence,
      };
    });

    try {
      const { next } = await persistCreatedAnnotations(pastedRectangles);
      addToHistory(next);
      setSelectedAnnotations(new Set(pastedIds));
      setSelectedAnnotation(pastedIds[0] || null);
      console.log(`📋 Pasted ${pastedRectangles.length} rectangle(s)`);
      return true;
    } catch (error) {
      console.error('Failed to paste copied rectangles:', error);
      return false;
    }
  }, [
    addToHistory,
    getScreenToCanvasCoords,
    pageNumber,
    persistCreatedAnnotations,
    setSelectedAnnotation,
    setSelectedAnnotations,
  ]);

  useKeyboardAndWheelShortcuts({
    containerRef,
    lastWheelTimeRef,
    lastPlacedAnnotationsRef,
    annotationsRef,
    selectedAnnotation,
    selectedAnnotations,
    deleteSelected,
    snapshotSelection,
    showSnapshotActions,
    handleCancelSnapshot,
    zoomIn,
    zoomOut,
    resetZoom,
    zoomAtPoint,
    isWireRunMode,
    countingModeActive,
    activePlacementComponent,
    addToHistory,
    wireRunExtendMode,
    wireRunEditContextMenu,
    setWireRunEditContextMenu,
    setWireRunExtendMode,
    setWireRunExtendPreviewPoint,
    setWireRunVertexDrag,
    setAnnotations,
    setSelectedAnnotation,
    setPanX,
    setPanY,
    persistDeletedAnnotations,
    onStopCounting,
    onCopyRectangles: copySelectedRectangles,
    onPasteRectangles: pasteCopiedRectanglesAtCursor,
    cancelActivePlacementDrawing: () => {
      setIsDrawing(false);
      setCurrentAnnotation(null);
      setSelectionRect(null);
      setIsSelecting(false);
      setDragStart(null);
    },
  });

  // Export annotated PDF
  const exportPDF = async () => {
    if (!file) return;
    
    try {
      const response = await fetch(file.url);
      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const pages = pdfDoc.getPages();
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      
      // Add annotations to PDF
      annotations.forEach((annotation) => {
        const pageIndex = annotation.page - 1;
        const page = pages[pageIndex];
        if (!page) return;
        
        const { width, height } = page.getSize();
        
        // Convert canvas coordinates to PDF coordinates
        const pdfY = height - annotation.startY;
        
        // Parse color from rgba string
        const parseColor = (colorStr: string) => {
          const match = colorStr.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
          if (match) {
            return rgb(
              parseInt(match[1]) / 255,
              parseInt(match[2]) / 255,
              parseInt(match[3]) / 255
            );
          }
          return rgb(1, 0, 0);
        };
        
        const color = parseColor(annotation.color);
        
        switch (annotation.type) {
          case 'rectangle':
            page.drawRectangle({
              x: annotation.startX,
              y: pdfY - (annotation.height || 0),
              width: annotation.width || 0,
              height: annotation.height || 0,
              borderColor: color,
              borderWidth: annotation.thickness
            });
            break;
            
          case 'line':
            page.drawLine({
              start: { x: annotation.startX, y: pdfY },
              end: { x: annotation.endX || annotation.startX, y: height - (annotation.endY || annotation.startY) },
              color: color,
              thickness: annotation.thickness
            });
            break;
            
          case 'text':
            if (annotation.text) {
              page.drawText(annotation.text, {
                x: annotation.startX,
                y: pdfY,
                size: 16,
                font: helveticaFont,
                color: color
              });
            }
            break;
        }
      });
      
      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([new Uint8Array(modifiedPdfBytes)], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `annotated_${file.name}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting PDF:', error);
    }
  };

  // Export current page as PNG at 8k resolution
  const exportPageAsPNG = async () => {
    if (!file || !pdfDocument) return;
    
    try {
      console.log('📸 Exporting page at 8k resolution...');
      
      // Get the current page from PDF
      const pdfPage = await pdfDocument.getPage(pageNumber);
      const rotation = pageRotations[pageNumber] || 0;
      const viewport = pdfPage.getViewport({ scale: 1.0, rotation });
      
      // Calculate scale to achieve 8000px on longest edge
      const maxRes = 8000;
      const longestSide = Math.max(viewport.width, viewport.height);
      const scale = maxRes / longestSide;
      const scaledViewport = pdfPage.getViewport({ scale, rotation });
      
      console.log(`📐 Export dimensions: ${scaledViewport.width}x${scaledViewport.height}`);
      
      // Create high-res canvas for PDF
      const pdfCanvas = document.createElement('canvas');
      pdfCanvas.width = scaledViewport.width;
      pdfCanvas.height = scaledViewport.height;
      const pdfCtx = pdfCanvas.getContext('2d')!;
      
      // Render PDF at high resolution
      await pdfPage.render({
        canvasContext: pdfCtx,
        viewport: scaledViewport
      }).promise;
      
      // Create combined canvas
      const combinedCanvas = document.createElement('canvas');
      combinedCanvas.width = scaledViewport.width;
      combinedCanvas.height = scaledViewport.height;
      const ctx = combinedCanvas.getContext('2d')!;
      
      // Draw the high-res PDF
      ctx.drawImage(pdfCanvas, 0, 0);
      
      // Get current canvas dimensions to calculate scale factor
      const currentCanvas = canvasRef.current;
      if (currentCanvas) {
        const scaleX = scaledViewport.width / currentCanvas.width;
        const scaleY = scaledViewport.height / currentCanvas.height;
        
        // Draw annotations scaled up to 8k resolution
        const pageAnnotations = annotations.filter(a => a.page === pageNumber);
        
        pageAnnotations.forEach(annotation => {
          const annotationColor = getColorForAnnotation(annotation);
          ctx.strokeStyle = annotationColor;
          ctx.lineWidth = annotation.thickness * scaleX;
          
          // Set fill style with semi-transparency
          const colorMatch = annotationColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
          if (colorMatch) {
            ctx.fillStyle = `rgba(${colorMatch[1]}, ${colorMatch[2]}, ${colorMatch[3]}, 0.4)`;
          }
          
          switch (annotation.type) {
            case 'circle':
              let centerX, centerY;
              if (annotation.x !== undefined && annotation.y !== undefined) {
                centerX = annotation.x * scaleX;
                centerY = annotation.y * scaleY;
              } else {
                centerX = (annotation.startX + (annotation.width || 0) / 2) * scaleX;
                centerY = (annotation.startY + (annotation.height || 0) / 2) * scaleY;
              }
              
              if (annotation.number) {
                const text = String(annotation.number);
                const radius = (text.length === 1 ? 24 : text.length === 2 ? 28 : 32) * scaleX;
                
                // Draw filled circle
                ctx.beginPath();
                ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
                ctx.fill();
                ctx.strokeStyle = annotationColor;
                ctx.lineWidth = 2 * scaleX;
                ctx.stroke();
                
                // Draw number
                const fontSize = (text.length === 1 ? 28 : 24) * scaleX;
                ctx.font = `bold ${fontSize}px Arial`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
                ctx.lineWidth = 3 * scaleX;
                ctx.strokeText(text, centerX, centerY);
                ctx.fillStyle = 'white';
                ctx.fillText(text, centerX, centerY);
              } else {
                // Small dot
                ctx.beginPath();
                ctx.arc(centerX, centerY, 5 * scaleX, 0, 2 * Math.PI);
                ctx.fill();
                ctx.strokeStyle = annotationColor;
                ctx.lineWidth = 1 * scaleX;
                ctx.stroke();
              }
              break;
              
            case 'rectangle':
              ctx.fillRect(
                annotation.startX * scaleX,
                annotation.startY * scaleY,
                (annotation.width || 0) * scaleX,
                (annotation.height || 0) * scaleY
              );
              ctx.strokeRect(
                annotation.startX * scaleX,
                annotation.startY * scaleY,
                (annotation.width || 0) * scaleX,
                (annotation.height || 0) * scaleY
              );
              break;
              
            case 'line':
              ctx.beginPath();
              ctx.moveTo(annotation.startX * scaleX, annotation.startY * scaleY);
              ctx.lineTo((annotation.endX || annotation.startX) * scaleX, (annotation.endY || annotation.startY) * scaleY);
              ctx.stroke();
              break;
              
            case 'pen':
              if (annotation.points && annotation.points.length > 0) {
                ctx.beginPath();
                ctx.moveTo(annotation.points[0].x * scaleX, annotation.points[0].y * scaleY);
                annotation.points.forEach(point => {
                  ctx.lineTo(point.x * scaleX, point.y * scaleY);
                });
                ctx.stroke();
              }
              break;
          }
        });
      }
      
      // Convert to blob and download
      combinedCanvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${file.name.replace('.pdf', '')}_page_${pageNumber}_8k.png`;
          a.click();
          URL.revokeObjectURL(url);
          console.log(`✅ Exported 8k PNG: ${scaledViewport.width}x${scaledViewport.height}`);
        }
      }, 'image/png');
    } catch (error) {
      console.error('Error exporting PNG:', error);
    }
  };

  // Export takeoff report with counts summary and annotated blueprints
  const exportCountTakeoffReport = async () => {
    if (!file || !pdfDocument) {
      console.error('Missing file or PDF document');
      return;
    }
    
    try {
      console.log('📊 Generating takeoff report with counts...');
      console.log('📊 Total annotations in state:', annotations.length);
      
      // Load ALL annotations from database
      let allAnnotations: Annotation[] = [];
      
      console.log('📊 Supabase available:', !!supabase);
      console.log('📊 Estimation ID:', estimationId);
      
      if (supabase) {
        console.log('📊 Loading annotations from database...');
        const { data, error } = await loadGroupedAnnotationsFromV2({
          supabase: supabase as any,
          estimationId,
          activeMode: 'estimate',
          currentDrawingFileId: null,
        });
        
        console.log('📊 Database query result:', { data, error });
        
        if (error) {
          console.error('❌ Error loading annotations from database:', error);
        } else if (data) {
          console.log(`📊 Loaded ${data.length} component records from database`);
          if (data.length === 0) {
            console.warn('⚠️ No records found in database for estimation_id:', estimationId);
          }
          // Combine all annotations from all component types
          data.forEach(record => {
            console.log(`📊 Record:`, record);
            if (record.annotations && Array.isArray(record.annotations)) {
              console.log(`📊 Adding ${record.annotations.length} annotations from ${record.component_type}`);
              allAnnotations = allAnnotations.concat(record.annotations as Annotation[]);
            } else {
              console.warn(`⚠️ No annotations array in record for ${record.component_type}`);
            }
          });
        }
      } else {
        console.error('❌ Supabase not available!');
      }
      
      // If no annotations in database, use current state
      if (allAnnotations.length === 0) {
        console.log('📊 No annotations in database, using state annotations:', annotations.length);
        allAnnotations = annotations;
      }
      
      console.log('📊 Total annotations to export:', allAnnotations.length);
      console.log('📊 Sample annotation:', allAnnotations[0]);
      
      const pdfDoc = await PDFDocument.create();
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      
      // ========================================
      // PART 1: Generate Summary Report Page(s)
      // ========================================
      
      // Group counts by symbol type and component
      const countsByComponent: any = {};
      // Use numbered annotations if available, otherwise use all circle annotations
      let numberedAnnotations = allAnnotations.filter(a => a.number !== undefined);
      console.log('📊 Numbered annotations:', numberedAnnotations.length);
      
      // If no numbered annotations, use all circles
      if (numberedAnnotations.length === 0) {
        numberedAnnotations = allAnnotations.filter(a => a.type === 'circle');
        console.log('📊 Using all circle annotations instead:', numberedAnnotations.length);
      }
      
      console.log('📊 Sample numbered annotation:', numberedAnnotations[0]);
      
      // If we have annotations, create summary page
      if (numberedAnnotations.length > 0) {
        console.log('📊 Creating summary page with annotations...');
        numberedAnnotations.forEach((a, index) => {
        console.log(`📊 Processing annotation ${index + 1}:`, {
          type: a.type,
          component_type: a.component_type,
          number: a.number,
          page: a.page
        });
        
        const componentName = a.component_type || 'Unnamed';
        
        // Look up symbol type from electrical key
        let symbolType = 'Other';
        if (electricalKey && a.component_type) {
          const keyComponent = electricalKey.components.find(
            (c: any) => c.name === a.component_type || c.symbol === a.component_type
          );
          if (keyComponent?.symbolType) {
            // Capitalize and format the symbol type
            symbolType = keyComponent.symbolType
              .split('_')
              .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
          }
        }
        
        const key = `${symbolType}_${componentName}`;
        if (!countsByComponent[key]) {
          countsByComponent[key] = {
            symbolType,
            componentName,
            items: []
          };
        }
        countsByComponent[key].items.push({
          page: a.page,
          number: a.number || index + 1, // Use index if no number
          x: Math.round(a.x || a.startX || 0),
          y: Math.round(a.y || a.startY || 0)
        });
      });
      
      console.log('📊 Counts by component:', countsByComponent);
      
      // Skip summary page if no valid components (will just show blueprints)
      const hasValidComponents = Object.keys(countsByComponent).length > 0;
      
      if (hasValidComponents) {
        // Create summary page
        const summaryPage = pdfDoc.addPage([612, 792]); // Letter size
        let yPosition = 750;
      
      // Draw a test rectangle to verify page is rendering
      summaryPage.drawRectangle({
        x: 40,
        y: 40,
        width: 532,
        height: 712,
        borderColor: rgb(0.8, 0.8, 0.8),
        borderWidth: 1,
      });
      
      // Title
      summaryPage.drawText('ELECTRICAL TAKEOFF SUMMARY', {
        x: 50,
        y: yPosition,
        size: 18,
        font: boldFont,
        color: rgb(0, 0, 0)
      });
      
      yPosition -= 30;
      summaryPage.drawText(`Project: ${file.name}`, {
        x: 50,
        y: yPosition,
        size: 12,
        font: font,
        color: rgb(0.3, 0.3, 0.3)
      });
      
      yPosition -= 10;
      summaryPage.drawText(`Date: ${new Date().toLocaleDateString()}`, {
        x: 50,
        y: yPosition,
        size: 12,
        font: font,
        color: rgb(0.3, 0.3, 0.3)
      });
      
      yPosition -= 40;
      
      // Table header
      summaryPage.drawText('SYMBOL TYPE', { x: 50, y: yPosition, size: 10, font: boldFont });
      summaryPage.drawText('COMPONENT', { x: 200, y: yPosition, size: 10, font: boldFont });
      summaryPage.drawText('COUNT', { x: 450, y: yPosition, size: 10, font: boldFont });
      summaryPage.drawText('PAGES', { x: 510, y: yPosition, size: 10, font: boldFont });
      
      yPosition -= 20;
      
      // Draw line
      summaryPage.drawLine({
        start: { x: 50, y: yPosition },
        end: { x: 560, y: yPosition },
        thickness: 1,
        color: rgb(0, 0, 0)
      });
      
      yPosition -= 15;
      
      // List each component
      Object.values(countsByComponent).forEach((component: any) => {
        const count = component.items.length;
        const pages = Array.from(new Set(component.items.map((i: any) => i.page))).sort((a: any, b: any) => (a as number) - (b as number)).join(', ');
        
        // Check if we need a new page
        if (yPosition < 100) {
          pdfDoc.addPage([612, 792]);
          yPosition = 750;
        }
        
        // Symbol type with color coding
        let typeColor = rgb(0, 0, 0);
        if (component.symbolType === 'Power') typeColor = rgb(0.13, 0.77, 0.37);
        else if (component.symbolType === 'Lighting') typeColor = rgb(0.92, 0.70, 0.03);
        else if (component.symbolType === 'Fire Alarm') typeColor = rgb(0.94, 0.27, 0.27);
        else if (component.symbolType === 'Communications') typeColor = rgb(0.23, 0.51, 0.95);
        
        summaryPage.drawText(component.symbolType, {
          x: 50,
          y: yPosition,
          size: 10,
          font: font,
          color: typeColor
        });
        
        summaryPage.drawText(component.componentName, {
          x: 200,
          y: yPosition,
          size: 10,
          font: font
        });
        
        summaryPage.drawText(String(count), {
          x: 460,
          y: yPosition,
          size: 10,
          font: boldFont
        });
        
        summaryPage.drawText(pages, {
          x: 510,
          y: yPosition,
          size: 8,
          font: font,
          color: rgb(0.5, 0.5, 0.5)
        });
        
        yPosition -= 20;
      });
      
      // Total count
      yPosition -= 10;
      summaryPage.drawLine({
        start: { x: 50, y: yPosition },
        end: { x: 560, y: yPosition },
        thickness: 1,
        color: rgb(0, 0, 0)
      });
      yPosition -= 20;
      
      summaryPage.drawText('TOTAL', {
        x: 200,
        y: yPosition,
        size: 12,
        font: boldFont
      });
      
        summaryPage.drawText(String(numberedAnnotations.length), {
          x: 460,
          y: yPosition,
          size: 12,
          font: boldFont
        });
      }
      } else {
        console.log('⚠️ No annotations for summary page, skipping it');
      }
      
      // ========================================
      // PART 2: Screenshot Each Page with Annotations
      // ========================================
      
      if (!numPages) {
        console.error('No pages to capture');
        return;
      }
      
      console.log(`📊 Capturing screenshots of ${numPages} pages...`);
      
      // Save current page to restore later
      const currentPage = pageNumber;
      
      // Capture each page
      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        console.log(`📊 Capturing page ${pageNum}/${numPages}...`);
        
        // Switch to this page
        setPageNumber(pageNum);
        
        // Wait for the page to render
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Get the annotation canvas
        const annotationCanvas = canvasRef.current;
        if (!annotationCanvas) {
          console.error(`No canvas found for page ${pageNum}`);
          continue;
        }
        
        console.log(`📊 Page ${pageNum}: Canvas size ${annotationCanvas.width}x${annotationCanvas.height}`);
        
        // Get the PDF page and render it to a temporary canvas
        const pdfPage = await pdfDocument.getPage(pageNum);
        const rotation = pageRotations[pageNum] || 0;
        
        // Match the viewport to the current canvas size
        const scale = annotationCanvas.width / pdfPage.getViewport({ scale: 1.0, rotation }).width;
        const viewport = pdfPage.getViewport({ scale, rotation });
        
        // Create a temporary canvas for the PDF
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = annotationCanvas.width;
        tempCanvas.height = annotationCanvas.height;
        const tempCtx = tempCanvas.getContext('2d')!;
        
        // Render PDF to temp canvas
        await pdfPage.render({
          canvasContext: tempCtx,
          viewport: viewport
        }).promise;
        
        // Now draw the annotations on top
        tempCtx.drawImage(annotationCanvas, 0, 0);
        
        console.log(`📊 Page ${pageNum}: Combined PDF + annotations`);
        
        // Convert combined canvas to image
        const imageData = tempCanvas.toDataURL('image/png');
        const imageBytes = await fetch(imageData).then(res => res.arrayBuffer());
        
        // Embed in PDF
        const image = await pdfDoc.embedPng(imageBytes);
        const page = pdfDoc.addPage([tempCanvas.width, tempCanvas.height]);
        page.drawImage(image, {
          x: 0,
          y: 0,
          width: tempCanvas.width,
          height: tempCanvas.height,
        });
        
        console.log(`📊 Page ${pageNum}: Added to PDF (${tempCanvas.width}x${tempCanvas.height})`);
      }
      
      // Restore original page
      setPageNumber(currentPage);
      
      // Save PDF
      console.log(`📊 Saving PDF with ${pdfDoc.getPageCount()} pages...`);
      const modifiedPdfBytes = await pdfDoc.save();
      console.log(`📊 PDF bytes generated: ${modifiedPdfBytes.length} bytes`);
      const blob = new Blob([new Uint8Array(modifiedPdfBytes)], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${file.name.replace('.pdf', '')}_takeoff_report.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      
      console.log('✅ Takeoff report generated!');
    } catch (error) {
      console.error('Error generating takeoff report:', error);
    }
  };

  function onDocumentLoadSuccess(pdf: any) {
    setPdfDocument(pdf);
    setNumPages(pdf.numPages);
    setSpecsNumPages(pdf.numPages); // Same document for specs mode
    activeRenderTokenRef.current += 1;
    shouldCenterAfterRenderRef.current = true;
    
    // Restore saved page number instead of always using 1
    if (estimationId && file?.name) {
      const storageKey = `page-number-${estimationId}-${file.name}`;
      const savedPage = localStorage.getItem(storageKey);
      if (savedPage) {
        const pageNum = parseInt(savedPage, 10);
        if (pageNum >= 1 && pageNum <= pdf.numPages) {
          setPageNumber(pageNum);
          console.log(`📖 Restored saved page number: ${pageNum}`);
        } else {
          setPageNumber(1);
        }
      } else {
        setPageNumber(1);
      }
    } else {
      setPageNumber(1);
    }
    
    // Update context if available
    if (hasContext && blueprintContext?.setTotalPages) {
      blueprintContext.setTotalPages(pdf.numPages);
    }
    
  }

  function onPageLoadSuccess(page: any) {
    // Parse PDF as SVG for element detection mode (DISABLED - feature not in use)
    // This was causing slow page loads by parsing elements that are never used
    // TODO: Re-enable if element detection mode is implemented
    // parsePDFAsSVG(page).then(elements => {
    //   setPdfVectorElements(elements);
    // });
    
    // Calculate scale to achieve 8000px on longest edge for maximum quality
    if (canvasRef.current) {
      // Apply PDF's native rotation if no manual rotation has been set
      // PDFs can have metadata rotation (0, 90, 180, 270) that should be respected
      const nativeRotation = page.rotate || 0;
      
      // Check if we have a saved rotation for this page
      const savedRotation = pageRotations[pageNumber];
      
      // If no saved rotation exists, use the PDF's native rotation
      if (savedRotation === undefined && nativeRotation !== 0) {
        console.log(`📄 Page ${pageNumber} has native rotation: ${nativeRotation}°`);
        setPageRotations(prev => {
          const updatedRotations = {
            ...prev,
            [pageNumber]: nativeRotation
          };
          
          // Save to localStorage for persistence
          if (estimationId && file?.name) {
            try {
              const storageKey = `page-rotations-${estimationId}-${file.name}`;
              localStorage.setItem(storageKey, JSON.stringify(updatedRotations));
            } catch { /* quota exceeded — ignore */ }
          }
          
          return updatedRotations;
        });
      }
      
      // Get current rotation for this page (either saved or native)
      const rotation = savedRotation !== undefined ? savedRotation : nativeRotation;
      
      // Two-pass rendering: fast low-res first, high-res upgrade in background
      const baseViewport = page.getViewport({ scale: 1, rotation });
      const longestSide = Math.max(baseViewport.width, baseViewport.height);

      // Cancel any in-progress/delayed high-res render from a previous page
      highResRenderRef.current.cancel = true;
      if (highResUpgradeTimeoutRef.current !== null) {
        window.clearTimeout(highResUpgradeTimeoutRef.current);
        highResUpgradeTimeoutRef.current = null;
      }
      highResRenderRef.current = { cancel: false };
      setIsHighResReady(!ENABLE_PHASE_2_UPGRADE);
      setIsUpgradingRes(false);

      // Store the page object for the background upgrade
      pdfPageRef.current = page;

      // Phase 1: Low-res for fast initial display
      const renderScale = FAST_RES / longestSide;
      setPdfRenderScale(renderScale);
      
      console.log('📐 Phase 1 (fast render):', {
        rotation,
        renderScale,
        targetResolution: `${FAST_RES}px on longest edge`,
        baseViewport: { width: baseViewport.width, height: baseViewport.height },
        longestSide,
        expectedDimensions: {
          width: Math.round(baseViewport.width * renderScale),
          height: Math.round(baseViewport.height * renderScale)
        }
      });
      
      // Get viewport for page size (use FULL_RES for context so annotations map correctly at final resolution)
      const viewport = page.getViewport({ scale: renderScale, rotation });
      
      // Update context if available
      if (hasContext && blueprintContext?.setPageSize) {
        blueprintContext.setPageSize({
          width: viewport.width,
          height: viewport.height
        });
      }
      
      // DISABLED: Base64 conversion that was creating 12MB images on every page load
      // This data was never actually used anywhere in the codebase
      // If needed in the future, this should be done on-demand, not eagerly
      // Original code created massive performance overhead for unused feature
      // const canvas = document.createElement('canvas');
      // page.render(...).then(() => { /* base64 conversion */ });
    }
  }

  // Phase 2: Background high-res upgrade
  const upgradeToHighRes = useCallback(async (cancelToken: { cancel: boolean }, renderToken: number) => {
    if (!ENABLE_PHASE_2_UPGRADE) return;
    const page = pdfPageRef.current;
    if (!page || cancelToken.cancel || renderToken !== activeRenderTokenRef.current) return;

    setIsUpgradingRes(true);

    try {
      const rotation = pageRotations[pageNumber] || 0;
      const baseViewport = page.getViewport({ scale: 1, rotation });
      const longestSide = Math.max(baseViewport.width, baseViewport.height);
      const fullScale = FULL_RES / longestSide;

      const effectiveDPR = Math.min(window.devicePixelRatio, DPR_CAP);
      const viewport = page.getViewport({ scale: fullScale * effectiveDPR, rotation });

      const offscreen = document.createElement('canvas');
      offscreen.width = viewport.width;
      offscreen.height = viewport.height;
      const ctx = offscreen.getContext('2d');
      if (!ctx) return;

      console.log(`🔄 Phase 2: Rendering high-res ${offscreen.width}x${offscreen.height} in background...`);
      await page.render({ canvasContext: ctx, viewport }).promise;

      if (cancelToken.cancel || renderToken !== activeRenderTokenRef.current) {
        console.log('⏹️ High-res render cancelled (page changed)');
        return;
      }

      const pdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
      if (pdfCanvas) {
        // Preserve on-screen size from phase 1 so quality upgrade doesn't change visual zoom.
        const previousDisplayWidth = pdfCanvas.clientWidth || parseFloat(getComputedStyle(pdfCanvas).width) || 0;
        const previousDisplayHeight = pdfCanvas.clientHeight || parseFloat(getComputedStyle(pdfCanvas).height) || 0;

        pdfCanvas.width = offscreen.width;
        pdfCanvas.height = offscreen.height;

        const displayWidth = previousDisplayWidth > 0 ? previousDisplayWidth : (offscreen.width / effectiveDPR);
        const displayHeight = previousDisplayHeight > 0 ? previousDisplayHeight : (offscreen.height / effectiveDPR);
        pdfCanvas.style.width = `${displayWidth}px`;
        pdfCanvas.style.height = `${displayHeight}px`;

        pdfCanvas.getContext('2d')?.drawImage(offscreen, 0, 0);

        if (canvasRef.current) {
          canvasRef.current.width = offscreen.width;
          canvasRef.current.height = offscreen.height;
          canvasRef.current.style.width = `${displayWidth}px`;
          canvasRef.current.style.height = `${displayHeight}px`;

          setAnnotations(prev => prev.map(ann => rescaleAnnotation(ann, offscreen.width, offscreen.height)));
          setCurrentCanvasWidth(offscreen.width);
          setCurrentCanvasHeight(offscreen.height);
        }

        setIsHighResReady(true);
        if (shouldCenterAfterRenderRef.current && renderToken === activeRenderTokenRef.current) {
          requestAnimationFrame(() => {
            if (renderToken !== activeRenderTokenRef.current) return;
            centerView(DEFAULT_VIEW_SCALE);
            shouldCenterAfterRenderRef.current = false;
          });
        }
        console.log(`✅ Phase 2 complete: ${offscreen.width}x${offscreen.height}`);
      }
    } catch (err) {
      console.error('❌ High-res upgrade failed:', err);
    } finally {
      setIsUpgradingRes(false);
    }
  }, [pageNumber, pageRotations, rescaleAnnotation, FULL_RES, DPR_CAP, centerView, ENABLE_PHASE_2_UPGRADE]);

  // Called when Phase 1 (low-res) render completes
  const handlePhase1Complete = useCallback((renderedPage?: any) => {
    if (renderedPage?.pageNumber && renderedPage.pageNumber !== pageNumber) {
      return;
    }
    const renderToken = activeRenderTokenRef.current;
    console.log('✅ Phase 1 rendered (fast)');
    setIsPDFRendered(true);
    setIsCanvasSynced(true);

    // Center after phase 1 for fast feedback; final centering is confirmed in phase 2.
    if (shouldCenterAfterRenderRef.current) {
      requestAnimationFrame(() => {
        if (renderToken !== activeRenderTokenRef.current) return;
        centerView(DEFAULT_VIEW_SCALE);
      });
    }

    if (ENABLE_PHASE_2_UPGRADE && !isHighResReady && pdfPageRef.current) {
      const cancelToken = highResRenderRef.current;
      if (highResUpgradeTimeoutRef.current !== null) {
        window.clearTimeout(highResUpgradeTimeoutRef.current);
      }
      highResUpgradeTimeoutRef.current = window.setTimeout(() => {
        highResUpgradeTimeoutRef.current = null;
        if (cancelToken.cancel || renderToken !== activeRenderTokenRef.current) return;
        void upgradeToHighRes(cancelToken, renderToken);
      }, HIGH_RES_UPGRADE_DELAY_MS);
    }
  }, [isHighResReady, upgradeToHighRes, centerView, pageNumber, HIGH_RES_UPGRADE_DELAY_MS, ENABLE_PHASE_2_UPGRADE]);

  useEffect(() => {
    return () => {
      if (highResUpgradeTimeoutRef.current !== null) {
        window.clearTimeout(highResUpgradeTimeoutRef.current);
        highResUpgradeTimeoutRef.current = null;
      }
    };
  }, []);

  // Get canvas screenshot as base64 (combines PDF and annotations)
  const getCanvasScreenshot = useCallback((): string | null => {
    const canvas = canvasRef.current;
    const pdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;

    if (!canvas || !pdfCanvas) {
      console.warn('⚠️ Canvas not available for screenshot');
      return null;
    }

    console.log('📸 Canvas dimensions for screenshot:', {
      annotationCanvas: { width: canvas.width, height: canvas.height },
      pdfCanvas: { width: pdfCanvas.width, height: pdfCanvas.height }
    });

    // Create a new canvas that combines PDF and annotations
    const combinedCanvas = document.createElement('canvas');
    
    // Canvas dimensions already match the rotated PDF
    combinedCanvas.width = canvas.width;
    combinedCanvas.height = canvas.height;

    const ctx = combinedCanvas.getContext('2d');
    if (!ctx) return null;
    
    // No rotation transform needed - PDF is already rotated

    // Check if we need to scale the PDF canvas to match annotation canvas
    if (pdfCanvas.width !== canvas.width || pdfCanvas.height !== canvas.height) {
      console.log('⚠️ Canvas size mismatch, scaling PDF to match annotation canvas');
      ctx.drawImage(pdfCanvas, 0, 0, pdfCanvas.width, pdfCanvas.height, 0, 0, canvas.width, canvas.height);
    } else {
      // Draw the PDF page first
      ctx.drawImage(pdfCanvas, 0, 0);
    }

    // Draw the annotations on top
    ctx.drawImage(canvas, 0, 0);

    // No restore needed - no rotation transform applied

    // Convert to base64
    const dataUrl = combinedCanvas.toDataURL('image/png');
    const base64 = dataUrl.split(',')[1];
    console.log('📸 Canvas screenshot captured:', {
      bytes: base64?.length,
      dimensions: { width: combinedCanvas.width, height: combinedCanvas.height }
    });
    return base64;
  }, [pageNumber, pageRotations]);
  
  // Function to get current page as base64
  const getCurrentPageAsBase64 = useCallback(() => {
    const pageElement = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
    if (pageElement) {
      try {
        const dataUrl = pageElement.toDataURL('image/png');
        const base64 = dataUrl.split(',')[1];
        console.log('🖼️ PDFAnnotator: Manual base64 extraction', {
          hasCanvas: true,
          base64Length: base64?.length || 0
        });
        return base64;
      } catch (error) {
        console.error('Error getting canvas as base64:', error);
      }
    } else {
      console.warn('⚠️ PDFAnnotator: No PDF canvas found');
    }
    return null;
  }, []);

  // Function to navigate to a specific page (resets zoom + recenters, matching toolbar behavior)
  const navigateToPage = useCallback((targetPage: number) => {
    if (targetPage >= 1 && targetPage <= (numPages || 1)) {
      console.log(`📖 PDFAnnotator: Navigating to page ${targetPage}`);
      activeRenderTokenRef.current += 1;
      shouldCenterAfterRenderRef.current = true;
      setPageNumber(targetPage);
      setScale(DEFAULT_VIEW_SCALE);
      return true;
    } else {
      console.warn(`⚠️ PDFAnnotator: Invalid page number ${targetPage}. Valid range: 1-${numPages}`);
      return false;
    }
  }, [numPages]);

  // Helper function to render any page at any scale completely off-screen
  // This does NOT affect what the user is currently viewing
  const renderPageOffScreen = useCallback(async (
    targetPage: number,
    maxWidth: number
  ): Promise<HTMLCanvasElement> => {
    if (!pdfDocument) {
      throw new Error('PDF not loaded');
    }

    console.log(`📸 Rendering page ${targetPage} off-screen at max width ${maxWidth}px...`);

    // IMPORTANT: Fetch the target page directly from pdfDocument
    // This does NOT affect what the user is currently viewing
    const pdfPage = await pdfDocument.getPage(targetPage);
    const viewport = pdfPage.getViewport({ scale: 1.0 });

    // Calculate scale to achieve target width
    const scale = maxWidth / viewport.width;
    const scaledViewport = pdfPage.getViewport({ scale });

    // Create a brand new off-screen canvas
    // This canvas is never attached to the DOM
    const offScreenCanvas = document.createElement('canvas');
    offScreenCanvas.width = scaledViewport.width;
    offScreenCanvas.height = scaledViewport.height;

    const context = offScreenCanvas.getContext('2d')!;

    // Render the page to our off-screen canvas
    await pdfPage.render({
      canvasContext: context,
      viewport: scaledViewport
    }).promise;

    console.log(`✅ Rendered page ${targetPage} off-screen: ${offScreenCanvas.width}x${offScreenCanvas.height}`);

    return offScreenCanvas;
  }, [pdfDocument]);

  useEffect(() => {
    countAllPageThumbnailsRef.current = countAllPageThumbnails;
  }, [countAllPageThumbnails]);

  // Generate low-quality page thumbnails for count-all pre-detection review animation.
  useEffect(() => {
    if (!(isCountingAll || showMultiComponentReviewModal)) return;
    if (componentPagesByName.size === 0) return;

    const powerPages = new Set<number>();
    const lightingPages = new Set<number>();
    Array.from(componentPagesByName.entries()).forEach(([componentKey, pages]) => {
      const matchingDetection = multiComponentDetections.find((det) => det.componentName === componentKey);
      const isLightingComponent = Boolean(matchingDetection?.isLighting || componentKey === '__LIGHTING_GROUP__');
      (pages || []).forEach((rawPage) => {
        const page = Math.floor(Number(rawPage));
        if (!Number.isFinite(page) || page <= 0) return;
        if (isLightingComponent) {
          lightingPages.add(page);
        } else {
          powerPages.add(page);
        }
      });
    });

    const sortedPowerPages = Array.from(powerPages).sort((a, b) => a - b);
    const sortedLightingPages = Array.from(lightingPages)
      .filter((page) => !powerPages.has(page))
      .sort((a, b) => a - b);
    const pagesToRender = [...sortedPowerPages, ...sortedLightingPages].slice(0, 4);
    if (pagesToRender.length === 0) return;

    let cancelled = false;

    const renderOnePage = async (page: number) => {
      if (countAllPageThumbnailsRef.current.has(page)) return;
      if (countAllThumbnailRenderInFlightRef.current.has(page)) return;

      countAllThumbnailRenderInFlightRef.current.add(page);
      try {
        const canvas = await renderPageOffScreen(page, 520);
        const thumbnail = canvas.toDataURL('image/jpeg', 0.5);
        if (cancelled) return;
        setCountAllPageThumbnails((prev) => {
          const existing = prev.get(page);
          if (existing === thumbnail) return prev;
          const next = new Map(prev);
          next.set(page, thumbnail);
          countAllPageThumbnailsRef.current = next;
          return next;
        });
      } catch (error) {
        console.warn(`Failed to render count-all thumbnail for page ${page}:`, error);
      } finally {
        countAllThumbnailRenderInFlightRef.current.delete(page);
      }
    };

    const generate = () => {
      void Promise.allSettled(pagesToRender.map((page) => renderOnePage(page)));
    };

    void generate();

    return () => {
      cancelled = true;
    };
  }, [
    componentPagesByName,
    isCountingAll,
    multiComponentDetections,
    renderPageOffScreen,
    showMultiComponentReviewModal,
  ]);

  // Expose state and methods to parent components via ref
  useImperativeHandle(ref, () => {
    // Get the actual canvas dimensions from the PDF page
    const pdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
    const currentCanvasWidth = pdfCanvas?.width || canvasRef.current?.width || 0;
    const currentCanvasHeight = pdfCanvas?.height || canvasRef.current?.height || 0;
    
    // console.log('🔍 Exposing canvas dimensions to parent:', {
    //   width: currentCanvasWidth,
    //   height: currentCanvasHeight,
    //   hasCanvas: !!canvasRef.current,
    //   hasPdfCanvas: !!pdfCanvas
    // });
    
    return {
      annotations,
      getAnnotations: () => annotations,  // ALWAYS RETURN FRESH STATE!
      currentPage: pageNumber,
      totalPages: numPages,
      canvasWidth: currentCanvasWidth,
      canvasHeight: currentCanvasHeight,
      getCurrentPageAsBase64,
      getCanvasScreenshot,
      navigateToPage,
      handleStartLightingScheduleExtraction,
      // Trigger autocount with saved reference image
      triggerAutocount: async (componentName: string, componentColor: string, savedReferenceImage: string, isLighting?: boolean) => {
        console.log('🤖 Triggering autocount from saved reference:', componentName);
        
        setPendingAutocountComponent({
          name: componentName,
          color: componentColor,
          isLighting: isLighting ?? false
        });
        setReferenceAreaImage(savedReferenceImage);
        
        // Run autocount directly
        await handleConfirmAutocount();
      },
      navigateToSpecsPage: (page: number, boundingBox?: { top: number; left: number; width: number; height: number }) => {
        console.log('🎯 navigateToSpecsPage called:', { page, boundingBox, currentPage: specsPageNumber });
        
        // Clear any existing highlight timeout
        if (highlightTimeoutRef.current) {
          clearTimeout(highlightTimeoutRef.current);
          highlightTimeoutRef.current = null;
        }
        
        // Update the last highlight page ref to prevent clearing
        lastHighlightPageRef.current = page;
        
        setSpecsPageNumber(page);
        setHighlightBoundingBox(boundingBox || null);
        console.log('📦 Set highlightBoundingBox to:', boundingBox);
        
        // Auto-clear highlight after 30 seconds
        if (boundingBox) {
          highlightTimeoutRef.current = setTimeout(() => {
            console.log('⏰ Clearing bounding box highlight');
            setHighlightBoundingBox(null);
            highlightTimeoutRef.current = null;
          }, 2500);
        }
      },
      navigateToNecaPage: (page: number) => {
        console.log('📖 navigateToNecaPage called:', { page });
        // For NECA manual, navigate to the page using NECA-specific state
        setNecaPageNumber(page);
        // Scroll to top of viewer to show the page
        const pdfContainer = document.querySelector('.pdf-container');
        if (pdfContainer) {
          pdfContainer.scrollTop = 0;
        }
      },
      getBaseScreenshot: async (targetPage: number) => {
        // CRITICAL: Never navigate or affect user's current view
        // Render entirely off-screen using pdfDocument directly
        const canvas = await renderPageOffScreen(targetPage, 1092);
        return canvas.toDataURL('image/png').split(',')[1];
      },
      getMediumResolutionScreenshot: async (targetPage: number) => {
        // CRITICAL: Never navigate or affect user's current view
        // Render entirely off-screen using pdfDocument directly
        const canvas = await renderPageOffScreen(targetPage, 8000);
        return canvas.toDataURL('image/png').split(',')[1];
      },
      getFullResolutionScreenshot: async (targetPage: number) => {
        // CRITICAL: Never navigate or affect user's current view
        // Render entirely off-screen using pdfDocument directly
        const canvas = await renderPageOffScreen(targetPage, 16000);
        return canvas.toDataURL('image/png').split(',')[1];
      },
      addAnnotation: (annotation: Annotation) => {
        setAnnotations(prev => [...prev, annotation]);
        
        // Trigger pop-in animation for the new annotation
        if (annotation.id) {
          setAnimatingAnnotations(prev => new Set(prev).add(annotation.id));
          setAnnotationTimestamps(prev => new Map(prev).set(annotation.id, Date.now()));
          
          // Remove from animating set after animation completes (700ms)
          setTimeout(() => {
            setAnimatingAnnotations(prev => {
              const next = new Set(prev);
              next.delete(annotation.id);
              return next;
            });
            // Clean up timestamp after a bit longer
            setTimeout(() => {
              setAnnotationTimestamps(prev => {
                const next = new Map(prev);
                next.delete(annotation.id);
                return next;
              });
            }, 100);
          }, 700);
        }
        // Redraw will happen automatically due to annotations state change
      },
      removeAnnotation: async (id: string) => {
        // Check if this is a wire run annotation
        if (id.startsWith(WIRE_RUN_ANNOTATION_PREFIX)) {
          const wireRunDbId = getWireRunDbIdFromAnnotationId(id);
          if (!wireRunDbId) {
            console.log('Skipping wire run DB delete for unsaved annotation:', id);
          } else {
          console.log('🗑️ Deleting wire run from database:', wireRunDbId);
          
          try {
            const { error } = await supabaseClient
              .from('wire_runs')
              .delete()
              .eq('id', wireRunDbId);
            
            if (error) {
              console.error('Error deleting wire run from database:', error);
            } else {
              console.log('✅ Wire run deleted from database');
              // Trigger refresh of WireRunsPanel
              bumpWireRunRefresh();
            }
          } catch (err) {
            console.error('Error deleting wire run:', err);
          }
          }
        }
        
        const currentAnnotations = annotationsRef.current;
        const deletedIds = new Set<string>([id]);
        currentAnnotations.forEach((annotation) => {
          if (annotation.type !== 'piping') return;
          const startDeleted = annotation.startAnnotationId && deletedIds.has(annotation.startAnnotationId);
          const endDeleted = annotation.endAnnotationId && deletedIds.has(annotation.endAnnotationId);
          if (startDeleted || endDeleted) deletedIds.add(annotation.id);
        });

        const nextAnnotations = currentAnnotations.filter(a => !deletedIds.has(a.id));
        const deletedComponentTypes = Array.from(new Set(
          currentAnnotations
            .filter((annotation) => deletedIds.has(annotation.id))
            .map((annotation) => annotation.component_type)
            .filter(Boolean)
        )) as string[];
        annotationsRef.current = nextAnnotations;
        setAnnotations(nextAnnotations);
        void persistDeletedAnnotations(Array.from(deletedIds), deletedComponentTypes);
      },
      setAnnotations: (newAnnotations: Annotation[]) => {
        setAnnotations(newAnnotations);
      },
      saveAnnotations: persistAnnotations,
      clearAnnotations: clearAll,
      loadAnnotations,
      // Trigger autocount with saved reference image
      triggerAutocountWithReference: async (componentName: string, componentColor: string, savedReferenceImage: string, isLighting?: boolean) => {
        console.log('🤖 Triggering autocount from saved reference:', componentName);
        
        setPendingAutocountComponent({
          name: componentName,
          color: componentColor,
          isLighting: isLighting ?? false
        });
        setReferenceAreaImage(savedReferenceImage);
        
        // Small delay to ensure state is set, then trigger
        setTimeout(async () => {
          await handleConfirmAutocount();
        }, 100);
      },
      setViewAllMode: (_enabled: boolean) => {
        setEditAllMode(true);
      },
      viewAllMode: editAllMode,
      HIGHLIGHT_ALL,
      highlightComponentType: (componentType: string | null) => {
        if (editAllMode) {
          setHighlightedComponentType(HIGHLIGHT_ALL);
          return;
        }
        setHighlightedComponentType(componentType);
        if (componentType && componentType !== HIGHLIGHT_ALL) {
          const annotationsToAnimate = annotations
            .filter(a => a.component_type === componentType)
            .map(a => a.id);
          setAnimatingAnnotations(new Set(annotationsToAnimate));

          setTimeout(() => {
            setAnimatingAnnotations(new Set());
          }, 800);
        }
      },
      openAddNewComponentModal: () => {
        console.log('🆕 Opening Add New Component modal from sidebar');
        setNewComponentName('');
        setCountingModalMode('manual'); // Default to manual/add-to-key mode
        setIsAddingNewOnly(true);
        setIsAddingNewOnlyScanMode(false);
        setShowNewComponentModal(true);
      },
      setCurrentTool: (tool: AnnotationTool) => {
        setCurrentTool(tool);
        console.log(`🔧 Tool changed to: ${tool}`);
      },
      // Wire run methods
      startWireRunMode,
      startWireRunWithType,
      isWireRunMode,
      hasCalibration: !!calibrationData[pageNumber],
      calibrationData,
      wireRunRefreshTrigger,
      startScaleCalibration,
      openCalibrationDropdown: () => setShowCalibrationDropdown(true),
      selectWireRunAnnotation: (runId: string, targetPageNumber?: number) => {
        const annotationId = `${WIRE_RUN_ANNOTATION_PREFIX}${runId}`;
        setIsWireRunMode(false);
        setSelectedWireType(null);
        setHandDrawnEnabled(false);
        if (targetPageNumber && targetPageNumber !== pageNumber) {
          setPageNumber(targetPageNumber);
        }
        setCurrentTool('select');
        setSelectedHandDrawnStrokes(new Set());
        setSelectedAnnotation(annotationId);
        setSelectedAnnotations(new Set([annotationId]));
        setDragStart(null);
        setWireRunExtendMode(null);
        setWireRunExtendPreviewPoint(null);
        setWireRunVertexDrag(null);
      },
      deleteWireRunAnnotation: (runId: string, _pageNumber: number) => {
        // Delete the wire run annotation that matches this database ID
        // Annotation ID format: `wire-run-${databaseId}`
        const annotationId = `${WIRE_RUN_ANNOTATION_PREFIX}${runId}`;
        console.log('🗑️ Deleting wire run annotation:', annotationId);

        const currentAnnotations = annotationsRef.current;
        const deletedComponentTypes = Array.from(new Set(
          currentAnnotations
            .filter((annotation) => annotation.id === annotationId)
            .map((annotation) => annotation.component_type)
            .filter(Boolean)
        )) as string[];

        const nextAnnotations = currentAnnotations.filter(a => a.id !== annotationId);
        annotationsRef.current = nextAnnotations;
        setAnnotations(nextAnnotations);
        void persistDeletedAnnotations([annotationId], deletedComponentTypes);
        
        // Trigger a redraw after state update (via setTimeout to let React batch the update)
        setTimeout(() => {
          drawAnnotations();
        }, 50);
      },
      currentDrawingFileId: file?.id,
      startCountingComponents: (components: Array<{ color: string; name: string; symbol: string; referenceImage: string; symbolType?: string; componentType?: string; subscript?: string; description?: string }>) => {
        startCountingComponents(components);
      },
      startCountingComponentsParallel: (components: Array<{ color: string; name: string; symbol: string; referenceImage: string; symbolType?: string; componentType?: string; subscript?: string; description?: string }>) => {
        startCountingComponents(components, { parallel: true });
      },
      startFullTakeoff: () => {
        resetGuidedTakeoffFlow();
        setShowTakeoffOnboardingStartModal(true);
      },
    };
  }, [annotations, pageNumber, numPages, scale, containerRef, getCurrentPageAsBase64, navigateToPage, renderPageOffScreen, persistAnnotations, persistDeletedAnnotations, clearAll, loadAnnotations, startWireRunMode, startWireRunWithType, isWireRunMode, calibrationData, wireRunRefreshTrigger, startScaleCalibration, file?.id, drawAnnotations, startCountingComponents, WIRE_RUN_ANNOTATION_PREFIX, getWireRunDbIdFromAnnotationId, bumpWireRunRefresh, resetGuidedTakeoffFlow, editAllMode, HIGHLIGHT_ALL]);
  
  // Update context with page changes
  useEffect(() => {
    if (hasContext && blueprintContext?.setCurrentPage) {
      blueprintContext.setCurrentPage(pageNumber);
    }
  }, [pageNumber, hasContext, blueprintContext]);
  
  // Update context with annotations
  useEffect(() => {
    if (hasContext && blueprintContext?.setAnnotations) {
      blueprintContext.setAnnotations(annotations);
    }
  }, [annotations, hasContext, blueprintContext]);

  useEffect(() => {
    annotationsRef.current = annotations;
  }, [annotations]);

  useEffect(() => {
    annotationsLoadedRef.current = annotationsLoaded;
  }, [annotationsLoaded]);

  const handleViewportMouseLeave = useCallback(() => {
    handleMouseUp();
    setHoverCursor('default');
    setHoveredAnnotation(null);
    setHoveredAnnotationPoint(null);
  }, [handleMouseUp]);

  const hoveredAnnotationType = useMemo(() => {
    if (!hoveredAnnotation) return null;
    return annotations.find((annotation) => annotation.id === hoveredAnnotation)?.type || null;
  }, [annotations, hoveredAnnotation]);

  const drawingOptions = drawingFiles || [];
  const drawingSelectValue =
    currentDrawingFile?.id || (drawingOptions.length > 0 ? drawingOptions[0].id : '');

  return (
    <div className="pdf-annotator-container">
      {/* Locked Lighting Extraction Modal */}
      {isExtractingLightingSchedule && !showLightingScheduleConfirmModal && (
        <LightingExtractionIndicator
          lightingScheduleSnapshot={lightingExtractionAnimationSnapshot || lightingScheduleSnapshot}
        />
      )}

      {/* Bottom Nav Pill — mode selector, page navigation, zoom, rotate */}
      <BottomModeToolbar
        activeMode={activeMode}
        showModeDropdown={showModeDropdown}
        setShowModeDropdown={setShowModeDropdown}
        modeDropdownRef={modeDropdownRef}
        onModeChange={onModeChange}
        specsFiles={specsFiles}
        currentSpecsFile={currentSpecsFile}
        onSpecsFileChange={onSpecsFileChange}
        specsPageNumber={specsPageNumber}
        specsNumPages={specsNumPages}
        specsScale={specsScale}
        setSpecsPageNumber={setSpecsPageNumber}
        setSpecsScale={setSpecsScale}
        rotateSpecsPage={rotateSpecsPage}
        necaPageNumber={necaPageNumber}
        necaNumPages={necaNumPages}
        necaScale={necaScale}
        setNecaPageNumber={setNecaPageNumber}
        setNecaScale={setNecaScale}
        pageNumber={pageNumber}
        numPages={numPages}
        pageInputValue={pageInputValue}
        setPageInputValue={setPageInputValue}
        setPageNumber={setPageNumber}
        scale={scale}
        setScale={setScale}
        zoomOutFromToolbar={zoomOutFromToolbar}
        zoomInFromToolbar={zoomInFromToolbar}
        rotateCurrentPage={rotateCurrentPage}
        activeRenderTokenRef={activeRenderTokenRef}
        shouldCenterAfterRenderRef={shouldCenterAfterRenderRef}
        defaultViewScale={DEFAULT_VIEW_SCALE}
      />

      {/* Right Tools Pill — annotation tools, chat */}
      <div className="pdf-annotator-tools-right">
        {activeMode === 'draw' && (
          <>
            <TooltipButton
              className={`toolbar-btn ${currentTool === 'select' ? 'active' : ''}`}
              onClick={() => setCurrentTool('select')}
              tooltip="Select"
            >
              <MousePointer size={16} />
            </TooltipButton>
            <TooltipButton
              className={`toolbar-btn ${isWireRunMode ? 'active wire-run-active' : ''}`}
              onClick={() => startWireRunMode()}
              tooltip="Wire/Conduit Runs"
            >
              <Cable size={16} />
            </TooltipButton>
            {/*
            <TooltipButton
              className={`toolbar-btn ${isSquareFootageMode ? 'active' : ''}`}
              onClick={() => {
                if (isSquareFootageMode) {
                  setIsSquareFootageMode(false);
                  setCurrentTool('select');
                } else {
                  startSquareFootageMode();
                }
              }}
              tooltip="Square Footage"
            >
              <Scan size={16} />
            </TooltipButton>
            */}
            <div 
              ref={(el) => { toolDropdownRefs.current['hand-dimension'] = el; }}
              style={{ position: 'relative' }}
            >
              <TooltipButton
                className={`toolbar-btn ${isCalibrating ? 'active' : ''}`}
                onClick={startScaleCalibration}
                tooltip="Set Scale / Calibration"
              >
                <Ruler size={16} />
              </TooltipButton>
            </div>
          </>
        )}

        {/*
        {activeMode === 'draw' && <div className="tools-right-divider" />}
        <TooltipButton
          className={`toolbar-btn chat-toggle-btn-circle ${!isAgentChatCollapsed ? 'active' : ''}`}
          onClick={onChatToggle}
          tooltip={isAgentChatCollapsed ? 'Open AI Assistant' : 'Close AI Assistant'}
        >
          <MessageCircle size={18} />
          {hasUnreadChat && <span className="chat-unread-dot" />}
        </TooltipButton>
        */}
      </div>
      
      {/* Show placeholder when no file - but not in estimate or rfi mode */}
      {!file && activeMode !== 'estimate' && activeMode !== 'rfi' && (
        <div className="pdf-annotator-empty" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ color: 'var(--text-placeholder)', fontSize: '14px' }}>No file selected</p>
        </div>
      )}
      
      {/* Specs Mode - Transitioning state */}
      {activeMode === 'specs' && isTransitioning && (
        <div className="pdf-annotator-wrapper" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          background: 'var(--bg-primary)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div className="loading-spinner" style={{ 
              width: '40px', 
              height: '40px', 
              border: '4px solid var(--border-color)',
              borderTop: '4px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }} />
            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>Loading Specifications...</p>
          </div>
        </div>
      )}

      {/* Specs Mode - Completely separate viewer */}
      {file && activeMode === 'specs' && !isTransitioning && (
        <SpecsModeViewer
          file={file}
          estimationId={estimationId}
          specsPageNumber={specsPageNumber}
          specsNumPages={specsNumPages}
          specsScale={specsScale}
          specsPageRotations={specsPageRotations}
          setSpecsNumPages={setSpecsNumPages}
          setSpecsPageNumber={setSpecsPageNumber}
          setSpecsPageRotations={setSpecsPageRotations}
          highlightBoundingBox={highlightBoundingBox}
          pdfOptions={pdfOptions}
        />
      )}

      {/* NECA Mode - Transitioning state */}
      {activeMode === 'neca' && isTransitioning && (
        <div className="pdf-annotator-wrapper" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          background: 'var(--bg-primary)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div className="loading-spinner" style={{ 
              width: '40px', 
              height: '40px', 
              border: '4px solid var(--border-color)',
              borderTop: '4px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }} />
            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>Loading NECA Manual...</p>
          </div>
        </div>
      )}

      {/* NECA Mode - Labor Manual viewer */}
      {activeMode === 'neca' && !isTransitioning && (
        <NecaModeViewer
          file={file ?? null}
          estimationId={estimationId}
          necaPageNumber={necaPageNumber}
          necaNumPages={necaNumPages}
          necaScale={necaScale}
          setNecaNumPages={setNecaNumPages}
          setNecaPageNumber={setNecaPageNumber}
          pdfOptions={pdfOptions}
        />
      )}

      {/* Estimate Mode - Transitioning state */}
      {activeMode === 'estimate' && isTransitioning && (
        <div className="pdf-annotator-wrapper" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          background: 'var(--bg-primary)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div className="loading-spinner" style={{ 
              width: '40px', 
              height: '40px', 
              border: '4px solid var(--border-color)',
              borderTop: '4px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }} />
            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>Loading Estimate...</p>
          </div>
        </div>
      )}

      {/* Estimate Mode - Simplified Estimate Table (Labor Agent Based) */}
      {activeMode === 'estimate' && !isTransitioning && (
        <div className="pdf-annotator-wrapper" style={{ background: 'var(--bg-secondary)', padding: 0, overflow: 'auto' }}>
          <SimplifiedEstimateTable
            estimationId={estimationId}
            projectName={estimation?.name}
          />
        </div>
      )}

      {/* Draw Mode - Transitioning state */}
      {activeMode === 'draw' && isTransitioning && (
        <div className="pdf-annotator-wrapper" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          background: 'var(--bg-primary)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div className="loading-spinner" style={{ 
              width: '40px', 
              height: '40px', 
              border: '4px solid var(--border-color)',
              borderTop: '4px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }} />
            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>Loading Drawing...</p>
          </div>
        </div>
      )}

      {/* Draw Mode - Main canvas with annotations */}
      {file && activeMode === 'draw' && !isTransitioning && (
        <DrawModeLayout
          file={file}
          numPages={numPages}
          pageNumber={pageNumber}
          pageIndex={pageIndex}
          annotationCountsByPage={annotationCountsByPage}
          pdfDocument={pdfDocument}
          containerRef={containerRef}
          canvasRef={canvasRef}
          isPageFullyLoaded={isPageFullyLoaded}
          scale={scale}
          panX={panX}
          panY={panY}
          isPanning={isPanning}
          pdfRenderScale={pdfRenderScale}
          devicePixelRatio={effectiveDevicePixelRatio}
          pageRotations={pageRotations}
          showSnapshotActions={showSnapshotActions}
          snapshotSelection={snapshotSelection}
          autoExtractKeyOnSnapshot={autoExtractKeyOnSnapshot}
          showDrawModeEscapeHint={Boolean(countingModeActive && activePlacementComponent)}
          canvasCursor={
            autocountMode === 'area-selection' ? 'crosshair' :
                     lightingScheduleMode ? 'crosshair' :
                     isElementDetectionMode ? 'crosshair' :
                     isGridSnapActive ? 'crosshair' :
                     isPanning ? 'grabbing' :
                     wireRunExtendMode ? 'crosshair' :
                     wireRunVertexDrag ? 'move' :
                     resizeHandle ? `${resizeHandle.handle}-resize` :
                     (hoverCursor !== 'default' && currentTool === 'select') ? hoverCursor :
                     isSelecting ? 'default' :
                     isCapturingSnapshot ? 'crosshair' :
                     (activePlacementComponent && countingModeActive) ? 'crosshair' :
                     currentTool === 'piping' ? 'crosshair' :
                     (currentTool === 'select' && dragStart) ? 'move' :
                     (hoveredAnnotation && selectedAnnotations.has(hoveredAnnotation) && currentTool === 'select' && hoveredAnnotationType !== 'piping') ? 'move' :
                     (hoveredAnnotation && hoveredAnnotationType === 'piping') ? 'pointer' :
                     (hoveredAnnotation ? 'pointer' :
                     (currentTool === 'select' ? 'default' :
                     (currentTool === 'drag' ? 'grab' :
                     (currentTool === 'snapshot' ? 'crosshair' : 'crosshair'))))
          }
          isUpgradingRes={isUpgradingRes}
          handDrawnProps={{
            isEnabled: handDrawnEnabled && !isCalibrating && !autocountMode && !lightingScheduleMode,
            canvasRef,
            currentTool,
            selectedColor: isWireRunMode && selectedWireType ? selectedWireType.color : handDrawnColor,
            lineWidth: handDrawnLineWidth,
            fontSize: handDrawnFontSize,
            highlightOpacity: 0.3,
            estimationId,
            fileId: file?.id || '',
            pageNumber,
            currentPageCalibration: calibrationData[pageNumber],
            onDrop: handleCanvasDrop,
            externalSelectedStrokeIds: currentTool === 'select' ? selectedHandDrawnStrokes : undefined,
            onStrokeSelectionChange: handleStrokeSelectionChange,
            onStrokesQuery: () => (window as any).getHandDrawnStrokes?.() || [],
            onContextMenu: handleContextMenu,
            onHistoryChange: (canUndo: boolean, canRedo: boolean) => {
              setCanUndoHandDrawn(canUndo);
              setCanRedoHandDrawn(canRedo);
            },
            isWireRunMode,
            onWireRunComplete: handleWireRunComplete,
            wireRunType: selectedWireType ? { name: selectedWireType.name, color: selectedWireType.color } : null,
            onWireRunClick: (x: number, y: number) => {
              const wireRunAnnotation = annotations
                .filter(a => a.page === pageNumber && a.type === 'wire-run')
                .find(a => {
                  if (!a.points || a.points.length < 2) return false;
                  for (let i = 0; i < a.points.length - 1; i++) {
                    const dist = pointToLineDistance(x, y, a.points[i].x, a.points[i].y, a.points[i+1].x, a.points[i+1].y);
                    if (dist < 15) return true;
                  }
                  return false;
                });
              if (wireRunAnnotation) {
                console.log('🔌 Clicked existing wire run while drawing, switching to edit mode');
                if (onSwitchToWiresTab) onSwitchToWiresTab();
                setIsWireRunMode(false);
                setSelectedWireType(null);
                setHandDrawnEnabled(false);
                setCurrentTool('select');
                setSelectedHandDrawnStrokes(new Set());
                setSelectedAnnotation(wireRunAnnotation.id);
                setSelectedAnnotations(new Set([wireRunAnnotation.id]));
                setDragStart(null);
                setWireRunExtendMode(null);
                setWireRunExtendPreviewPoint(null);
                setWireRunVertexDrag(null);
                return true;
              }
              return false;
            },
            onWireRunContextMenu: (e: any, canFinish: boolean, pointCount: number, finishCallback: () => void, cancelCallback: () => void) => {
              console.log('🔌 Wire run right-click - points:', pointCount, 'canFinish:', canFinish);
              if (canFinish) {
                console.log('🔌 Finishing wire run automatically on right-click');
                finishCallback();
                setIsWireRunMode(false);
                setSelectedWireType(null);
                setHandDrawnEnabled(false);
                setCurrentTool('select');
              }
            },
            onRightClickPan: (e: any, phase: 'start' | 'move' | 'end') => {
              if (wireRunExtendMode) return;
              if (phase === 'start') {
                console.log('🖱️ Right-click pan START from HandDrawnAnnotations');
                setIsRightClickPanning(true);
                setIsPanning(true);
                setPanStart({ x: e.clientX, y: e.clientY, panX, panY });
              } else if (phase === 'move') {
                if (panStart) {
                  setPanX(panStart.panX + e.clientX - panStart.x);
                  setPanY(panStart.panY + e.clientY - panStart.y);
                }
              } else if (phase === 'end') {
                console.log('🖱️ Right-click pan END from HandDrawnAnnotations');
                setIsRightClickPanning(false);
                setIsPanning(false);
                setPanStart(null);
              }
            },
            disableRightClickPan: Boolean(wireRunExtendMode),
            isSquareFootageMode,
            onSquareFootageComplete: handleSquareFootageComplete,
          }}
          onNavigateToPage={navigateToPage}
          onDownloadPDF={handleDownloadOriginalPDF}
          onHomeClick={onHomeClick}
          onDocumentLoadSuccess={onDocumentLoadSuccess}
          onPageLoadSuccess={onPageLoadSuccess}
          onPhase1Complete={handlePhase1Complete}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onViewportMouseLeave={handleViewportMouseLeave}
          onContextMenu={handleContextMenu}
          onCanvasDrop={handleCanvasDrop}
          canvasToVisualCoords={canvasToVisualCoords}
          handleExtractKey={handleExtractKey}
          handleSendToAgent={handleSendToAgent}
          handleCancelSnapshot={handleCancelSnapshot}
        />
      )}

      {/* RFI Mode - Transitioning state */}
      {activeMode === 'rfi' && isTransitioning && (
        <div className="pdf-annotator-wrapper" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          background: 'var(--bg-primary)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div className="loading-spinner" style={{ 
              width: '40px', 
              height: '40px', 
              border: '4px solid var(--border-color)',
              borderTop: '4px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }} />
            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>Loading RFIs...</p>
          </div>
        </div>
      )}

      {/* RFI Mode - Grid View or Editor */}
      {activeMode === 'rfi' && !isTransitioning && (
        <div className="pdf-annotator-wrapper" style={{
          background: 'var(--bg-secondary)',
          display: 'flex',
          padding: '0',
          overflow: 'hidden'
        }}>
          <RFIManager
            estimationId={estimationId}
            projectName={estimation?.name}
            contractNumber={estimation?.contract_number}
          />
        </div>
      )}

      {/* Wire Run Context Menu (when NOT actively drawing) */}
      {wireRunContextMenu && isWireRunMode && selectedWireType && (
        <div
          className="placement-context-menu"
          style={{
            position: 'fixed',
            left: wireRunContextMenu.x,
            top: wireRunContextMenu.y,
            zIndex: 100000
          }}
        >
          <div className="context-menu-header">
            <span style={{ color: selectedWireType.color }}>●</span>
            {selectedWireType.name}
          </div>
          <button
            onClick={() => {
              console.log('🔌 Stopping wire run mode');
              setIsWireRunMode(false);
              setSelectedWireType(null);
              setHandDrawnEnabled(false);
              setCurrentTool('select');
              setWireRunContextMenu(null);
            }}
          >
            ✕ Stop Drawing Wires
          </button>
          <button
            onClick={() => {
              console.log('🔌 Changing wire type');
              setWireRunContextMenu(null);
              setShowWireTypeModal(true);
            }}
          >
            🔀 Change Wire Type
          </button>
          <button
            onClick={() => setWireRunContextMenu(null)}
          >
            ↩ Continue Drawing
          </button>
        </div>
      )}

      {/* Wire Run Edit Context Menu (right-click existing wire run) */}
      {wireRunEditContextMenu && (
        <div
          className="placement-context-menu"
          style={{
            position: 'fixed',
            left: wireRunEditContextMenu.x,
            top: wireRunEditContextMenu.y,
            zIndex: 100000
          }}
        >
          <div className="context-menu-header">
            <span style={{ color: wireRunEditContextMenu.wireTypeColor || '#3b82f6' }}>●</span>
            {wireRunEditContextMenu.wireTypeName || 'Wire Run'}
          </div>
          <button onClick={handleDeleteWireRunFromContextMenu}>
            🗑 Delete Run
          </button>
          <button
            onClick={handleAddWireRunSegmentFromContextMenu}
            disabled={!wireRunEditContextMenu.extendEndpoint}
          >
            ➕ Add New Segment
          </button>
          <button
            onClick={handleDeleteWireRunSegmentFromContextMenu}
            disabled={wireRunEditContextMenu.removableVertexIndex === null}
          >
            ➖ Delete Segment
          </button>
          <button onClick={() => setWireRunEditContextMenu(null)}>
            ↩ Cancel
          </button>
        </div>
      )}

      {/* Wire Run Drawing Context Menu - REMOVED: Right-click now auto-finishes */}

      {/* Calibration Setup Modal */}
      <CalibrationSetupModal
        isOpen={showCalibrationDropdown}
        currentPageNumber={pageNumber}
        currentPageCalibration={calibrationData[pageNumber]}
        vectorizationStatus={vectorizationStatus}
        vectorizationMessage={vectorizationMessage}
        onStartManualCalibration={startManualCalibration}
        onApplyPresetCalibration={applyPresetCalibration}
        onClose={() => setShowCalibrationDropdown(false)}
      />

      {/* Manual Calibration Value Modal */}
      <CalibrationMeasurementModal
        isOpen={showCalibrationModal}
        percentage={calibrationLine?.percentage || 0}
        onClose={() => {
          setShowCalibrationModal(false);
          setCalibrationLine(null);
          setIsCalibrating(false);
          setCurrentTool('select');
        }}
        onSave={handleCalibrationSave}
        onRedo={handleCalibrationRedo}
      />

      <TakeoffOnboardingStartModal
        show={showTakeoffOnboardingStartModal}
        onSelectMode={handleTakeoffOnboardingSelectMode}
      />

      {showTakeoffLightingFixtureKeyModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100021,
            padding: '20px',
          }}
          onClick={handleGuidedTakeoffCancel}
        >
          <div
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '16px',
              padding: '28px',
              minWidth: '720px',
              maxWidth: '1100px',
              width: '100%',
              boxShadow: '0 24px 64px rgba(0, 0, 0, 0.35)',
              border: '1px solid var(--border-color)',
              maxHeight: '84vh',
              overflow: 'hidden',
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '20px', fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Lightbulb size={22} style={{ color: '#f59e0b' }} />
                Lighting Schedule
              </h3>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '14px' }}>
                These fixtures are already in your key and will be used for takeoff.
              </p>
            </div>

            <div style={{ marginBottom: '18px' }}>
              <div className="takeoff-lighting-table-wrapper">
                <table className="takeoff-lighting-table">
                  <colgroup>
                    <col className="takeoff-lighting-col-code" />
                    <col className="takeoff-lighting-col-name" />
                    <col className="takeoff-lighting-col-manufacturer" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="takeoff-lighting-col-code">
                        CODE
                      </th>
                      <th className="takeoff-lighting-col-name">
                        NAME
                      </th>
                      <th className="takeoff-lighting-col-manufacturer">
                        MANUFACTURER
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {takeoffLightingFixtureKeyItems.map((fixture, index) => (
                      <tr key={`${fixture.code}-${index}`} className="takeoff-lighting-table-row">
                        <td className="takeoff-lighting-cell-code">
                          {fixture.code}
                        </td>
                        <td className="takeoff-lighting-cell-name">
                          {fixture.name || fixture.code}
                        </td>
                        <td className="takeoff-lighting-cell-manufacturer">
                          {fixture.manufacturer || '-'}
                        </td>
                      </tr>
                    ))}
                    {takeoffLightingFixtureKeyItems.length === 0 && (
                      <tr>
                        <td colSpan={3} className="takeoff-lighting-table-empty">
                          No fixtures in key yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div
                style={{
                  marginTop: '8px',
                  color: 'var(--text-secondary)',
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                {takeoffLightingFixtureKeyItems.length} fixture{takeoffLightingFixtureKeyItems.length === 1 ? '' : 's'}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleScanLightingScheduleFromTakeoffKeyModal}
                style={{
                  padding: 0,
                  background: 'transparent',
                  border: 'none',
                  color: '#3b82f6',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginRight: 'auto',
                }}
              >
                Scan Manually
              </button>
              <button
                onClick={handleConfirmTakeoffLightingFixtureKeyModal}
                style={{
                  padding: '10px 18px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: '#fff',
                  cursor: 'pointer',
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                Next
                <ArrowRight size={15} />
              </button>
            </div>
          </div>
        </div>
      )}

      {showLightingExtractionGuideModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100018,
            padding: '20px',
          }}
          onClick={handleGuidedTakeoffCancel}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '760px',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-color)',
              borderRadius: '16px',
              boxShadow: '0 24px 64px rgba(0, 0, 0, 0.35)',
              padding: '28px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '24px', marginBottom: '12px', fontWeight: 700 }}>
              Add Lighting Schedule
            </h3>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px', fontWeight: 700 }}>
              Instructions
            </p>
            <ol style={{ margin: '8px 0 14px', color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.6, paddingLeft: '20px' }}>
              <li>Find lighting fixture schedule</li>
              <li>Put a box around the fixture schedule region</li>
            </ol>
            <div>
              <img
                src="/ezgif-5384fe58cc43fa01-2_5x.gif"
                alt="Scan example"
                style={{
                  width: '100%',
                  maxHeight: '460px',
                  objectFit: 'contain',
                  borderRadius: '12px',
                }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={handleGuidedTakeoffCancel}
                style={{
                  padding: '10px 18px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-strong)',
                  background: 'var(--bg-tertiary)',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                Cancel
              </button>
              <button
                onClick={startGuidedLightingExtraction}
                style={{
                  padding: '10px 18px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: '#fff',
                  cursor: 'pointer',
                  fontWeight: 700,
                }}
              >
                Start Scanning
              </button>
            </div>
          </div>
        </div>
      )}

      {showLightingScheduleUploadModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.62)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100019,
            padding: '20px',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '620px',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-color)',
              borderRadius: '16px',
              boxShadow: '0 24px 64px rgba(0, 0, 0, 0.35)',
              padding: '28px',
            }}
          >
            <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '24px', marginBottom: '10px', fontWeight: 700 }}>
              We couldn't find the lighting fixture schedule.
            </h3>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.6 }}>
              Send a screenshot of the schedule here and we will extract it for you.
            </p>

            <label
              className={`file-upload-button ${isDraggingLightingScheduleUpload ? 'dragging' : ''}`}
              style={{ marginTop: '16px', minHeight: '150px', borderStyle: 'dashed' }}
              onDragOver={handleLightingScheduleUploadDragOver}
              onDragEnter={handleLightingScheduleUploadDragEnter}
              onDragLeave={handleLightingScheduleUploadDragLeave}
              onDrop={handleLightingScheduleUploadDrop}
              onMouseEnter={() => setIsLightingScheduleUploadHovered(true)}
              onMouseLeave={() => setIsLightingScheduleUploadHovered(false)}
              onFocus={() => setIsLightingScheduleUploadHovered(true)}
              onBlur={() => setIsLightingScheduleUploadHovered(false)}
              tabIndex={0}
            >
              <input
                ref={lightingScheduleScreenshotInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                multiple
                style={{ display: 'none' }}
                disabled={isUploadingLightingScheduleScreenshot}
                onChange={handleLightingScheduleScreenshotUpload}
              />
              <div className="upload-icon-wrapper">
                <Upload size={36} />
              </div>
              <div className="upload-text">
                {lightingScheduleUploadItems.length > 0
                  ? `${lightingScheduleUploadItems.length} screenshot${lightingScheduleUploadItems.length === 1 ? '' : 's'} selected - drop or click to add more`
                  : 'Upload lighting schedule screenshots'}
              </div>
              <div className="upload-hint">PNG, JPG, WEBP - click or hover here, then paste screenshot (Cmd/Ctrl+V)</div>
            </label>

            {lightingScheduleUploadItems.length > 0 && (
              <div className="uploaded-files-list" style={{ marginTop: '12px', maxHeight: '280px', overflowY: 'auto' }}>
                <div className="uploaded-files-header">Selected files ({lightingScheduleUploadItems.length})</div>
                {lightingScheduleUploadItems.map((item, index) => (
                  <div key={item.id} className="uploaded-file-item">
                    <div className="uploaded-file-thumbnail-wrapper">
                      <img
                        src={item.snapshot}
                        alt={`Lighting schedule screenshot ${index + 1}`}
                        className="uploaded-file-thumbnail"
                      />
                    </div>
                    <div className="uploaded-file-info">
                      <div className="uploaded-file-name">{item.fileName}</div>
                      <div className="uploaded-file-size">{(item.fileSize / 1024 / 1024).toFixed(2)} MB</div>
                    </div>
                    <button
                      className="remove-uploaded-file"
                      onClick={() => handleRemoveLightingScheduleUploadItem(item.id)}
                      disabled={isUploadingLightingScheduleScreenshot}
                      title="Remove screenshot"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {lightingScheduleUploadError && (
              <p style={{ margin: '14px 0 0', color: '#ef4444', fontSize: '13px' }}>
                {lightingScheduleUploadError}
              </p>
            )}

            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              {lightingScheduleUploadItems.length > 0 && (
                <button
                  onClick={() => setLightingScheduleUploadItems([])}
                  disabled={isUploadingLightingScheduleScreenshot}
                  style={{
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-strong)',
                    background: 'var(--bg-tertiary)',
                    color: 'var(--text-secondary)',
                    cursor: isUploadingLightingScheduleScreenshot ? 'not-allowed' : 'pointer',
                    fontWeight: 600,
                  }}
                >
                  Clear All
                </button>
              )}
              <button
                onClick={handleExtractUploadedLightingScheduleScreenshots}
                disabled={isUploadingLightingScheduleScreenshot || lightingScheduleUploadItems.length === 0}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: (isUploadingLightingScheduleScreenshot || lightingScheduleUploadItems.length === 0)
                    ? 'var(--border-strong)'
                    : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: '#fff',
                  cursor: (isUploadingLightingScheduleScreenshot || lightingScheduleUploadItems.length === 0) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  opacity: (isUploadingLightingScheduleScreenshot || lightingScheduleUploadItems.length === 0) ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  minWidth: '210px',
                }}
              >
                <Lightbulb size={16} />
                Extract Fixtures ({lightingScheduleUploadItems.length})
              </button>
            </div>
          </div>
        </div>
      )}

      <TakeoffPageSelectionModal
        show={showTakeoffPageSelectionModal && !!takeoffPageSelectionStage}
        title={takeoffPageSelectionStage === 'lighting' ? 'Select Lighting Pages' : 'Select Power Pages'}
        numPages={numPages}
        pageIndex={pageIndex}
        selectedPages={takeoffPageSelectionStage === 'lighting' ? takeoffLightingPages : takeoffPowerPages}
        powerDevices={takeoffPageSelectionStage === 'power' ? takeoffPowerDevices : []}
        onTogglePage={(pg) => {
          if (takeoffPageSelectionStage === 'lighting') {
            setTakeoffLightingPages((prev) => {
              const next = new Set(prev);
              if (next.has(pg)) next.delete(pg);
              else next.add(pg);
              return next;
            });
            return;
          }
          setTakeoffPowerPages((prev) => {
            const next = new Set(prev);
            if (next.has(pg)) next.delete(pg);
            else next.add(pg);
            return next;
          });
        }}
        onCancel={handleGuidedTakeoffCancel}
        onBack={guidedTakeoffMode === 'full' && takeoffPageSelectionStage === 'power' ? handleGuidedTakeoffPageBack : undefined}
        onConfirm={handleGuidedTakeoffPageConfirm}
        confirmLabel={takeoffPageSelectionStage === 'lighting' && guidedTakeoffMode === 'full' ? 'Continue to Power Pages' : 'Start Takeoff'}
      />

      {/* New Component Modal for Count */}
      <NewComponentCountModal
        show={showNewComponentModal}
        isAddingNewOnly={isAddingNewOnly}
        countingModalMode={countingModalMode}
        newComponentName={newComponentName}
        newComponentColor={newComponentColor}
        isAddingComponent={isAddingComponent}
        quickCountSearch={quickCountSearch}
        selectedComponentsForBatch={selectedComponentsForBatch}
        referenceAreaImage={referenceAreaImage}
        electricalKey={electricalKey}
        autocountModalTab={autocountModalTab}
        onOverlayClose={() => {
            setShowNewComponentModal(false);
            setSelectedComponentsForBatch(new Set());
            setIsAddingNewOnly(false);
          }}
        onCloseButton={() => {
                  setShowNewComponentModal(false);
                  setQuickCountSearch('');
                  setSelectedComponentsForBatch(new Set());
                  setIsAddingNewOnly(false);
                }}
        onStartLightingScheduleExtraction={handleStartLightingScheduleExtraction}
        onStartQuickCount={handleStartQuickCount}
        onBatchAutocount={handleBatchAutocount}
        onRetakeAreaSelection={handleRetakeAreaSelection}
        onStartAreaSelection={handleStartAreaSelection}
        onConfirmAddToKey={handleConfirmAddToKey}
        onKeyExtracted={onKeyExtracted}
        setIsAddingNewOnly={setIsAddingNewOnly}
        setNewComponentName={setNewComponentName}
        setNewComponentColor={setNewComponentColor}
        setQuickCountSearch={setQuickCountSearch}
        setSelectedComponentsForBatch={setSelectedComponentsForBatch}
        setAutocountModalTab={setAutocountModalTab}
      />




      {/* Autocount Area Selection Instruction Overlay */}
      {autocountMode === 'area-selection' && !showAutocountConfirmModal && pendingAutocountComponent && (
        <AutocountAreaSelectionOverlay
          pendingAutocountComponent={pendingAutocountComponent}
          queuedComponents={queuedComponents}
          onCancel={() => {
              setAutocountMode(null);
              setAutocountSelection(null);
              setPendingAutocountComponent(null);
              setQueuedComponents([]);
              setShowNewComponentModal(true);
            }}
        />
      )}

      <LightingScheduleConfirmModal
        show={showLightingScheduleConfirmModal && !!lightingScheduleSnapshot}
        lightingScheduleSnapshot={lightingScheduleSnapshot}
        lightingScheduleJobs={lightingScheduleJobs}
        onDeleteQueuedJob={handleDeleteQueuedLightingSchedule}
        onAddAndScanNext={handleQueueLightingScheduleAndContinue}
        onExtractAndDone={handleExtractQueuedLightingSchedules}
        onCancel={() => {
            setShowLightingScheduleConfirmModal(false);
            setLightingScheduleSnapshot(null);
            setLightingScheduleJobs([]);
            setLightingExtractionAnimationSnapshot(null);
          setLightingScheduleSelection(null);
          setLightingScheduleMode(false);
          if (isGuidedLightingExtractionFlow) {
            setShowLightingExtractionGuideModal(true);
          }
        }}
        onOverlayClick={() => {
                  setShowLightingScheduleConfirmModal(false);
                  setLightingScheduleSnapshot(null);
                  setLightingScheduleJobs([]);
                  setLightingExtractionAnimationSnapshot(null);
                  if (isGuidedLightingExtractionFlow) {
                    setShowLightingExtractionGuideModal(true);
                  }
        }}
        onSelectDifferent={() => {
                  setShowLightingScheduleConfirmModal(false);
                  setLightingScheduleSnapshot(null);
                  setLightingScheduleSelection(null);
                  setLightingScheduleMode(true);
                }}
      />

      <PageSelectionModal
        show={showPageSelectionModal && !!pageSelectionContext}
        pageSelectionContext={pageSelectionContext}
        numPages={numPages}
        selectedPagesForCount={selectedPagesForCount}
        pageIndex={pageIndex}
        onTogglePage={(pg) => {
                        setSelectedPagesForCount(prev => {
                          const next = new Set(prev);
                          if (next.has(pg)) next.delete(pg);
                          else next.add(pg);
                          return next;
                        });
                      }}
        onCancel={() => {
                  setShowPageSelectionModal(false);
                  setPageSelectionContext(null);
                  if (pageSelectionContext?.newComponents) {
                    setPendingKeyComponents([]);
                  }
                }}
        onConfirm={handleConfirmPageSelection}
      />

      {keyExtractionMode && !showKeyExtractionConfirmModal && (
        <KeyExtractionPanel
          keyExtractionJobs={keyExtractionJobs}
          onCancel={handleCancelKeyExtractionJobs}
          onDeleteJob={handleDeleteKeyExtractionJob}
          onFinishSelection={handleFinishKeyExtractionSelection}
        />
      )}

      <KeyExtractionProcessingOverlay
        show={!keyExtractionMode && keyExtractionJobs.length > 0 && keyExtractionJobs.some(j => j.status === 'processing') && !showKeyExtractionPreview}
        onCancel={() => {
              keyExtractionJobs.forEach(j => {
                if (j.status === 'processing' && j.abortController) j.abortController.abort();
              });
              setKeyExtractionJobs([]);
        }}
      />

      <KeyExtractionConfirmModal
        show={showKeyExtractionConfirmModal && !!keyExtractionSnapshot}
        keyExtractionSnapshot={keyExtractionSnapshot}
        keyExtractionSelection={keyExtractionSelection}
        keyExtractionJobs={keyExtractionJobs}
        onReselect={() => {
                  setShowKeyExtractionConfirmModal(false);
                  setKeyExtractionSnapshot(null);
                  setKeyExtractionSelection(null);
                }}
        onAddAndScanNext={handleExtractAndScanNext}
        onExtractSingle={handleExtractSingle}
        onDeleteJob={handleDeleteKeyExtractionJob}
      />

      <KeyExtractionLoadingOverlay show={isExtractingKeyFromLegend} />

      <KeyExtractionPreviewModal
        show={showKeyExtractionPreview}
        extractedKeyPreview={extractedKeyPreview}
        selectedKeyPreviewIndices={selectedKeyPreviewIndices}
        keyExtractionSnapshot={keyExtractionSnapshot}
        onToggleSelection={(idx) => {
                    setSelectedKeyPreviewIndices(prev => {
                      const next = new Set(prev);
                      next.has(idx) ? next.delete(idx) : next.add(idx);
                      return next;
                    });
                  }}
        onSelectAll={() => {
          if (selectedKeyPreviewIndices.size === extractedKeyPreview.length) {
            setSelectedKeyPreviewIndices(new Set());
          } else {
            setSelectedKeyPreviewIndices(new Set(extractedKeyPreview.map((_, i) => i)));
          }
        }}
        onClose={() => { setShowKeyExtractionPreview(false); setKeyExtractionJobs([]); }}
        onCloseFull={() => {
                  setShowKeyExtractionPreview(false);
                  setExtractedKeyPreview([]);
                  setSelectedKeyPreviewIndices(new Set());
                  setKeyExtractionSnapshot(null);
                }}
        onAddToKeyOnly={handleAddToKeyOnly}
        onAddToKeyAndCount={handleAddToKeyAndCount}
      />

      <LightingSchedulePreviewModal
        show={showLightingSchedulePreview}
        extractedLightingFixtures={extractedLightingFixtures}
        selectedLightingFixtureIndices={selectedLightingFixtureIndices}
        lightingScheduleSnapshot={lightingScheduleSnapshot}
        lightingScheduleSnapshots={lightingSchedulePreviewSnapshots}
        onToggleSelection={(idx) => {
                      setSelectedLightingFixtureIndices(prev => {
                        const next = new Set(prev);
                        next.has(idx) ? next.delete(idx) : next.add(idx);
                        return next;
                      });
                    }}
        onSelectAll={() => {
          if (selectedLightingFixtureIndices.size === extractedLightingFixtures.length) {
            setSelectedLightingFixtureIndices(new Set());
          } else {
            setSelectedLightingFixtureIndices(new Set(extractedLightingFixtures.map((_, i) => i)));
          }
        }}
        onClose={() => {
          if (isGuidedLightingExtractionFlow) {
            handleGuidedLightingExtractionRetry();
            return;
          }
          setShowLightingSchedulePreview(false);
          setLightingSchedulePreviewSnapshots([]);
        }}
        onCloseFull={() => {
                  setShowLightingSchedulePreview(false);
                  setExtractedLightingFixtures([]);
                  setSelectedLightingFixtureIndices(new Set());
                  setLightingScheduleSnapshot(null);
                  setLightingSchedulePreviewSnapshots([]);
                  setLightingScheduleJobs([]);
                  setLightingExtractionAnimationSnapshot(null);
                  if (isGuidedLightingExtractionFlow) {
                    resetGuidedTakeoffFlow();
                  }
                }}
        onAddToKeyOnly={handleAddLightingFixturesToKey}
        onAddToKeyAndCount={handleAddLightingFixturesToKeyAndCount}
        guidedMode={isGuidedLightingExtractionFlow}
        onGuidedContinue={handleGuidedLightingExtractAndContinue}
        onGuidedRetry={handleGuidedLightingExtractionRetry}
      />

      {/* Autocount Confirmation Modal — with batch queue */}
      <AutocountConfirmModal
        show={showAutocountConfirmModal}
        referenceAreaImage={referenceAreaImage}
        pendingAutocountComponent={pendingAutocountComponent}
        editingQueuedName={editingQueuedName}
        queuedComponents={queuedComponents}
        electricalKey={electricalKey}
        pdfDocument={pdfDocument}
        numPages={numPages}
        onKeyExtracted={onKeyExtracted}
        resolveUniqueComponentName={getUniqueComponentName}
        startCountingComponents={startCountingComponents}
        setEditingQueuedName={setEditingQueuedName}
        setQueuedComponents={setQueuedComponents}
        setShowAutocountConfirmModal={setShowAutocountConfirmModal}
        setReferenceAreaImage={setReferenceAreaImage}
        setAutocountSelection={setAutocountSelection}
        setPendingAutocountComponent={setPendingAutocountComponent}
        setAutocountMode={setAutocountMode}
      />




      <AutocountProgressOverlay
        show={!!(isAutocounting && autocountProgress)}
        autocountProgress={autocountProgress || ''}
        pageProgressMap={pageProgressMap}
      />

      {/* Blocking Overlay During Processing */}
      {(isAutocounting || showAutocountReviewModal) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'transparent',
          zIndex: 100002,
          pointerEvents: isAutocounting ? 'all' : 'none',
          cursor: isAutocounting ? 'wait' : 'default'
        }} />
      )}

      {/* Count All Progress Overlay */}
      {(isCountingAll || showMultiComponentReviewModal) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'transparent',
          zIndex: 100002,
          pointerEvents: isCountingAll ? 'all' : 'none',
          cursor: isCountingAll ? 'wait' : 'default'
        }} />
      )}

      {/* Autocount Review Modal */}
      <AutocountReviewModal
        show={showAutocountReviewModal}
        pendingAutocountComponent={pendingAutocountComponent}
        isAutocounting={isAutocounting}
        autocountDetections={autocountDetections}
        selectedDetectionsToKeep={selectedDetectionsToKeep}
        pageNumber={pageNumber}
        autocountTotalBatches={autocountTotalBatches}
        autocountCompletedBatches={autocountCompletedBatches}
        toggleDetectionSelection={toggleDetectionSelection}
        onConfirm={handleFinalAutocountConfirm}
        onCancel={() => {
                    setShowAutocountReviewModal(false);
                    setAutocountDetections([]);
                    setSelectedDetectionsToKeep(new Set());
                    setPendingAutocountComponent(null);
                    setReferenceAreaImage(null);
                    setAutocountSelection(null);
                    setIsAutocounting(false);
                    setAutocountTotalBatches(0);
                    setAutocountCompletedBatches(0);
                  }}
        setSelectedDetectionsToKeep={setSelectedDetectionsToKeep}
      />




      <MultiComponentReviewModal
        show={showMultiComponentReviewModal}
        isCountingAll={isCountingAll}
        multiComponentDetections={multiComponentDetections}
        currentReviewComponentIndex={currentReviewComponentIndex}
        allComponentSelections={allComponentSelections}
        setAllComponentSelections={setAllComponentSelections}
        receptacleGroupComponents={receptacleGroupComponents}
        showReceptacleRefPopup={showReceptacleRefPopup}
        setShowReceptacleRefPopup={setShowReceptacleRefPopup}
        countAllProgress={countAllProgress}
        countAllComponentProgress={countAllComponentProgress}
        componentPagesByName={componentPagesByName}
        pageThumbnailsByPage={countAllPageThumbnails}
        detectionClassifications={detectionClassifications}
        setDetectionClassifications={setDetectionClassifications}
        receptacleClassFilter={receptacleClassFilter}
        setReceptacleClassFilter={setReceptacleClassFilter}
        teleDataGroupComponents={teleDataGroupComponents}
        teleDataClassFilter={teleDataClassFilter}
        setTeleDataClassFilter={setTeleDataClassFilter}
        lightingGroupComponents={lightingGroupComponents}
        lightingClassFilter={lightingClassFilter}
        setLightingClassFilter={setLightingClassFilter}
        bulkSelectedDetections={bulkSelectedDetections}
        setBulkSelectedDetections={setBulkSelectedDetections}
        bulkReclassTarget={bulkReclassTarget}
        setBulkReclassTarget={setBulkReclassTarget}
        toggleMultiDetectionSelection={toggleMultiDetectionSelection}
        handleMultiComponentBack={handleMultiComponentBack}
        handleFinishMultiComponentReview={handleFinishMultiComponentReview}
        handleExportDatasetZip={handleExportDatasetZip}
        handleMultiComponentNext={handleMultiComponentNext}
        isExportingDataset={isExportingDataset}
        datasetExportError={datasetExportError}
        handleCancel={() => {
                        setShowMultiComponentReviewModal(false);
                        setMultiComponentDetections([]);
                        setAllComponentSelections(new Map());
                        setCurrentReviewComponentIndex(0);
                        setIsCountingAll(false);
                        setIsParallelCountSession(false);
                        setCountAllProgress('');
                        setCountAllComponentProgress([]);
                        setComponentPagesByName(new Map());
                        setCountAllPageThumbnails(new Map());
                        countAllPageThumbnailsRef.current = new Map();
                        countAllThumbnailRenderInFlightRef.current.clear();
                        setPendingKeyComponents([]);
                        setReceptacleGroupComponents([]);
                        setLightingGroupComponents([]);
                        setDetectionClassifications(new Map());
                        setBulkSelectedDetections(new Set());
                        setBulkReclassTarget('');
                        setDatasetExportError(null);
                        setReviewFinalizeError(null);
                        countTargetNamesRef.current = new Set();
                      }}
        isParallelCountSession={isParallelCountSession}
        pendingKeyComponents={pendingKeyComponents}
        isFinalizingReview={isFinalizingReview}
        reviewFinalizeError={reviewFinalizeError}
        pageNumber={pageNumber}
      />

      <CountAllProgressModal
        show={isCountingAll && countAllComponentProgress.length > 0 && !showMultiComponentReviewModal}
        countAllComponentProgress={countAllComponentProgress}
      />

      <LaborEstimationIndicator
        show={showLaborEstimationModal && laborEstimationProgress.length > 0}
        laborEstimationProgress={laborEstimationProgress}
        showEstimatePopover={showEstimatePopover}
        onTogglePopover={() => setShowEstimatePopover(v => !v)}
      />

      {/* Wire Type Selection Modal */}
      <WireTypeModal
        isOpen={showWireTypeModal}
        onClose={() => {
          setShowWireTypeModal(false);
          // If closing while already in wire run mode, exit the mode
          if (isWireRunMode) {
            setIsWireRunMode(false);
            setSelectedWireType(null);
            setCurrentTool('select');
          }
        }}
        onSave={handleWireTypeSelected}
        totalLength={0}
        hasCalibration={!!calibrationData[pageNumber]}
        unit={calibrationData[pageNumber]?.unit || 'ft'}
        isSelectMode={true}
        selectedWireType={selectedWireType}
      />
    </div>
  );
});

export default PDFAnnotator;
